/**
 * A function to create a log object for the 'myWalletRemovePaymentMethodButtonClickedSrc64Evid1' event (evid: 1)
 *
 * Event description: "Remove Payment Method" Button Clicked inside 'Manage' modal on wallet page
 *
 * @param {myWalletRemovePaymentMethodButtonClickedSrc64Evid1Params} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - user_guid
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} memberId - member id
 * @property {string} savedPaymentMethodId - saved payment method id
 * @property {string} paymentMethod - CreditCard/ PayPal
 * @property {boolean} isPrimary - true/ false
 * @property {boolean} isExpired - true/ false
 * @return {LogObject<myWalletRemovePaymentMethodButtonClickedSrc64Evid1Params>} LogObject<myWalletRemovePaymentMethodButtonClickedSrc64Evid1Params>
 */
export function myWalletRemovePaymentMethodButtonClickedSrc64Evid1(params) {
    return { evid: 1, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'orderSnapshot' event (evid: 1001)
 *
 * Event description: Order snapshot - freezing the transaction
 *
 * @param {orderSnapshotParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} cartId - -
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} flowType - type of the order's creation flow: recurring-first, recurring-subsequent, one-time, old-recurring-first, old-recurring-subsequent, other
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {string} orderId - TPA's order_id (transaction_id for stores)\
 * @property {string} billingEmailHash - billing email hash in lowercase before @
 * @property {GUID} uuid - uuid
 * @property {boolean} captchaRequested - true if captcha challenge is requested for that order
 * @property {number} installments - 1 no installments, 2+ - number of expected payments
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} authorizationAmount - authorization amount
 * @property {string} cofIntentType - type of intent when an order is created (e.g. recurring)
 * @property {string} shippingEmailHash - shipping email hash in lowercase before @
 * @property {string} user_agent - user_agent
 * @property {string} shippingEmailDomain - domain from shipping email
 * @property {GUID} appid - app_id
 * @property {string} managingAppId - who initiated payment: POS / BaaS
 * @property {string} checkoutType - -
 * @property {string} currency - amount currency - USD/GBP/EUR...
 * @property {GUID} msid - msid
 * @property {GUID} biWatermark - business_order_id
 * @property {string} captureMethod - immediate -regular payment flow, delayed - auth&capture flow
 * @property {string} billingEmailDomain - domain from billing email
 * @property {boolean} allowImmediateCapture - true if immediate capture is allowed and all connected payment methods are shown
 * @property {string} shippingRequired - if vertical ask about shipping address for current order
 *
 * @property {string} initiatorType - type of those who initiated payment - "buyer" | "merchant" | "service"
 *
 * @property {string} initiatorId - id of those who initiated payment - "buyer" | "merchant" | "service"
 *
 * @property {string} scheduledAction - Capture/Void ecom - capture by default other - Void
 *
 * @property {string} scheduledActionDelay - expiration time in [days] when scheduled action is planned
 *
 * @property {string} suggestedPaymentAgreementId - suggested PA for cancellation flow
 * @return {LogObject<orderSnapshotParams>} LogObject<orderSnapshotParams>
 */
export function orderSnapshot(params) {
    return { evid: 1001, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'creditCardTransactionServer' event (evid: 1002)
 *
 * Event description: Credit Card and offline transactions before 2017-11-17. Will be switched to 1005 event (same field names)
 *
 * @param {creditCardTransactionServerParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} paymentMethod - not in use
 * @property {number} cashierErrorCode - Cashier internal error code
 * @property {string} paymentCategory - not in use
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {string} orderId - TPA order_id
 * @property {string} declineReason - In case of Decline - string
 * @property {number} declineCode - not in use
 * @property {string} errorDesc - In case of Failure - string
 * @property {GUID} transactionid - transaction_id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodType - Visa/Amex
 * @property {string} user_agent - user_agent
 * @property {string} transactionStatus - values: Success/Decline/Failure
 * @property {number} errorCode - not in use
 * @property {GUID} appid - app_id
 * @property {string} providerErrorDesc - Provider detailed error description
 * @property {string} paymentProvider - authorize-net/Stripe
 * @property {string} currency - amount currency - USD/GBP/EUR...
 * @property {string} providerErrorName - Provider error code or general name (depends on provider)
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<creditCardTransactionServerParams>} LogObject<creditCardTransactionServerParams>
 */
export function creditCardTransactionServer(params) {
    return { evid: 1002, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantConnectDisconnectProvider' event (evid: 1003)
 *
 * Event description: Provider status is changed: connected, disconncted, updated. msid is added since 2017-11-14. Need to exclude connections with 'cash' and 'fakePaymentMethod' payment_category. app_instance_id is cashier's instance id
 *
 * @param {merchantConnectDisconnectProviderParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} advancedPermissionsGranted - true if advanced permissions are granted (allows to accept recurring payments)
 * @property {string} providerAccountId - decoded provider account id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} gatewayId - payment_provider
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - Ewallet
 * @property {string} businessLocation - defined business location
 * @property {string} user_agent - user_agent
 * @property {GUID} appid - app_id
 * @property {string} origin - 'Auto OB' - made from welcome page / 'manual OB' - manual flow or second page of Smart OB
 * @property {string} status - connect/update/disconnect
 * @property {boolean} isNew - true is connect, false is update
 * @property {boolean} isActive - true - connected or updated, false - disconnected
 * @property {string} currency - currency
 * @property {string} providerCountry - provider country ( related to WixPayments )
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {GUID} msid - msid
 * @property {boolean} allowInstallments - true if installments are allowed by merchant
 * @property {string} namespace - namespace
 * @property {string} locationMethod - How business location was defined
 * @return {LogObject<merchantConnectDisconnectProviderParams>} LogObject<merchantConnectDisconnectProviderParams>
 */
export function merchantConnectDisconnectProvider(params) {
    return { evid: 1003, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buyerTransactionStart' event (evid: 1004)
 *
 * Event description: Transaction is initialized when buyer confirmed payment
 *
 * @param {buyerTransactionStartParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} paymentMethod - CreditCard/Ewallet
 * @property {GUID} saved_cc_id - guid (hash) of saved previously cc
 * @property {string} authMethod - auth_method
 * @property {string} providerAccountId - provider account id
 * @property {string} paymentCategory - CreditCard/Ewallet
 * @property {string} apmbankid - bank id for APM flow
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} flowType - flow_type
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {string} orderId - TPA order_id
 * @property {GUID} uuid - uuid
 * @property {boolean} shouldSetupForFutureUsages - should_setup_for_future_usages
 * @property {string} paymentAgreementId - payment_agreement_id
 * @property {boolean} captchaRequested - captcha_requested
 * @property {number} installments - 1 no installments, 2+ - number of expected payments
 * @property {DateTime} processingtime - processing time from provider
 * @property {string} errorDesc - dsc
 * @property {string} transactionType - Sale / InitializeRecurring / Recurring
 * @property {GUID} transactionid - guid
 * @property {string} gateway - gateway
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodType - PayPal
 * @property {boolean} savePaymentMethod - save_payment_method
 * @property {number} authorizationAmount - authorization_amount
 * @property {string} cofIntentType - intent_type
 * @property {string} user_agent - user_agent
 * @property {string} transactionStatus - values: Success/Decline/Failure
 * @property {GUID} appid - app_id
 * @property {boolean} testTransaction - is_test
 * @property {string} managingAppId - managing_app_id
 * @property {string} checkoutType - express / regular
 * @property {string} lastFourHash - last_four_hash
 * @property {string} sessionId - front_session_id
 * @property {GUID} card_id - internal card_id (guid)
 * @property {string} paymentProvider - PayPal
 * @property {string} currency - amount currency - USD/GBP/EUR...
 * @property {string} savedPaymentMethodId - saved_payment_method_id
 * @property {string} providerCountry - provider country ( related to WixPayments )
 * @property {number} bin - The first eight digits of a card number (including the initial MII digit) are known as the issuer identification number (IIN)
 * @property {string} owner - owner
 * @property {string} apiDetails - api_details
 * @property {boolean} merchantUseAllowed - merchant_use_allowed
 * @property {number} sequenceNumber - payment serial number for recurring
 * @property {GUID} msid - msid
 * @property {boolean} merchantInitiated - trx is initiated by merchant
 * @property {number} appfeeAmount - application fee amount - multiply by 100,000
 * @property {string} paymentSource - payment_source
 * @property {string} captureMethod - immediate -regular payment flow, delayed - auth&capture flow
 * @property {boolean} isMoto - is_moto
 * @property {boolean} requiresDelayedCapture - true if delayed capture/void flow is applied
 * @property {string} initiatorType - type of those who initiated payment - "buyer" | "merchant" | "service"
 * @property {string} initiatorId - id of those who initiated payment - "buyer" | "merchant" | "service"
 * @property {string} scheduledAction - Capture/Void
 * ecom - capture by default
 * other - Void
 * @property {string} scheduledActionDelay - expiration time in [days] when scheduled action is planned
 * @return {LogObject<buyerTransactionStartParams>} LogObject<buyerTransactionStartParams>
 */
export function buyerTransactionStart(params) {
    return { evid: 1004, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buyerTransactionComplete' event (evid: 1005)
 *
 * Event description: Final Status for completed transaction. Need to exclude transactions with transaction_type = 'InitializeRecurring'. MSID IS valid after 2021-01-01
 *
 * @param {buyerTransactionCompleteParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} paymentMethod - -
 * @property {GUID} saved_cc_id - guid (hash) of saved previously cc
 * @property {string} authMethod - auth_method
 * @property {string} providerAccountId - provider account id
 * @property {number} cashierErrorCode - errors mapping - cashier internal error code for not approved transactions
 * @property {string} paymentCategory - -
 * @property {string} providertxreason - provider_tx_reason
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} flowType - flow_type
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {string} orderId - TPA order_id
 * @property {GUID} uuid - uuid
 * @property {boolean} shouldSetupForFutureUsages - should_setup_for_future_usages
 * @property {string} providertxid - provider_tx_id
 * @property {string} paymentAgreementId - payment_agreement_id
 * @property {boolean} captchaRequested - captcha_requested
 * @property {string} fundingSource - funding_source
 * @property {number} installments - 1 no installments, 2+ - number of expected payments
 * @property {string} signature - signature
 * @property {string} errorDesc - -
 * @property {string} transactionType - Sale / InitializeRecurring / Recurring
 * @property {GUID} transactionid - transaction_id
 * @property {string} gateway - gateway
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodType - actual for credit cards only. Network  - Visa/Amex etc.
 * @property {number} authorizationAmount - authorization_amount
 * @property {string} cofIntentType - intent_type
 * @property {string} user_agent - user_agent
 * @property {string} transactionStatus - values: Approved, Offline or type of failure
 * @property {GUID} appid - app_id
 * @property {string} providerErrorDesc - Provider detailed error description
 * @property {string} riskOutcomeType - risk_outcome_type
 * @property {boolean} testTransaction - true if test false is prod
 * @property {string} managingAppId - managing_app_id
 * @property {string} checkoutType - -
 * @property {string} lastFourHash - last_four_hash
 * @property {GUID} card_id - internal card_id (guid)
 * @property {string} providertxstatus - provider_tx_status
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - currency
 * @property {string} savedPaymentMethodId - saved_payment_method_id
 * @property {string} providerCountry - provider country ( related to WixPayments )
 * @property {number} bin - The first eight digits of a card number (including the initial MII digit) are known as the issuer identification number (IIN)
 * @property {string} funding - funding
 * @property {string} providerErrorName - errors mapping - Provider error code or general name (depends on provider)
 * @property {string} owner - owner
 * @property {string} apiDetails - api_details
 * @property {boolean} merchantUseAllowed - merchant_use_allowed
 * @property {number} sequenceNumber - payment serial number for recurring
 * @property {GUID} msid - msid
 * @property {boolean} merchantInitiated - merchant_initiated
 * @property {number} appfeeAmount - application fee amount - multiply by 100,000
 * @property {string} riskOutcomeReason - risk_outcome_reason
 * @property {boolean} isMoto - is_moto
 * @property {boolean} requiresDelayedCapture - true if delayed capture/void flow is applied
 * @property {string} readyForPayment - valid only for express flow, once all payment details are collected from both provider and vertical (amount, tax, billing, shipping info) then true
 * @property {string} initiatorType - type of those who initiated payment - "buyer" | "merchant" | "service"
 * @property {string} initiatorId - id of those who initiated payment - "buyer" | "merchant" | "service"
 * @property {string} captureMethod - immediate -regular payment flow, delayed - auth&capture flow
 * @property {string} scheduledAction - Capture/Void ecom - capture by default other - Void
 * @property {string} scheduledActionDelay - expiration time in [days] when scheduled action is planned
 * @return {LogObject<buyerTransactionCompleteParams>} LogObject<buyerTransactionCompleteParams>
 */
export function buyerTransactionComplete(params) {
    return { evid: 1005, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buyerTransactionCancel' event (evid: 1006)
 *
 * Event description: PayPal click on pay now
 *
 * @param {buyerTransactionCancelParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} paymentMethod - CreditCard/Ewallet
 * @property {string} paymentCategory - CreditCard/Ewallet
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {string} orderId - TPA order_id
 * @property {GUID} uuid - uuid
 * @property {string} errorDesc - dsc
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodType - PayPal
 * @property {string} user_agent - user_agent
 * @property {string} transactionStatus - values: Success/Decline/Failure
 * @property {GUID} appid - app_id
 * @property {string} paymentProvider - PayPal
 * @property {string} currency - amount currency - USD/GBP/EUR...
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} transactionId - transaction id
 * @return {LogObject<buyerTransactionCancelParams>} LogObject<buyerTransactionCancelParams>
 */
export function buyerTransactionCancel(params) {
    return { evid: 1006, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buyerTransactionStatusChanged' event (evid: 1007)
 *
 * Event description: Transaction status update (server) can be - Success/Decline/Failure
 *
 * @param {buyerTransactionStatusChangedParams} params - The parameters for this event
 * @property {GUID} transactionid - guid
 * @property {GUID} visitorId - visitor_id
 * @property {string} paymentMethod - CreditCard/Ewallet
 * @property {boolean} requiresDelayedCapture - true if delayed capture/void flow is applied
 *
 * @property {string} checkoutType - express / regular
 * @property {string} readyForPayment - valid only for express flow, once all payment details are collected from both provider and vertical (amount, tax, billing, shipping info) then true
 * @property {GUID} provider_tx_id - provider_tx_id
 * @property {string} authMethod - pan type: PAN_ONLY/CRYPTOGRAM_3DS
 * @property {string} paymentCategory - CreditCard/Ewallet
 * @property {string} providertxreason - provider_tx_reason
 * @property {boolean} shouldSetupForFutureUsages - true if payment agreement should be created during payment
 * @property {boolean} captchaRequested - true if captcha challenge is requested for that order
 * @property {string} signature - signature
 * @property {string} gateway - gateway
 * @property {string} paymentMethodType - PayPal
 * @property {number} authorizationAmount - authorization amount
 * @property {string} user_agent - user_agent
 * @property {string} transactionStatus - values: Complited/Decline
 * @property {string} providerErrorDesc - Provider detailed error description
 * @property {string} panType - pan type: dpan/ fpan
 * @property {string} lastFourHash - hash for last 4 number on CC
 * @property {string} providertxstatus - provider_tx_status
 * @property {string} paymentProvider - PayPal
 * @property {string} savedPaymentMethodId - true if buyer choose to allow for MIT trx
 * @property {string} providerCountry - provider country ( related to WixPayments )
 * @property {number} bin - The first eight digits of a card number (including the initial MII digit) are known as the issuer identification number (IIN)
 * @property {string} providerErrorName - errors mapping - Provider error code or general name (depends on provider)
 * @property {string} apiDetails - api details
 * @property {boolean} merchantUseAllowed - true if buyer choose to allow for MIT trx
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} pan - Primary Account Number: dpan/ fpan
 * @property {string} paymentSource - test for venmo under paypal
 * @property {string} initiatorType - type of those who initiated payment - "buyer" | "merchant" | "service"
 * @property {string} initiatorId - id of those who initiated payment - "buyer" | "merchant" | "service"
 * @property {string} captureMethod - immediate -regular payment flow, delayed - auth&capture flow
 * @property {string} scheduledAction - Capture/Void ecom - capture by default other - Void
 * @property {string} scheduledActionDelay - expiration time in [days] when scheduled action is planned
 * @property {string} fundingSource - funding_source
 * @return {LogObject<buyerTransactionStatusChangedParams>} LogObject<buyerTransactionStatusChangedParams>
 */
export function buyerTransactionStatusChanged(params) {
    return { evid: 1007, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantNotifiedTransactionCompleted' event (evid: 1008)
 *
 * Event description: Notify the merchant that the transaction completed
 *
 * @param {merchantNotifiedTransactionCompletedParams} params - The parameters for this event
 * @property {GUID} transactionid - guid
 * @property {GUID} visitorId - visitor_id
 * @property {string} user_agent - user_agent
 * @property {string} transactionStatus - current transaction status
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantNotifiedTransactionCompletedParams>} LogObject<merchantNotifiedTransactionCompletedParams>
 */
export function merchantNotifiedTransactionCompleted(params) {
    return { evid: 1008, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantConnectStart' event (evid: 1009)
 *
 * Event description: Merchant start connect to  acount
 *
 * @param {merchantConnectStartParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - amount_long
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - payment_category
 * @property {GUID} meta_site_id - msid
 * @property {string} user_agent - user_agent
 * @property {GUID} appid - app_id
 * @property {string} origin - 'Welcome page' - made from welcome page / 'Add provider' - manual flow or second page of Smart OB / 'Manual OB'
 * @property {string} paymentProvider - payment_provider
 * @property {boolean} isActive - true-connect,false- disconnect
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {boolean} createAccount - create_account
 * @property {string} advancedPermissionsFlow - API for granting permissions for recurring payments. For PayPal: classic / new rest
 * @property {GUID} msid - msid
 * @property {string} namespace - identifier of user origin
 * @return {LogObject<merchantConnectStartParams>} LogObject<merchantConnectStartParams>
 */
export function merchantConnectStart(params) {
    return { evid: 1009, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkOutViewTooltip' event (evid: 101)
 *
 * Event description: The visitoer looked at the tooltip
 *
 * @param {checkOutViewTooltipParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} buyerComponent - defines new buyer component (null for old, 'react' for new one)
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} tooltipText - description in tooltip
 * @property {string} tooltipType - card brands/PCI form etc
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @return {LogObject<checkOutViewTooltipParams>} LogObject<checkOutViewTooltipParams>
 */
export function checkOutViewTooltip(params) {
    return { evid: 101, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantConnectComplete' event (evid: 1011)
 *
 * Event description: Merchant success to connect to acount
 *
 * @param {merchantConnectCompleteParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} paymentProviderConnectId - guid of successful connection of provider
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - payment_category
 * @property {string} errorDesc - error_desc
 * @property {GUID} meta_site_id - msid
 * @property {string} user_agent - user_agent
 * @property {string} errorCode - reason of connection failure
 * @property {GUID} appid - app_id
 * @property {string} origin - 'Welcome page' - made from welcome page / 'Add provider' - manual flow or second page of Smart OB
 * @property {string} status - pay_pal_status
 * @property {string} paymentProvider - payment_provider
 * @property {boolean} isActive - true-connect,false- disconnect
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {string} advancedPermissionsFlow - API for granting permissions for recurring payments. For PayPal: classic / new rest
 * @property {GUID} msid - msid
 * @property {string} namespace - identifier of user origin
 * @return {LogObject<merchantConnectCompleteParams>} LogObject<merchantConnectCompleteParams>
 */
export function merchantConnectComplete(params) {
    return { evid: 1011, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'auditEvent' event (evid: 1012)
 *
 * Event description: audit event.
 *
 * @param {auditEventParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} cid - visitor_id
 * @property {GUID} uuid - logged_session_uuid
 * @property {string} user_agent - user_agent
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} eventData - event_data
 * @return {LogObject<auditEventParams>} LogObject<auditEventParams>
 */
export function auditEvent(params) {
    return { evid: 1012, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantAutoConnectedAccountClaimed' event (evid: 1013)
 *
 * Event description: Merchant Setup Completed
 *
 * @param {merchantAutoConnectedAccountClaimedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - creditCard,offline
 * @property {string} user_agent - user_agent
 * @property {GUID} appid - app_id
 * @property {string} paymentProvider - com.eway, net.authorize, NA
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantAutoConnectedAccountClaimedParams>} LogObject<merchantAutoConnectedAccountClaimedParams>
 */
export function merchantAutoConnectedAccountClaimed(params) {
    return { evid: 1013, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantAutoConnectAccountFailed' event (evid: 1014)
 *
 * Event description: Merchant Auto Connecte Payment  Failed
 *
 * @param {merchantAutoConnectAccountFailedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - creditCard,offline
 * @property {string} errorDesc - error of failer
 * @property {string} user_agent - user_agent
 * @property {GUID} appid - app_id
 * @property {string} paymentProvider - com.eway, net.authorize, NA
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantAutoConnectAccountFailedParams>} LogObject<merchantAutoConnectAccountFailedParams>
 */
export function merchantAutoConnectAccountFailed(params) {
    return { evid: 1014, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantOnboardingStatusWithYandex' event (evid: 1015)
 *
 * Event description: Merchant Onboarding status with Yandex
 *
 * @param {merchantOnboardingStatusWithYandexParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} onboardingStatus - pending, applicationSubmitted ,pendingReview ,pendingInformationCompletion, pendingFinalReview ,live ,banned
 * @property {string} paymentCategory - YandexPB
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {string} user_agent - user_agent
 * @property {GUID} attemptId - attempt id
 * @property {GUID} appid - app_id
 * @property {string} paymentProvider - YandexPB
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantOnboardingStatusWithYandexParams>} LogObject<merchantOnboardingStatusWithYandexParams>
 */
export function merchantOnboardingStatusWithYandex(params) {
    return { evid: 1015, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentAcceptanceConfigurationServiceRecordSuccesSrc64Evid1016' event (evid: 1016)
 *
 * Event description: Events to track successful migration of PaymentAcceptanceConfigurationService
 *
 * @param {paymentAcceptanceConfigurationServiceRecordSuccesSrc64Evid1016Params} params - The parameters for this event
 * @property {GUID} appInstanceId - app_instanc
 * @property {GUID} appId - app_id
 * @property {string} paymentCategory - payment_method
 * @property {string} paymentProvider - payment_provider
 * @return {LogObject<paymentAcceptanceConfigurationServiceRecordSuccesSrc64Evid1016Params>} LogObject<paymentAcceptanceConfigurationServiceRecordSuccesSrc64Evid1016Params>
 */
export function paymentAcceptanceConfigurationServiceRecordSuccesSrc64Evid1016(params) {
    return { evid: 1016, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'useCashierSettings' event (evid: 1017)
 *
 * Event description: The result of check, if it is possible to use Cashier Settings for the TPA. Is initiated for all existing verticals as user enters payment tab from one of them. Will be repeated for all TPAs as new TPA is added to the site
 *
 * @param {useCashierSettingsParams} params - The parameters for this event
 * @property {GUID} visitorId - not in use
 * @property {boolean} isProvisioned - true if the site was provisioned with cashier during online sync
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {boolean} hadCashierSettings - had_cashier_settings before sync
 * @property {GUID} appInstanceId - app_instance_id user came from
 * @property {GUID} uuid - uuid
 * @property {string} user_agent - user_agent
 * @property {boolean} hadNonCashierSettings - had_non_cashier_settings before sync
 * @property {GUID} appId - app_id user came from
 * @property {GUID} msid - msid
 * @property {boolean} useCashier - vertical use cashier settings. in case when site has cashier and non cashier settings, cashier app will use it's settings (use_cashier = true), vertical with non cashier settings won't be allowed to use cashier settings until conflict is merged (use_cashier = false). it's the only case when different tpas has different use_cashier value per same site
 * @return {LogObject<useCashierSettingsParams>} LogObject<useCashierSettingsParams>
 */
export function useCashierSettings(params) {
    return { evid: 1017, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'migrationStarted' event (evid: 1018)
 *
 * Event description: Migration Started
 *
 * @param {migrationStartedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - credit card,paypal
 * @property {boolean} wasBulkMigrated - true/false
 * @property {string} user_agent - user_agent
 * @property {GUID} appid - app_id
 * @property {string} paymentProvider - payment provider
 * @property {boolean} isActive - is the user active
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<migrationStartedParams>} LogObject<migrationStartedParams>
 */
export function migrationStarted(params) {
    return { evid: 1018, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'migrationEnded' event (evid: 1019)
 *
 * Event description: Migration Ended
 *
 * @param {migrationEndedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} migrationSucceed - success yes/no
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - credit card,paypal
 * @property {boolean} wasBulkMigrated - true/false
 * @property {string} user_agent - user_agent
 * @property {GUID} appid - app_id
 * @property {string} paymentProvider - payment provider
 * @property {boolean} isActive - is the user active
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<migrationEndedParams>} LogObject<migrationEndedParams>
 */
export function migrationEnded(params) {
    return { evid: 1019, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'disconnectReasonSelected' event (evid: 102)
 *
 * Event description: Merchant select reason when disconnect WP
 *
 * @param {disconnectReasonSelectedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - currently connected category
 * @property {string} page - page
 * @property {string} platformName - platform_name
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} reason - reason selected or user input
 * @property {string} modal_name - disconnect_last_provider_popup / switch_provider etc
 * @property {string} paymentProvider - currently connected provider
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<disconnectReasonSelectedParams>} LogObject<disconnectReasonSelectedParams>
 */
export function disconnectReasonSelected(params) {
    return { evid: 102, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'verticalsOfflineSyncFinished' event (evid: 1021)
 *
 * Event description: Offline Verticals Sync is finished
 *
 * @param {verticalsOfflineSyncFinishedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} keepSettings - false - settings are dismissed due to the conflict
 * @property {boolean} cashierProvision - cashier_provision
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {boolean} isShowToBuyer - is the connection active (available on check out)
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - credit card,paypal
 * @property {boolean} isConnect - -
 * @property {string} user_agent - user_agent
 * @property {GUID} appid - app_id
 * @property {string} paymentProvider - payment provider
 * @property {GUID} msid - msid
 * @property {string} conflictType - conflict_type
 * @property {string} winnerDefinition - winner_definition
 * @return {LogObject<verticalsOfflineSyncFinishedParams>} LogObject<verticalsOfflineSyncFinishedParams>
 */
export function verticalsOfflineSyncFinished(params) {
    return { evid: 1021, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buyerRecurringPaymentInitialized' event (evid: 1022)
 *
 * Event description: Recurring Payment Initialized (call from TPA received), contains payment details
 *
 * @param {buyerRecurringPaymentInitializedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {GUID} orderSnapshotId - order_snapshot_id (equal to id in 1001 event)
 * @property {string} user_agent - user_agent
 * @property {string} recurringFrequency - Day / Week / Month
 * @property {number} recurringInterval - number of payments to be payed by buyer
 * @property {string} currency - amount currency - USD/GBP/EUR...
 * @property {number} trialInterval - number of trial (zero amount) payments
 * @property {GUID} appId - app_id
 * @property {string} trialFrequency - trial period frequency: Day / Week / Month
 * @property {GUID} msid - msid
 * @return {LogObject<buyerRecurringPaymentInitializedParams>} LogObject<buyerRecurringPaymentInitializedParams>
 */
export function buyerRecurringPaymentInitialized(params) {
    return { evid: 1022, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buyerRecurringPaymentStatusChanged' event (evid: 1023)
 *
 * Event description: Recurring Payment Status Changed
 *
 * @param {buyerRecurringPaymentStatusChangedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {GUID} orderSnapshotId - order_snapshot_id (equal to id in 1001 event)
 * @property {string} cancelInitiator - Merchant / Buyer / Payment
 * @property {string} user_agent - user_agent
 * @property {string} recurringFrequency - Day / Week / Month
 * @property {number} recurringInterval - number of payments to be payed by buyer
 * @property {string} recurringStatus - Active / Finished / Canceled
 * @property {string} currency - amount currency - USD/GBP/EUR...
 * @property {number} trialInterval - number of trial (zero amount) payments
 * @property {GUID} appId - app_id
 * @property {string} trialFrequency - trial period frequency: Day / Week / Month
 * @property {GUID} msid - msid
 * @return {LogObject<buyerRecurringPaymentStatusChangedParams>} LogObject<buyerRecurringPaymentStatusChangedParams>
 */
export function buyerRecurringPaymentStatusChanged(params) {
    return { evid: 1023, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'squarePosSettingsMigration' event (evid: 1024)
 *
 * Event description: Square POS settings migration from Stores to Cashier
 *
 * @param {squarePosSettingsMigrationParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} statusCashier - Square status in Cashier: none / selected / connected
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} result - settings saved / square not found / pos exists / cashier not found
 * @property {string} statusPos - POS status in Cashier: none / selected / connected
 * @property {string} user_agent - user_agent
 * @property {string} paymentProvider - com.square
 * @property {GUID} msid - msid
 * @return {LogObject<squarePosSettingsMigrationParams>} LogObject<squarePosSettingsMigrationParams>
 */
export function squarePosSettingsMigration(params) {
    return { evid: 1024, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'availablePaymentMethodsToShowOnCheckout' event (evid: 1025)
 *
 * Event description: When checking connected payment methods - if they are available on check-out, event is sent per payment method and indicates that provider is available to pay with (is_active = true)
 *
 * @param {availablePaymentMethodsToShowOnCheckoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} source - stage where request was sent from: order created / tpa request
 * @property {string} paymentCategory - payment_category
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {GUID} orderSnapshotId - order_snapshot_id
 * @property {string} user_agent - user_agent
 * @property {GUID} requestid - request_id (allows to join different payment methods per request)
 * @property {string} paymentProvider - payment_provider
 * @property {boolean} isActive - true if payment provider is available on check out
 * @property {GUID} appId - app_id
 * @property {GUID} msid - -
 * @property {GUID} siteMemberId - site_member_id
 * @return {LogObject<availablePaymentMethodsToShowOnCheckoutParams>} LogObject<availablePaymentMethodsToShowOnCheckoutParams>
 */
export function availablePaymentMethodsToShowOnCheckout(params) {
    return { evid: 1025, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundRequestSent' event (evid: 1026)
 *
 * Event description: Refund Request Sent
 *
 * @param {refundRequestSentParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} providerAccountId - provider_account_id
 * @property {string} paymentCategory - payment_category
 * @property {string} providertxreason - provider_tx_reason
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {string} providertxid - provider_tx_id
 * @property {string} signature - signature
 * @property {GUID} transactionid - transaction_id
 * @property {string} reason - reason of refund
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} user_agent - user_agent
 * @property {number} amount - refund amount
 * @property {string} transactionStatus - NOT IN USE
 * @property {GUID} appid - app_id
 * @property {string} origin - payments dashboard / tpa dashboard / provider
 * @property {string} providertxstatus - provider_tx_status
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - currency
 * @property {string} providerErrorName - NOT IN USE
 * @property {GUID} msid - NOT VALID. you can take msid from slowly_changing_apps, joined by app_instance_id
 * @property {string} refundType - full / partial
 * @return {LogObject<refundRequestSentParams>} LogObject<refundRequestSentParams>
 */
export function refundRequestSent(params) {
    return { evid: 1026, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundRequestCompleted' event (evid: 1027)
 *
 * Event description: Refund Request Completed
 *
 * @param {refundRequestCompletedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} providerAccountId - provider_account_id
 * @property {number} cashierErrorCode - cashier error code
 * @property {string} paymentCategory - payment_category
 * @property {string} providerData - additional info from provider (expected - errors)
 * @property {string} providertxreason - provider_tx_reason
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {string} providertxid - provider_tx_id
 * @property {string} signature - signature
 * @property {GUID} transactionid - transaction_id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} user_agent - user_agent
 * @property {number} amount - refund amount
 * @property {string} transactionStatus - refund status
 * @property {GUID} appid - app_id
 * @property {string} origin - payments dashboard / tpa dashboard / provider
 * @property {string} providertxstatus - provider_tx_status
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - currency
 * @property {string} providerErrorName - error description, if any
 * @property {GUID} msid - NOT VALID. you can take msid from slowly_changing_apps, joined by app_instance_id
 * @property {string} refundType - full / partial
 * @return {LogObject<refundRequestCompletedParams>} LogObject<refundRequestCompletedParams>
 */
export function refundRequestCompleted(params) {
    return { evid: 1027, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'squareTokenRenew' event (evid: 1029)
 *
 * Event description: status of token renew for provider
 *
 * @param {squareTokenRenewParams} params - The parameters for this event
 * @property {GUID} visitorId - not in use
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {string} reason - reason of failure
 * @property {string} user_agent - user_agent
 * @property {string} providerId - providerId
 * @property {string} status - renew status: skip / failed / success
 * @property {string} type - same / new for success
 * @property {GUID} msid - synthetic, cashier instance id
 * @return {LogObject<squareTokenRenewParams>} LogObject<squareTokenRenewParams>
 */
export function squareTokenRenew(params) {
    return { evid: 1029, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickFixIssue' event (evid: 103)
 *
 * Event description: User clicks on 'Fix Issue' with Wix Payments accounts
 *
 * @param {merchantClickFixIssueParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - provider under wp
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} bannerName - i.e. wix_payments_card_notification
 * @property {string} notificationType - badge , verificationMessage , profileStatus  etc
 * @return {LogObject<merchantClickFixIssueParams>} LogObject<merchantClickFixIssueParams>
 */
export function merchantClickFixIssue(params) {
    return { evid: 103, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletCreditCardSaved' event (evid: 1030)
 *
 * Event description: Credit Card is sent
 *
 * @param {myWalletCreditCardSavedParams} params - The parameters for this event
 * @property {GUID} creditCardId - credit_card_id
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - cashier app_instance_id
 * @property {boolean} status - true if saved successfully
 * @property {GUID} memberId - null if visitor is not logged in
 * @property {GUID} appId - cashier app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<myWalletCreditCardSavedParams>} LogObject<myWalletCreditCardSavedParams>
 */
export function myWalletCreditCardSaved(params) {
    return { evid: 1030, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentAgreementFlow' event (evid: 1033)
 *
 * Event description: Events to track payment agreement creation flow
 *
 * @param {paymentAgreementFlowParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - payment method
 * @property {string} result - result of creation payment agreement
 * @property {string} paymentAgreementId - agreement id associated to the subscription with this method
 * @property {string} errorReason - reason of failure
 * @property {GUID} transactionid - guid
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} user_agent - user_agent
 * @property {string} stepName - step of Payment agreement creation flow
 * @property {string} paymentProvider - PayPal, Stripe, etc
 * @property {string} savedPaymentMethodId - guid of payment method for which payment agreement is being created
 * @property {boolean} merchantUseAllowed - true if buyer choose to allow for MIT trx or saved by merchant
 * @property {GUID} msid - msid
 * @property {boolean} merchantInitiated - agreement is saved by merchant
 * @return {LogObject<paymentAgreementFlowParams>} LogObject<paymentAgreementFlowParams>
 */
export function paymentAgreementFlow(params) {
    return { evid: 1033, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentMethodCreationProcess' event (evid: 1034)
 *
 * Event description: Status of creation PM while Updating PM
 *
 * @param {paymentMethodCreationProcessParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - payment method
 * @property {string} result - result of PM creation
 * @property {string} errorReason - reason of failure
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} user_agent - user_agent
 * @property {string} stepName - initialisation, final result
 * @property {string} ownerSiteMemberId - owner of saved payment method if it's site member
 * @property {string} ownerUserId - owner of saved payment method if it's user (OneApp case)
 * @property {string} paymentProvider - payment_provider
 * @property {string} savedPaymentMethodId - guid of payment method for which payment agreement is being created
 * @property {string} saveInitiator - who initiate saving payment method: Merchant / Buyer
 * @property {boolean} merchantUseAllowed - true if buyer choose to allow for MIT trx
 * @property {GUID} msid - msid
 * @property {boolean} merchantInitiated - payment method is saved by merchant
 * @property {GUID} transactionId - transaction_id
 * @return {LogObject<paymentMethodCreationProcessParams>} LogObject<paymentMethodCreationProcessParams>
 */
export function paymentMethodCreationProcess(params) {
    return { evid: 1034, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'voidFlow' event (evid: 1035)
 *
 * Event description: Events to track void flow
 *
 * @param {voidFlowParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - payment method
 * @property {string} errorReason - reason of failure
 * @property {GUID} transactionid - guid
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} authorizationAmount - authorization amount
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount
 * @property {string} stepName - step of Void flow:  Initiated/ Completed
 * @property {string} status - status of capture flow: Initiated/Pending/ Completed/ Failed
 * @property {string} paymentProvider - PayPal, Stripe, etc
 * @property {string} initiatorName - action is initiated manually - by the user - or from the automatic scheduled action task - by the system
 * @property {GUID} msid - msid
 * @property {string} scheduledActionDelay - expiration time in [days] when scheduled action is planned
 * @property {string} actionInitiatorId - id of those who initiated payment - "buyer" | "merchant" | "service"
 *
 * @property {string} actionInitiatorType - type of those who initiated payment - "buyer" | "merchant" | "service"
 * @property {string} scheduledAction - Capture/Void ecom - capture by default other - Void
 * @return {LogObject<voidFlowParams>} LogObject<voidFlowParams>
 */
export function voidFlow(params) {
    return { evid: 1035, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'aptureTransactionFlow' event (evid: 1036)
 *
 * Event description: Events to track capture flow of transaction
 *
 * @param {aptureTransactionFlowParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - payment method
 * @property {string} errorReason - reason of failure
 * @property {GUID} transactionid - guid
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} authorizationAmount - authorization amount
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount
 * @property {string} stepName - step of capture flow:  Initiated/ Completed
 * @property {string} status - status of capture flow: Initiated/Pending/ Completed/ Failed
 * @property {string} paymentProvider - PayPal, Stripe, etc
 * @property {string} initiatorName - action is initiated manually - by the user - or from the automatic scheduled action task - by the system
 * @property {GUID} msid - msid
 * @property {string} scheduledActionDelay - expiration time in [days] when scheduled action is planned
 *
 * @property {string} actionInitiatorId - id of those who initiated payment - "buyer" | "merchant" | "service"
 * @property {string} actionInitiatorType - type of those who initiated payment - "buyer" | "merchant" | "service"
 *
 * @property {string} scheduledAction - Capture/Void ecom - capture by default other - Void
 *
 * @return {LogObject<aptureTransactionFlowParams>} LogObject<aptureTransactionFlowParams>
 */
export function aptureTransactionFlow(params) {
    return { evid: 1036, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'squareCustomerApiFlow' event (evid: 1037)
 *
 * Event description: Events to track Square Customer API flow:
 * 1) Happy flow:
 *  initialization>> try to find>> result and error_reason is null
 *  initialization>> try to create>> result and error_reason is null
 * 2) Failure on any step:
 *  initialization>> … and error_reason is not null
 *
 * @param {squareCustomerApiFlowParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} squareCustomerId - customer_id from Square if it is found
 * @property {GUID} uuid - uuid
 * @property {string} emailHash - email hash
 * @property {string} errorReason - reason of failure
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} user_agent - user_agent
 * @property {string} customerId - customer_id
 * @property {string} stepName - step of Customer API flow: initialization/ try to find/ try to create/ result
 * @property {string} paymentProvider - Square
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {GUID} transactionId - guid
 * @return {LogObject<squareCustomerApiFlowParams>} LogObject<squareCustomerApiFlowParams>
 */
export function squareCustomerApiFlow(params) {
    return { evid: 1037, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantConnectSquareProviderWithRequestedPermissions' event (evid: 1038)
 *
 * Event description: Once user clicks connect, we send request to generate the corresponding redirect url where can be asked additional permissions for WIX through that url [Square].
 *
 * @param {merchantConnectSquareProviderWithRequestedPermissionsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} gatewayId - payment_provider
 * @property {GUID} uuid - uuid
 * @property {string} result - the result of connecting process: connected/ deniedd
 * @property {string} errorReason - reason of failure
 * @property {string} user_agent - user_agent
 * @property {string} permissionsRequested - array with requested permissions for WIX
 * @property {string} stepName - step of connecting Square Provider flow: start_connection/ redirect / end_connection
 * @property {GUID} appid - app_id
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantConnectSquareProviderWithRequestedPermissionsParams>} LogObject<merchantConnectSquareProviderWithRequestedPermissionsParams>
 */
export function merchantConnectSquareProviderWithRequestedPermissions(params) {
    return { evid: 1038, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'captchaChallengeEnablingDisabling' event (evid: 1039)
 *
 * Event description: This event fires once captcha challenge was enabled or disabled on specific merchant to prevent bruteforce attack
 *
 * @param {captchaChallengeEnablingDisablingParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} enablingReason - reason due to which captcha challenge was enabled
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {boolean} captchaRequested - true if captcha challenge is requested for that merchant
 * @property {string} user_agent - user_agent
 * @property {string} captchaLevel - on which level captcha challenge was enabled: site/buyer
 * @property {GUID} appid - app_id
 * @property {GUID} msid - msid
 * @property {string} captchaRequiredForPM - list of payment methods which require passing captcha challenge (e.i. creditCard))
 * @return {LogObject<captchaChallengeEnablingDisablingParams>} LogObject<captchaChallengeEnablingDisablingParams>
 */
export function captchaChallengeEnablingDisabling(params) {
    return { evid: 1039, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkOutSubmitOrder' event (evid: 104)
 *
 * Event description: The visitor clicked on 'Submit order' and transaction was created
 *
 * @param {checkOutSubmitOrderParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id ; starting from June-4th 2020 the value is aid
 * @property {boolean} isPartner - is_partner
 * @property {GUID} creditCardId - for previously saved credit cards
 * @property {string} paymentCategory - payment_category
 * @property {boolean} saveCreditCard - true if user choose to save CC
 * @property {string} appInstanceId - app_instance_id
 * @property {GUID} aid - visitor_id from aid
 * @property {GUID} orderId - vertical order id
 * @property {string} savePaymentMethodDisplayOption - if  pm is supposed to be saved by default/ by willing of uou/ saving is not supported:‘disclaimer’\ ‘checkbox’\‘show_nothing’
 * @property {string} buyerComponent - -
 * @property {string} paymentAgreementId - payment_agreement_id
 * @property {GUID} membershipId - membership_id
 * @property {string} errorDesc - where is the error
 * @property {GUID} cardId - -
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodType - For credit card- credit card type:jcb
 * mastercard
 * discover
 * isracard
 * maestro
 * visa
 * amex
 * diners
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} redirectTarget - EMBEDDED - modal, NEW_WINDOW - new tab/window, SAME_WINDOW - full redirect
 * @property {boolean} forcedFullRedirectInIAB - Whether full-redirect was forced in InAppBrowser instead of new tab/window strategy
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} status - pass client validation
 * @property {string} paymentProvider - stripe,paypal,square
 * @property {string} currency - currency
 * @property {string} savedPaymentMethodId - saved_payment_method_id (the same guid for CC )
 * @property {GUID} memberId - null if visitor is not logged in
 * @property {string} allowMerchantUseDisplayOption - if pm is supposed to be saved for MIT trx by willing of uou/ required to be saved/ saving is not supported: 'SHOW_ALLOW_MERCHANT_USE_CHECKBOX’, ‘SHOW_NOTHING’
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {boolean} merchantUseAllowed - true if buyer choose to allow for MIT trx
 * @property {boolean} captchaTokenSent - true if we send captcha token to BE
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} transactionId - transaction_id
 * @property {string} mobileAppVervion - mobile_app_vervion
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {boolean} delayedCapture - true, if order is placed with Auth&Capture
 * @property {boolean} setupFutureUsages - true, if card tokenization is required for that payment
 * @property {string} purchaseFlowId - key to identify ecom flow
 * @property {string} catalogAppId - A list of verticals which are in this order separated by “ ,“:(stores app id, bookings app_id, ...) .
 * @property {string} intentCharge - intent of charge: one time/ recurring
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<checkOutSubmitOrderParams>} LogObject<checkOutSubmitOrderParams>
 */
export function checkOutSubmitOrder(params) {
    return { evid: 104, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'validationOfCaptchaChallengeEnablingDisabling' event (evid: 1040)
 *
 * Event description: This event fires once BE receives the request for charge and validates it
 *
 * @param {validationOfCaptchaChallengeEnablingDisablingParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - payment method
 * @property {string} result - result of validation: Success/Failure
 * @property {string} errorReason - reason of failed validation: CAPTCHA_REQUIRED/ CAPTCHA_ASSESSMENT_FAILED, RateLimiter, fraudsterOps
 * @property {string} captchaToken - id of captcha token if exists
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} user_agent - user_agent
 * @property {string} stepName - request received - start, request validated - end
 * @property {GUID} appid - app_id
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} msid - msid
 * @property {GUID} transactionId - possible transaction_id
 * @return {LogObject<validationOfCaptchaChallengeEnablingDisablingParams>} LogObject<validationOfCaptchaChallengeEnablingDisablingParams>
 */
export function validationOfCaptchaChallengeEnablingDisabling(params) {
    return { evid: 1040, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addAnElementToPopulation' event (evid: 1041)
 *
 * Event description: BI event is fired when we add an element to population
 *
 * @param {addAnElementToPopulationParams} params - The parameters for this event
 * @property {string} msid - site which is supposed to be added
 * @property {string} population_id - id of population, population is assigned per specific product/ feature
 * @property {boolean} isSuccess - true if try to add was successful
 * @property {GUID} accountId - account id/ uuid of owner
 * @property {string} populationName - name of product/ feature which population is referred to
 * @return {LogObject<addAnElementToPopulationParams>} LogObject<addAnElementToPopulationParams>
 */
export function addAnElementToPopulation(params) {
    return { evid: 1041, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'creditCardTokenizationDetokenizationSrc64Evid1042' event (evid: 1042)
 *
 * Event description: This BI event fires once credit card undergoes tokenization/ detokenization flows. Only result of actions will be written.
 *
 * @param {creditCardTokenizationDetokenizationSrc64Evid1042Params} params - The parameters for this event
 * @property {string} action - CreditCardTokenization/CreditCardDetokenization
 * @property {string} tokenId - credit card token
 * @property {string} tenantId - ours or premium
 * @property {string} cvvToken - cvv token
 * @property {boolean} isInTransit - if true, token is temporary
 * @property {string} tokenizationError - errors from skless if exist
 * @property {string} cvvTokenizationError - errors from cvv service if exist
 * @property {string} uuid - uuid
 * @property {string} meta_site_id - msid
 * @return {LogObject<creditCardTokenizationDetokenizationSrc64Evid1042Params>} LogObject<creditCardTokenizationDetokenizationSrc64Evid1042Params>
 */
export function creditCardTokenizationDetokenizationSrc64Evid1042(params) {
    return { evid: 1042, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'syncCashierOrderTransactionWithProvider' event (evid: 1043)
 *
 * Event description: Sync order/ transaction with provider: request and response logging
 *
 * Updating cashier order/ transaction with data from provider: amount, tax, billing/ shipping info
 *
 * @param {syncCashierOrderTransactionWithProviderParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} paymentMethod - CreditCard/Ewallet etc
 * @property {string} providerAccountId - provider account id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {string} orderId - TPA order_id if exists
 * @property {GUID} uuid - uuid
 * @property {DateTime} processingtime - processing time from provider
 * @property {string} gateway - gateway
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} authorizationAmount - authorization_amount
 * @property {boolean} testTransaction - is_test
 * @property {string} managingAppId - managing_app_id
 * @property {string} checkoutType - express / regular
 * @property {string} sessionId - front_session_id for express flow
 * @property {string} paymentProvider - WixPayments, PayPal
 * @property {string} currency - amount currency - USD/GBP/EUR...
 * @property {GUID} msid - msid
 * @property {number} appfeeAmount - application fee amount - multiply by 100,000
 * @property {string} status - Initiated/ Failed/ Succeeded
 * @property {string} providerOrderId - payment guid on provider side
 * @property {string} billingEmailHash - billing email hash
 * @property {string} shippingEmailHash - shipping email hash
 * @property {number} shippingAmount - shipping_amount - multiply by 100,000
 * @property {number} taxAmount - tax_amount - multiply by 100,000
 * @property {number} discountAmount - discount_amount - multiply by 100,000
 * @property {string} errorReason - error_reason
 * @property {string} transactionId - transaction_id
 * @property {string} appId - app_id
 * @return {LogObject<syncCashierOrderTransactionWithProviderParams>} LogObject<syncCashierOrderTransactionWithProviderParams>
 */
export function syncCashierOrderTransactionWithProvider(params) {
    return { evid: 1043, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'updateOrderSnapshotIdTransactionIdWithOrderData' event (evid: 1044)
 *
 * Event description: Update order_snapshot_id/ transaction_id  with order data: response logging
 *
 * Updating cashier order/ transaction with data during payment flow: only amount
 * (TBD billing/ shipping info )
 *
 * @param {updateOrderSnapshotIdTransactionIdWithOrderDataParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} paymentMethod - CreditCard/Ewallet etc
 * @property {string} providerAccountId - provider account id
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - money amount - multiply by 100,000
 * @property {string} orderId - TPA order_id if exists
 * @property {GUID} uuid - uuid
 * @property {DateTime} processingtime - processing time from provider
 * @property {string} gateway - gateway
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} authorizationAmount - authorization_amount
 * @property {boolean} testTransaction - is_test
 * @property {string} managingAppId - managing_app_id
 * @property {string} checkoutType - express / regular
 * @property {string} sessionId - front_session_id for express flow
 * @property {string} paymentProvider - WixPayments, PayPal
 * @property {string} currency - amount currency - USD/GBP/EUR...
 * @property {GUID} msid - msid
 * @property {number} appfeeAmount - application fee amount - multiply by 100,000
 * @property {string} status - Initiated/ Failed/ Succeeded
 * @property {string} providerOrderId - payment guid on provider side
 * @property {string} billingEmailHash - billing email hash
 * @property {string} shippingEmailHash - shipping email hash
 * @property {number} shippingAmount - shipping_amount - multiply by 100,000
 * @property {number} taxAmount - tax_amount - multiply by 100,000
 * @property {number} discountAmount - discount_amount - multiply by 100,000
 * @property {string} appId - app_id
 * @property {string} transactionId - transaction_id
 * @property {string} errorReason - error_reason
 * @return {LogObject<updateOrderSnapshotIdTransactionIdWithOrderDataParams>} LogObject<updateOrderSnapshotIdTransactionIdWithOrderDataParams>
 */
export function updateOrderSnapshotIdTransactionIdWithOrderData(params) {
    return { evid: 1044, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutListOfConnectedShownOnCheckoutPaymentMethods' event (evid: 1045)
 *
 * Event description: Sends list of connected payment methods and list of 'shown on checkout' (displayed to UoU in chosen currency) payment methods from backend on checkout shown (incl sites with enabled multicurrency feature)
 *
 * @param {checkoutListOfConnectedShownOnCheckoutPaymentMethodsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} uuid - uuid
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} paymentMethodsConnected - list of site connected payment methods
 * @property {string} paymentProvidersConnected - list of site connected payment providers
 * @property {string} paymentMethodsShown - list of shown on checkout payment methods
 * @property {string} paymentProvidersShown - list of shown on checkout payment providers
 * @property {string} orderId - TPA order_id if exists
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} siteCurrency - site currency
 * @property {string} checkoutSessionId - checkout_session_id if exists
 * @return {LogObject<checkoutListOfConnectedShownOnCheckoutPaymentMethodsParams>} LogObject<checkoutListOfConnectedShownOnCheckoutPaymentMethodsParams>
 */
export function checkoutListOfConnectedShownOnCheckoutPaymentMethods(params) {
    return { evid: 1045, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buyerChoosesPaymentMethod' event (evid: 105)
 *
 * Event description: Buyer chooses payment method: Offline payment, MercadoPago, Paypal, Credit card
 *
 * @param {buyerChoosesPaymentMethodParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} creditCardId - null if visitor doesn't have saved cc
 * @property {string} paymentCategory - payment_category
 * @property {string} mandatoryFieldsList - list of required mandatory fields that were shown to UoU
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} orderId - order id
 * @property {string} savePaymentMethodDisplayOption - if  pm is supposed to be saved by default/ by willing of uou/ saving is not supported:‘disclaimer’\ ‘checkbox’\‘show_nothing’
 * @property {string} buyerComponent - -
 * @property {string} paymentMethod - -
 * @property {string} paymentAgreementId - null if visitor doesn't have saved payment agreements
 * @property {GUID} membershipId - membership_id
 * @property {string} requiredPmMandatoryFields - list of required mandatory fields from payment provider
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} prefilledMandatoryFields - list of required mandatory fields  that were shown to UoU already prefilled
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - currency
 * @property {string} savedPaymentMethodId - saved_payment_method_id (the same guid for CC )
 * @property {GUID} memberId - null if visitor is not logged in
 * @property {string} allowMerchantUseDisplayOption - if pm is supposed to be saved for MIT trx by willing of uou/ required to be saved/ saving is not supported: 'SHOW_ALLOW_MERCHANT_USE_CHECKBOX’, ‘SHOW_NOTHING’
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} captchaRequiredForPM - payment method which requires passing captcha challenge (e.i. creditCard))
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {string} passedMandatoryFields - list of required mandatory fields  that weren't shown to UoU and were passed by vertical
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {boolean} delayedCapture - true, if widget will be shown shown with methods which support Auth&Capture
 * @property {boolean} setupFutureUsages - true, if card tokenization is required for that payment
 * @property {string} purchaseFlowId - key to identify ecom flow
 * @property {string} catalogAppId - A list of verticals which are in this order separated by “ ,“:(stores app id, bookings app_id, ...) .
 * @property {string} intentCharge - intent of charge: one time/ recurring
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<buyerChoosesPaymentMethodParams>} LogObject<buyerChoosesPaymentMethodParams>
 */
export function buyerChoosesPaymentMethod(params) {
    return { evid: 105, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'applePayShippingAddressNotAccepted' event (evid: 106)
 *
 * Event description: if Shipping addres is not OK
 *
 * @param {applePayShippingAddressNotAcceptedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {GUID} appInstanceId - vertical instance id
 * @property {number} amountLong - new amount ( * 100 000)
 * @property {string} paymentMethod - new method, when method was changed
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - session_id
 * @property {string} currency - new value
 * @property {string} appId - app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} checkoutType - regular / express
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<applePayShippingAddressNotAcceptedParams>} LogObject<applePayShippingAddressNotAcceptedParams>
 */
export function applePayShippingAddressNotAccepted(params) {
    return { evid: 106, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantPaymentMethodPageLoadStart' event (evid: 107)
 *
 * Event description: Merchant click on payment settings in TPA and the cashier payment method page start to load
 *
 * @param {merchantPaymentMethodPageLoadStartParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - loading time in miliseconds
 * @property {string} referralInfo - place from which user came to AP
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} page - Payments Overview / smart onBoarding welcome / smart onBoarding connect
 * @property {string} platformName - desktop/oneApp etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @return {LogObject<merchantPaymentMethodPageLoadStartParams>} LogObject<merchantPaymentMethodPageLoadStartParams>
 */
export function merchantPaymentMethodPageLoadStart(params) {
    return { evid: 107, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantAcceptPaymentsPageLoadComplete' event (evid: 108)
 *
 * Event description: Merchant click on payment settings in TPA and the cashier payment method page load successfully. In case of wix.numeric--parsing error--duration, please reopen https://jira.wixpress.com/browse/PAY-6934
 *
 * @param {merchantAcceptPaymentsPageLoadCompleteParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} offeredPaymentCategory - array of offered payment_methods
 * @property {number} duration - loading time in miliseconds
 * @property {string} referralInfo - place from which user came to AP
 * @property {boolean} isPartner - is_partner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {string} paymentCategory - array of connected payment_methods
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} flowType - specific flows. e.g. CBD flow
 * @property {string} page - Payments Overview / smart onBoarding welcome / smart onBoarding connect
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} origin - place user came from
 * @property {boolean} status - 1-success/ 0-failed
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @property {string} screenResolution - screen resolution
 * @return {LogObject<merchantAcceptPaymentsPageLoadCompleteParams>} LogObject<merchantAcceptPaymentsPageLoadCompleteParams>
 */
export function merchantAcceptPaymentsPageLoadComplete(params) {
    return { evid: 108, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'applePayMerchantValidationError' event (evid: 109)
 *
 * Event description: ApplePay error during startMerchantValidationCheckout
 *
 * @param {applePayMerchantValidationErrorParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart_id
 * @property {string} errorReason - error_reason
 * @property {string} paymentmethod - paymentmethod
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - session_id
 * @property {string} paymentProvider - authorize.net/Stripe...
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} checkoutType - regular / express
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<applePayMerchantValidationErrorParams>} LogObject<applePayMerchantValidationErrorParams>
 */
export function applePayMerchantValidationError(params) {
    return { evid: 109, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'applePayStartMerchantValidation' event (evid: 110)
 *
 * Event description: ApplePay startMerchantValidation
 *
 * @param {applePayStartMerchantValidationParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} paymentmethod - ApplePay
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} checkoutType - regular / express
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<applePayStartMerchantValidationParams>} LogObject<applePayStartMerchantValidationParams>
 */
export function applePayStartMerchantValidation(params) {
    return { evid: 110, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'applePayMerchantValidationSucceeded' event (evid: 111)
 *
 * Event description: ApplePay success Validation Checkout (user is able to login/select payment method)
 *
 * @param {applePayMerchantValidationSucceededParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart_id
 * @property {string} paymentmethod - paymentmethod
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - session_id
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} checkoutType - regular / express
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<applePayMerchantValidationSucceededParams>} LogObject<applePayMerchantValidationSucceededParams>
 */
export function applePayMerchantValidationSucceeded(params) {
    return { evid: 111, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'applePayButtonRendered' event (evid: 112)
 *
 * Event description: Apple Pay Button was rendered
 *
 * @param {applePayButtonRenderedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} paymentmethod - Apple Pay
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - if Stores ask about shipping address
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen ( Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} pageType - page where button is appear
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} checkoutType - regular / express
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<applePayButtonRenderedParams>} LogObject<applePayButtonRenderedParams>
 */
export function applePayButtonRendered(params) {
    return { evid: 112, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'manualObClickOnUpdateInfo' event (evid: 113)
 *
 * Event description: User clicks on 'Update Info' with Wix Payments accounts
 *
 * @param {manualObClickOnUpdateInfoParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - provider under wp
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} bannerName - i.e. wix_payments_card_notification
 * @property {string} namespace - namespace
 * @return {LogObject<manualObClickOnUpdateInfoParams>} LogObject<manualObClickOnUpdateInfoParams>
 */
export function manualObClickOnUpdateInfo(params) {
    return { evid: 113, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'manualClickOnStartBillingAgreement' event (evid: 114)
 *
 * Event description: Click on Start PayPal review button to enable the Billing agreement option
 *
 * @param {manualClickOnStartBillingAgreementParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payPal
 * @property {string} platformName - null if desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - paypal
 * @property {string} pageType - accept_payments
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<manualClickOnStartBillingAgreementParams>} LogObject<manualClickOnStartBillingAgreementParams>
 */
export function manualClickOnStartBillingAgreement(params) {
    return { evid: 114, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'viewPaymentMethodNotification' event (evid: 115)
 *
 * Event description: Merchant view Payments Notifications
 *
 * @param {viewPaymentMethodNotificationParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} notificationKey - notification key
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} platformName - null(desktop)/oneApp/partnets etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} pageType - page where notification is appeared accept_payments, manage_page etc
 * @property {string} notificationBody - text of notification
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<viewPaymentMethodNotificationParams>} LogObject<viewPaymentMethodNotificationParams>
 */
export function viewPaymentMethodNotification(params) {
    return { evid: 115, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantConnectExistingProviderAccountClicked' event (evid: 116)
 *
 * Event description: click on connect on the payment method modal
 * Merchant gets error when he can't access the payment method ( can't login to the provider) - smartOB - connect in the payment method - connect/create account modal
 *
 * @param {merchantConnectExistingProviderAccountClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Credit Card
 * @property {boolean} isValid - 1-success 0- failed
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} errorDesc - the reason why merchant cant connect to the provider account
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @property {string} manualPaymentInstructions - text input that user enters in the "How to get paid" text field
 * @return {LogObject<merchantConnectExistingProviderAccountClickedParams>} LogObject<merchantConnectExistingProviderAccountClickedParams>
 */
export function merchantConnectExistingProviderAccountClicked(params) {
    return { evid: 116, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantCreateProviderAccountToConnectClicked' event (evid: 117)
 *
 * Event description: click on create a new account on the payment method modal - smartOB - create in the payment method - connect/create account modal
 *
 * @param {merchantCreateProviderAccountToConnectClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Credit Card
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @return {LogObject<merchantCreateProviderAccountToConnectClickedParams>} LogObject<merchantCreateProviderAccountToConnectClickedParams>
 */
export function merchantCreateProviderAccountToConnectClicked(params) {
    return { evid: 117, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantManageAccountClicked' event (evid: 118)
 *
 * Event description: Click manage in Payment Methods Page
 *
 * @param {merchantManageAccountClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - connected payment methods (credit card etc.)
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} avalableCategory - available payment methods
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @return {LogObject<merchantManageAccountClickedParams>} LogObject<merchantManageAccountClickedParams>
 */
export function merchantManageAccountClicked(params) {
    return { evid: 118, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantConfirmDisconnectClicked' event (evid: 123)
 *
 * Event description: Confirm disconnect- merchant is asked id he sure that he want to disconnect and click disconnect
 *
 * @param {merchantConfirmDisconnectClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Credit Card
 * @property {string} interaction - interaction
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} cta - init_disconnect / ok / cancel
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @return {LogObject<merchantConfirmDisconnectClickedParams>} LogObject<merchantConfirmDisconnectClickedParams>
 */
export function merchantConfirmDisconnectClicked(params) {
    return { evid: 123, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'applePayPaymentMethodSelected' event (evid: 124)
 *
 * Event description: Payment Method Selected on ApplePay Payment sheet
 *
 * @param {applePayPaymentMethodSelectedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} paymentmethod - payment method name
 * @property {number} amount - money amount - multiply by 100,000
 * @property {boolean} requestShipping - if Stores ask about shipping address
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen ( Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} pageType - page where button is appear
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} checkoutType - express / regular
 * @property {string} paymentMethodType - payment method type
 * @property {string} networkType - network type
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<applePayPaymentMethodSelectedParams>} LogObject<applePayPaymentMethodSelectedParams>
 */
export function applePayPaymentMethodSelected(params) {
    return { evid: 124, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buyerPaymentsComponentLoadStart' event (evid: 125)
 *
 * Event description: Payments component  load start
 *
 * @param {buyerPaymentsComponentLoadStartParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - loading time in miliseconds
 * @property {boolean} isPartner - is_partner
 * @property {string} payWith - Payment method
 * @property {string} appInstanceId - app_instance_id
 * @property {string} buyerComponent - defines new buyer component (null for old, 'react' for new one)
 * @property {string} intentCharge - intent of charge: one time/ recurring
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} currency - currency
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {boolean} delayedCapture - true, if widget will be shown shown with methods which support Auth&Capture
 *
 * @property {boolean} setupFutureUsages - true, if card tokenization is required for that payment
 * @property {string} purchaseFlowId - key to identify ecom flow
 * @property {string} catalogAppId - A list of verticals which are in this order separated by “ ,“:(stores app id, bookings app_id, ...) .
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<buyerPaymentsComponentLoadStartParams>} LogObject<buyerPaymentsComponentLoadStartParams>
 */
export function buyerPaymentsComponentLoadStart(params) {
    return { evid: 125, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentsComponentLoadComplete' event (evid: 126)
 *
 * Event description: The payment method pay page is loaded successfully,
 *
 * @param {paymentsComponentLoadCompleteParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - loading time in miliseconds
 * @property {boolean} isPartner - is_partner
 * @property {string} source - 1.pay with, 2.buyer choose, 3.one payment method
 * @property {string} paymentCategory - CreditCard/Ewallet,offline
 * @property {string} payWith - pay_with
 * @property {string} appInstanceId - app_instance_id
 * @property {string} buyerComponent - defines new buyer component (null for old, 'react' for new one)
 * @property {string} errorDesc - A technical error string. Empty if load complete successfully
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} status - true - success / false - failed
 * @property {string} paymentProvider - com.stripe/net.authorize
 * @property {string} currency - payment currency (is sent by vertical)
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Checkout Component was loaded for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} mobileAppVersion - mobile app version
 * @property {boolean} isIframe - is component rendered in iframe
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {boolean} delayedCapture - true, if widget will be shown shown with methods which support Auth&Capture
 * @property {string} intentCharge - intent of charge: one time/ recurring
 * (filtered payment methods which support recurring)
 * @property {boolean} setupFutureUsages - true, if card tokenization is required for that payment
 * @property {string} purchaseFlowId - key to identify ecom flow
 * @property {string} catalogAppId - A list of verticals which are in this order separated by “ ,“:(stores app id, bookings app_id, ...) .
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<paymentsComponentLoadCompleteParams>} LogObject<paymentsComponentLoadCompleteParams>
 */
export function paymentsComponentLoadComplete(params) {
    return { evid: 126, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'isEligibleApplePayButton' event (evid: 127)
 *
 * Event description: Apple Pay Button is eligible for rendering on express or regular checkout widget
 *
 * @param {isEligibleApplePayButtonParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} paymentmethod - Apple Pay
 * @property {number} amount - money amount - multiply by 100,000
 * @property {boolean} requestShipping - if Stores ask about shipping address
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen ( Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} pageType - page where button is appear
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} checkoutType - express / regular
 * @property {boolean} isEligible - Is Apple Pay eligible for rendering
 * @property {string} currentApplePaySdkVersion - current visitor's ApplePay SDK Version
 * @property {string} highestSupportedApplePaySdkVersion - highest supported visitor's  ApplePay SDK Version
 * @return {LogObject<isEligibleApplePayButtonParams>} LogObject<isEligibleApplePayButtonParams>
 */
export function isEligibleApplePayButton(params) {
    return { evid: 127, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantConnectMeClicked' event (evid: 128)
 *
 * Event description: Merchant clicks connect me in smartOB funnel (welcome page or page with existing connections)
 *
 * @param {merchantConnectMeClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {boolean} isFirst - true = if connection was made from initial page (with no connected pm)
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {boolean} isAdditionalStep - false if additional screen for confirming emails wasn't shown; else true; null for is_first = false
 * @property {string} emailStatus - default/changed/not required - tells user changed default (wix) email to create deferred account
 * @property {string} paymentProvider - list of providers, defined as default - for each category, according to categories order, separated by comma
 * @property {string} selectedValue - list of categories, selected by a user to connect, separated by comma
 * @property {string} place - place where action happened
 * @property {string} pageType - page name
 * @property {GUID} appId - cashier app_id
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} connectionType - auto/manual - connection type for each of defined providers, according to categories order, separated by comma
 * @return {LogObject<merchantConnectMeClickedParams>} LogObject<merchantConnectMeClickedParams>
 */
export function merchantConnectMeClicked(params) {
    return { evid: 128, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantShowOnCheckOutClicked' event (evid: 129)
 *
 * Event description: Merchant-click hide payment provider
 *
 * @param {merchantShowOnCheckOutClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,paypal
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - viewmode
 * @property {string} coupledCategory - paymentCategory which will activate implicitly. i.e. creditCard when turn on WP applePay
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {GUID} appId - TPA guid
 * @property {boolean} showBuyer - true/false
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @return {LogObject<merchantShowOnCheckOutClickedParams>} LogObject<merchantShowOnCheckOutClickedParams>
 */
export function merchantShowOnCheckOutClicked(params) {
    return { evid: 129, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantCompleteSetUpClicked' event (evid: 131)
 *
 * Event description: Merchant-click complete setup
 *
 * @param {merchantCompleteSetUpClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,paypal
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - viewmode
 * @property {string} notificationType - notification name
 * @property {string} origin - payment page / autoonboarding / active on checkout
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} pageType - page
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} bannerName - name of badge/banner
 * @return {LogObject<merchantCompleteSetUpClickedParams>} LogObject<merchantCompleteSetUpClickedParams>
 */
export function merchantCompleteSetUpClicked(params) {
    return { evid: 131, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnCountrySmartOb' event (evid: 132)
 *
 * Event description: Merchant-click on country on the Payment first page of the smart onboarding
 *
 * @param {merchantClickOnCountrySmartObParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} type - first_entry/remove_pm/country_list
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @return {LogObject<merchantClickOnCountrySmartObParams>} LogObject<merchantClickOnCountrySmartObParams>
 */
export function merchantClickOnCountrySmartOb(params) {
    return { evid: 132, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantChooseBusinessLocation' event (evid: 133)
 *
 * Event description: Merchant -choose business location
 *
 * @param {merchantChooseBusinessLocationParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} businessLocation - the country that the user selected
 * @property {string} origin - seamless/classic
 * @property {string} type - first_entry/country_list
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @return {LogObject<merchantChooseBusinessLocationParams>} LogObject<merchantChooseBusinessLocationParams>
 */
export function merchantChooseBusinessLocation(params) {
    return { evid: 133, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantCancelChangeBusinessLocation' event (evid: 134)
 *
 * Event description: Merchant -cancel change  business location
 *
 * @param {merchantCancelChangeBusinessLocationParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} businessLocation - the country that the user selected
 * @property {string} origin - seamless/classic
 * @property {string} type - first_entry/remove_pm/country_list
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @return {LogObject<merchantCancelChangeBusinessLocationParams>} LogObject<merchantCancelChangeBusinessLocationParams>
 */
export function merchantCancelChangeBusinessLocation(params) {
    return { evid: 134, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantSelectPaymentCategoryToConnect' event (evid: 137)
 *
 * Event description: Merchant-select payment category to connect
 *
 * @param {merchantSelectPaymentCategoryToConnectParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Credit Card
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} origin - - not in use
 * @property {string} pageName - Payment Method_first page
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantSelectPaymentCategoryToConnectParams>} LogObject<merchantSelectPaymentCategoryToConnectParams>
 */
export function merchantSelectPaymentCategoryToConnect(params) {
    return { evid: 137, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantUnselectPaymentCategoryToConnect' event (evid: 138)
 *
 * Event description: Merchant-unselect payment category to connect
 *
 * @param {merchantUnselectPaymentCategoryToConnectParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Credit Card
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - platform_name
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} origin - - not in use
 * @property {string} pageName - Payment Method_first page
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantUnselectPaymentCategoryToConnectParams>} LogObject<merchantUnselectPaymentCategoryToConnectParams>
 */
export function merchantUnselectPaymentCategoryToConnect(params) {
    return { evid: 138, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnChangeProviderOnThePaymentMethodPage' event (evid: 140)
 *
 * Event description: Merchant-click on change provider on the payment method page. It can be happens from the connected provider or from the link to list providers in +add, if it is add and not change we will get null on payment provider and payment category.
 *
 * @param {merchantClickOnChangeProviderOnThePaymentMethodPageParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} source - change provider/ list from +add
 * @property {string} paymentCategory - Credit Card
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {boolean} isConnect - true if payment method is connected, false if selected
 * @property {string} paymentProvider - current payment provider name
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantClickOnChangeProviderOnThePaymentMethodPageParams>} LogObject<merchantClickOnChangeProviderOnThePaymentMethodPageParams>
 */
export function merchantClickOnChangeProviderOnThePaymentMethodPage(params) {
    return { evid: 140, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnCanelInTheUpdateModal' event (evid: 141)
 *
 * Event description: Merchant--click on canel in the update modal- General event for click on cancel or exsit in the modal
 *
 * @param {merchantClickOnCanelInTheUpdateModalParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - paymentCategory
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} paymentProvider - payment_provider
 * @property {string} pageName - add more payment methods
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantClickOnCanelInTheUpdateModalParams>} LogObject<merchantClickOnCanelInTheUpdateModalParams>
 */
export function merchantClickOnCanelInTheUpdateModal(params) {
    return { evid: 141, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnAdd' event (evid: 143)
 *
 * Event description: Merchant-click on +add  on the second page of the payment methods
 *
 * @param {merchantClickOnAddParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} origin - notification/button
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantClickOnAddParams>} LogObject<merchantClickOnAddParams>
 */
export function merchantClickOnAdd(params) {
    return { evid: 143, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantSaveInManageAccountClicked' event (evid: 144)
 *
 * Event description: Merchant-click complete setup
 *
 * @param {merchantSaveInManageAccountClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,paypal
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} oldValue - old field setting
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} selectedValue - list of additional provider settings e.g. PayPal Credit
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} manualPaymentInstructions - text input that user enters in the "How to get paid" text field
 * @return {LogObject<merchantSaveInManageAccountClickedParams>} LogObject<merchantSaveInManageAccountClickedParams>
 */
export function merchantSaveInManageAccountClicked(params) {
    return { evid: 144, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnRemoveFromPaymentMethodsPage' event (evid: 146)
 *
 * Event description: Merchant- click on remove from Payment Methods page in the not connected list
 *
 * @param {merchantClickOnRemoveFromPaymentMethodsPageParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment method
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - payment provider
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantClickOnRemoveFromPaymentMethodsPageParams>} LogObject<merchantClickOnRemoveFromPaymentMethodsPageParams>
 */
export function merchantClickOnRemoveFromPaymentMethodsPage(params) {
    return { evid: 146, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'upgradePopupPaymentTabShown' event (evid: 150)
 *
 * Event description: Cashier Upgrade Pop Up is shown to user after payment method is successfully connected
 *
 * @param {upgradePopupPaymentTabShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} appid - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<upgradePopupPaymentTabShownParams>} LogObject<upgradePopupPaymentTabShownParams>
 */
export function upgradePopupPaymentTabShown(params) {
    return { evid: 150, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'upgradePopupPaymentTabInteraction' event (evid: 151)
 *
 * Event description: User interacts with Upgrade Pop Up which is shown after payment method is successfully connected: close or click Upgrade
 *
 * @param {upgradePopupPaymentTabInteractionParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} interaction - close/upgrade
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} appid - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<upgradePopupPaymentTabInteractionParams>} LogObject<upgradePopupPaymentTabInteractionParams>
 */
export function upgradePopupPaymentTabInteraction(params) {
    return { evid: 151, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'completeProviderSetupPopupShown' event (evid: 152)
 *
 * Event description: To be sent when at least one payment method doesn't connect automatically
 *
 * @param {completeProviderSetupPopupShownParams} params - The parameters for this event
 * @property {GUID} visitorId - VisitoID
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} appid - app_id
 * @property {string} origin - autoonboarding / add provider / active on check out
 * @property {GUID} msid - MSID
 * @return {LogObject<completeProviderSetupPopupShownParams>} LogObject<completeProviderSetupPopupShownParams>
 */
export function completeProviderSetupPopupShown(params) {
    return { evid: 152, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantSetUpAccountClicked' event (evid: 153)
 *
 * Event description: Merchant click on setup account to activate in smart ob - different sources
 *
 * @param {merchantSetUpAccountClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - -
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Credit Card,PayPal,Mercado Pago, Offline
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} origin - autoconnect / add provider / set up / set up to activate / change provider
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<merchantSetUpAccountClickedParams>} LogObject<merchantSetUpAccountClickedParams>
 */
export function merchantSetUpAccountClicked(params) {
    return { evid: 153, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantCompleteSetUpLearnMoreClicked' event (evid: 154)
 *
 * Event description: Merchant click on setup account to activate in smart ob - different sources
 *
 * @param {merchantCompleteSetUpLearnMoreClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Credit Card,PayPal,Mercado Pago, Offline
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {string} origin - autoconnect / add provider / set up / set up to activate / change provider
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<merchantCompleteSetUpLearnMoreClickedParams>} LogObject<merchantCompleteSetUpLearnMoreClickedParams>
 */
export function merchantCompleteSetUpLearnMoreClicked(params) {
    return { evid: 154, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentDashboardTabSelected' event (evid: 155)
 *
 * Event description: Merchant - Click Accept Terms and Conditions TOS notification at WP settings
 *
 * @param {paymentDashboardTabSelectedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} viewMode - siteLevel'/ 'accountLevel' etc
 * @property {string} platformName - null(desktop)/oneApp/partnets etc
 * @property {string} accountStatus - wix payments account status
 * @property {string} wpGeo - GEO
 * @property {string} pageType - wp_settings/ payouts / accept_payments, restricted_products
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} referralinfo - place from which user came
 * @property {string} namespace - namespace
 * @return {LogObject<paymentDashboardTabSelectedParams>} LogObject<paymentDashboardTabSelectedParams>
 */
export function paymentDashboardTabSelected(params) {
    return { evid: 155, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentDashboardFilterApplied' event (evid: 157)
 *
 * Event description: one of filters is applied (Currency / App / Status / Payment Method)
 *
 * @param {paymentDashboardFilterAppliedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} selectedValue - what was exactly selected
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<paymentDashboardFilterAppliedParams>} LogObject<paymentDashboardFilterAppliedParams>
 */
export function paymentDashboardFilterApplied(params) {
    return { evid: 157, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentDashboardSortingApplied' event (evid: 158)
 *
 * Event description: Sorting by date is applied
 *
 * @param {paymentDashboardSortingAppliedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} sortBy - currently date only
 * @property {GUID} userGuid - user_guid
 * @property {string} sortOrder - asc / desc
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<paymentDashboardSortingAppliedParams>} LogObject<paymentDashboardSortingAppliedParams>
 */
export function paymentDashboardSortingApplied(params) {
    return { evid: 158, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentDashboardViewMoreTransactionsClicked' event (evid: 159)
 *
 * Event description: when user clicks to view 10 more transactions in the end of the list
 *
 * @param {paymentDashboardViewMoreTransactionsClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} transactionsShown - current number of visible transactions per page
 * @property {GUID} userGuid - user_guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<paymentDashboardViewMoreTransactionsClickedParams>} LogObject<paymentDashboardViewMoreTransactionsClickedParams>
 */
export function paymentDashboardViewMoreTransactionsClicked(params) {
    return { evid: 159, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentDashboardTransactionSelected' event (evid: 160)
 *
 * Event description: user selects transaction to view details
 *
 * @param {paymentDashboardTransactionSelectedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} transactionStatus - current transaction status
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} action - expand / collapse
 * @property {GUID} transactionId - transaction_id
 * @return {LogObject<paymentDashboardTransactionSelectedParams>} LogObject<paymentDashboardTransactionSelectedParams>
 */
export function paymentDashboardTransactionSelected(params) {
    return { evid: 160, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentDashboardRefundRequestConfirmed' event (evid: 161)
 *
 * Event description: user confirms that he wants to refund transaction
 *
 * @param {paymentDashboardRefundRequestConfirmedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {GUID} transactionId - transaction_id
 * @return {LogObject<paymentDashboardRefundRequestConfirmedParams>} LogObject<paymentDashboardRefundRequestConfirmedParams>
 */
export function paymentDashboardRefundRequestConfirmed(params) {
    return { evid: 161, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantEnableAdvancedOptionsClicked' event (evid: 163)
 *
 * Event description: Click enable advance options in Payment Methods Page - in order to grant permissions for user account to Wix
 *
 * @param {merchantEnableAdvancedOptionsClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Credit Card
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @property {string} ctaName - button name on the paypal card
 * @return {LogObject<merchantEnableAdvancedOptionsClickedParams>} LogObject<merchantEnableAdvancedOptionsClickedParams>
 */
export function merchantEnableAdvancedOptionsClicked(params) {
    return { evid: 163, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkOutMandatoryFieldsShown' event (evid: 164)
 *
 * Event description: list of mandatory & CC fields to be fulfilled by buyer
 *
 * @param {checkOutMandatoryFieldsShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} creditCardId - credit_card_id
 * @property {string} paymentCategory - payment_category
 * @property {string} mandatoryFieldsList - list of mandatory fields to be fulfilled by buyer
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} orderId - vertical order id
 * @property {string} paymentMethod - Offline payment, MercadoPago, Paypal, Credit card
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - currency
 * @property {GUID} memberId - member_id
 * @property {string} pageType - page_type
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<checkOutMandatoryFieldsShownParams>} LogObject<checkOutMandatoryFieldsShownParams>
 */
export function checkOutMandatoryFieldsShown(params) {
    return { evid: 164, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkOutPaymentMethodValidationCompleted' event (evid: 165)
 *
 * Event description: The result of Payment Method Validation
 *
 * @param {checkOutPaymentMethodValidationCompletedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment_category
 * @property {string} appInstanceId - app_instance_id
 * @property {string} orderId - order id
 * @property {string} paymentMethod - payment method
 * @property {string} reason - list of fields with the problem in case when status = false
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} buyercomponent - buyercomponent
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} status - true if success
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - currency
 * @property {GUID} memberId - member_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<checkOutPaymentMethodValidationCompletedParams>} LogObject<checkOutPaymentMethodValidationCompletedParams>
 */
export function checkOutPaymentMethodValidationCompleted(params) {
    return { evid: 165, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantCompleteSetUpBannerShown' event (evid: 166)
 *
 * Event description: In case when deferred account was created, user need to claim it.
 * Banner notifies user
 *
 * @param {merchantCompleteSetUpBannerShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,paypal
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<merchantCompleteSetUpBannerShownParams>} LogObject<merchantCompleteSetUpBannerShownParams>
 */
export function merchantCompleteSetUpBannerShown(params) {
    return { evid: 166, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantLearnMoreFromCompleteSetUpBannerClicked' event (evid: 167)
 *
 * Event description: User clicks on Learn More in Complete Set-Up Banner (for providers with deferred account
 *
 * @param {merchantLearnMoreFromCompleteSetUpBannerClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,paypal
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantLearnMoreFromCompleteSetUpBannerClickedParams>} LogObject<merchantLearnMoreFromCompleteSetUpBannerClickedParams>
 */
export function merchantLearnMoreFromCompleteSetUpBannerClicked(params) {
    return { evid: 167, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantWixPaymentsLbShown' event (evid: 168)
 *
 * Event description: The LB is shown to Brazilian users, who doesn't have any payment settings (connected or selected payment methods), on opening Accept Payments tab (so they don't see full list of available payment methods)
 *
 * @param {merchantWixPaymentsLbShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} appid - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<merchantWixPaymentsLbShownParams>} LogObject<merchantWixPaymentsLbShownParams>
 */
export function merchantWixPaymentsLbShown(params) {
    return { evid: 168, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantWixPaymentsLbInteraction' event (evid: 169)
 *
 * Event description: when user interacts with Wix Payments LB
 *
 * @param {merchantWixPaymentsLbInteractionParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} interaction - close / see_all_methods / create_account
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} appid - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<merchantWixPaymentsLbInteractionParams>} LogObject<merchantWixPaymentsLbInteractionParams>
 */
export function merchantWixPaymentsLbInteraction(params) {
    return { evid: 169, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentDashboardCurrencyChanged' event (evid: 170)
 *
 * Event description: Currency
 *
 * @param {paymentDashboardCurrencyChangedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} changeFromCurrency - change_from_currency
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} changeToCurrency - change_to_currency
 * @return {LogObject<paymentDashboardCurrencyChangedParams>} LogObject<paymentDashboardCurrencyChangedParams>
 */
export function paymentDashboardCurrencyChanged(params) {
    return { evid: 170, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantWixPaymentsLearnMoreClicked' event (evid: 171)
 *
 * Event description: User clicks on Learn More for Wix Payments
 *
 * @param {merchantWixPaymentsLearnMoreClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} appid - app_id
 * @property {string} origin - Welcome page / Add provider
 * @property {string} paymentProvider - payment provider
 * @property {GUID} msid - msid
 * @return {LogObject<merchantWixPaymentsLearnMoreClickedParams>} LogObject<merchantWixPaymentsLearnMoreClickedParams>
 */
export function merchantWixPaymentsLearnMoreClicked(params) {
    return { evid: 171, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'popUpConfigureOtherVerticalsSettings' event (evid: 172)
 *
 * Event description: When user enters Accept Payments Tab in BM, having installed verticals with payment settings, which are not integrated with Cashier, User is shown to Pop-up with links to these verticals. The event shows also User interaction with the pop-up
 *
 * @param {popUpConfigureOtherVerticalsSettingsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} interaction - shown / closed / [vertical] go to link
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {string} verticalsLinkShown - list of verticals, not integrated with Cashier and with out payment settings
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} appid - app_id
 * @property {GUID} msid - msid
 * @property {boolean} doNotShowAgain - true if user clicks on "got it" having checked "Don't show again"
 * @return {LogObject<popUpConfigureOtherVerticalsSettingsParams>} LogObject<popUpConfigureOtherVerticalsSettingsParams>
 */
export function popUpConfigureOtherVerticalsSettings(params) {
    return { evid: 172, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantActivateProviderOnCheckOutPopUpShown' event (evid: 173)
 *
 * Event description: When user has completed APM account and tries to make it visible on a check-out by turning on toggle, Activate pop-up is shown
 *
 * @param {merchantActivateProviderOnCheckOutPopUpShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment_category
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} appid - app_id
 * @property {string} origin - active on check out
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} msid - msid
 * @return {LogObject<merchantActivateProviderOnCheckOutPopUpShownParams>} LogObject<merchantActivateProviderOnCheckOutPopUpShownParams>
 */
export function merchantActivateProviderOnCheckOutPopUpShown(params) {
    return { evid: 173, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkOutNoAvailableProvidersPopUpShown' event (evid: 174)
 *
 * Event description: When buyer comes to Payment Component on a check-out, and all of connected providers are disabled, we show notification to a buyer
 *
 * @param {checkOutNoAvailableProvidersPopUpShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Offline payment, MercadoPago, Paypal, Credit card
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} orderId - order id
 * @property {string} buyerComponent - buyer_component
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentProvider - payment_provider
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<checkOutNoAvailableProvidersPopUpShownParams>} LogObject<checkOutNoAvailableProvidersPopUpShownParams>
 */
export function checkOutNoAvailableProvidersPopUpShown(params) {
    return { evid: 174, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkOutNoAvailableProvidersRefreshClicked' event (evid: 175)
 *
 * Event description: When buyer comes to Payment Component on a check-out, and all of connected providers are disabled, we show notification to a buyer and buyer clicks Refresh
 *
 * @param {checkOutNoAvailableProvidersRefreshClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Offline payment, MercadoPago, Paypal, Credit card
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} orderId - order id
 * @property {boolean} refreshResult - true if payment component was loaded
 * @property {string} buyerComponent - buyer_component
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<checkOutNoAvailableProvidersRefreshClickedParams>} LogObject<checkOutNoAvailableProvidersRefreshClickedParams>
 */
export function checkOutNoAvailableProvidersRefreshClicked(params) {
    return { evid: 175, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantSwitchAccountClicked' event (evid: 176)
 *
 * Event description: Merchant clicks on Switch Account on Accept Payments
 *
 * @param {merchantSwitchAccountClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Credit Card
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} stepNext - redirect / confirmation popup for trx / manaege popup
 * @return {LogObject<merchantSwitchAccountClickedParams>} LogObject<merchantSwitchAccountClickedParams>
 */
export function merchantSwitchAccountClicked(params) {
    return { evid: 176, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantOaChoosePaymentMethodsClicked' event (evid: 177)
 *
 * Event description: Merchant clicks Choose Payment Methods on OA Accept Payments Welcome tab
 *
 * @param {merchantOaChoosePaymentMethodsClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - namespace
 * @return {LogObject<merchantOaChoosePaymentMethodsClickedParams>} LogObject<merchantOaChoosePaymentMethodsClickedParams>
 */
export function merchantOaChoosePaymentMethodsClicked(params) {
    return { evid: 177, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantOaNoProvidersAvailable' event (evid: 178)
 *
 * Event description: When Merchants goes from / select business location with no payment providers, allowing automatically connect, this pop up is shown (only OA)
 *
 * @param {merchantOaNoProvidersAvailableParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} businessLocation - the country that the user selected
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantOaNoProvidersAvailableParams>} LogObject<merchantOaNoProvidersAvailableParams>
 */
export function merchantOaNoProvidersAvailable(params) {
    return { evid: 178, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantSwitchAccountWithTransactionsConfirmClickedInPopup' event (evid: 179)
 *
 * Event description: When merchant clicks on Switch account, having at least one transaction processed through this account, additional popup is shown. Event means that merchant confirmed this switch.
 *
 * @param {merchantSwitchAccountWithTransactionsConfirmClickedInPopupParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - Credit Card
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {GUID} appId - cashier app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantSwitchAccountWithTransactionsConfirmClickedInPopupParams>} LogObject<merchantSwitchAccountWithTransactionsConfirmClickedInPopupParams>
 */
export function merchantSwitchAccountWithTransactionsConfirmClickedInPopup(params) {
    return { evid: 179, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutPaymentComponentShown' event (evid: 180)
 *
 * Event description: Payment Component is shown to a buyer
 *
 * @param {checkoutPaymentComponentShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentAgreementList - list of payment agreements which were received
 * @property {number} hasSavedPaymentMethods - null if visitor is not logged in or doesn't have saved payment methods
 * @property {GUID} creditCardId - null if visitor is not logged in or doesn't have saved cc
 * @property {string} paymentCategory - CreditCard/Ewallet,offline
 * @property {string} payWith - default payment category
 * @property {string} mandatoryFieldsList - list of required mandatory fields that were shown to UoU
 * @property {string} appInstanceId - app_instance_id
 * @property {GUID} orderId - vertical order id
 * @property {string} savePaymentMethodDisplayOption - if  pm is supposed to be saved by default/ by willing of uou/ saving is not supported:‘disclaimer’\ ‘checkbox’\‘show_nothing’
 * @property {string} buyerComponent - react if new
 * @property {GUID} membershipId - membership_id
 * @property {string} requiredPmMandatoryFields - list of required mandatory fields from payment provider
 * @property {string} intentCharge - intent of charge: one time/ recurring
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} hasSavedCreditCards - null if visitor is not logged in or doesn't have saved cc
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} prefilledMandatoryFields - list of required mandatory fields  that were shown to UoU already prefilled
 * @property {string} paymentProvider - com.stripe/net.authorize
 * @property {string} currency - payment currency (is sent by vertical)
 * @property {string} savedPaymentMethodId - saved_payment_method_id (the same guid for CC )
 * @property {GUID} memberId - null if visitor is not logged in
 * @property {string} allowMerchantUseDisplayOption - if pm is supposed to be saved for MIT trx by willing of uou/ required to be saved/ saving is not supported: 'SHOW_ALLOW_MERCHANT_USE_CHECKBOX’, ‘SHOW_NOTHING’
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} captchaRequiredForPM - list of payment methods which require passing captcha challenge (e.i. creditCard))
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {string} passedMandatoryFields - list of required mandatory fields  that weren't shown to UoU and were passed by vertical
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {number} hasSavedPaymentAgreements - null if visitor is not logged in or doesn't have saved payment agreements
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {boolean} delayedCapture - true, if widget will be shown shown with methods which support Auth&Capture
 * @property {boolean} setupFutureUsages - true, if card tokenization is required for that payment
 * @property {string} purchaseFlowId - key to identify ecom flow
 * @property {string} catalogAppId - A list of verticals which are in this order separated by “ ,“:(stores app id, bookings app_id, ...) .
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<checkoutPaymentComponentShownParams>} LogObject<checkoutPaymentComponentShownParams>
 */
export function checkoutPaymentComponentShown(params) {
    return { evid: 180, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantGoToPaymentsDashboardClicked' event (evid: 181)
 *
 * Event description: Merchant clicks on  Go to Payments Dashboard (relevant for wix pay only)
 *
 * @param {merchantGoToPaymentsDashboardClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} transactionStatus - null from payment methods
 * @property {string} origin - payment methods / dashboard
 * @property {string} connectionStatus - active / suspended / else
 * @property {string} paymentProvider - wix.com.wixpay
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @property {GUID} transactionId - null from payment methods
 * @return {LogObject<merchantGoToPaymentsDashboardClickedParams>} LogObject<merchantGoToPaymentsDashboardClickedParams>
 */
export function merchantGoToPaymentsDashboardClicked(params) {
    return { evid: 181, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutPaymentComponentCollapsed' event (evid: 182)
 *
 * Event description: Payments Component is collapsed after buyer clicked continue in Stores Checkout
 *
 * @param {checkoutPaymentComponentCollapsedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} creditCardId - null if visitor is not logged in or doesn't have saved cc
 * @property {string} paymentCategory - CreditCard/Ewallet,offline
 * @property {boolean} saveCreditCard - true if buyer wants to save CC
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} orderId - vertical order id
 * @property {string} cardId - card_id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} paymentProvider - com.stripe/net.authorize
 * @property {string} currency - currency
 * @property {GUID} memberId - null if visitor is not logged in
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {GUID} appId - app_id
 * @property {boolean} captchaTokenSent - true if we send captcha token to BE
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<checkoutPaymentComponentCollapsedParams>} LogObject<checkoutPaymentComponentCollapsedParams>
 */
export function checkoutPaymentComponentCollapsed(params) {
    return { evid: 182, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantConfirmDisconnectPopup' event (evid: 183)
 *
 * Event description: when users wants do disconnect WixPay (directly or because of connecting other provider), we show notification popup
 *
 * @param {merchantConfirmDisconnectPopupParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - currently connected category
 * @property {string} interaction - popup shown / disconnect confirmed
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - uuid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} appid - app_id
 * @property {string} origin - disconnect / connect other
 * @property {string} paymentProvider - currently connected provider
 * @property {GUID} msid - msid
 * @return {LogObject<merchantConfirmDisconnectPopupParams>} LogObject<merchantConfirmDisconnectPopupParams>
 */
export function merchantConfirmDisconnectPopup(params) {
    return { evid: 183, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantContinueToChooseMethodsForConnect' event (evid: 184)
 *
 * Event description: Merchant clicks Continue on Welcome screen. If he chooses at least 1 online pm with autoconnect, he will be leaded to next screen
 *
 * @param {merchantContinueToChooseMethodsForConnectParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} selectedValue - list of selected payment methods to continue
 * @property {string} availableValue - list of available payment methods
 * @property {GUID} appId - cashier app_id
 * @property {string} defaultValue - list of selected payment methods checked by default
 * @property {GUID} msid - msid
 * @return {LogObject<merchantContinueToChooseMethodsForConnectParams>} LogObject<merchantContinueToChooseMethodsForConnectParams>
 */
export function merchantContinueToChooseMethodsForConnect(params) {
    return { evid: 184, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantProvidersToConnectDefined' event (evid: 185)
 *
 * Event description: Additional event, that describes connection type for providers, defined as default for each of selected categories
 *
 * @param {merchantProvidersToConnectDefinedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} paymentProvider - list of providers, defined as default - for each category, according to categories order, separated by comma
 * @property {string} selectedValue - list of categories, selected by a user to connect, separated by comma
 * @property {GUID} appId - cashier app_id
 * @property {GUID} msid - msid
 * @property {string} connectionType - auto/manual - connection type for each of defined providers, according to categories order, separated by comma
 * @return {LogObject<merchantProvidersToConnectDefinedParams>} LogObject<merchantProvidersToConnectDefinedParams>
 */
export function merchantProvidersToConnectDefined(params) {
    return { evid: 185, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantShowMoreLessProvidersClickedDuringOnboarding' event (evid: 186)
 *
 * Event description: In a new onboarding, we show only major methods on a welcome page. this event tracks when users clicks show more / less methods
 *
 * @param {merchantShowMoreLessProvidersClickedDuringOnboardingParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} hiddenMethods - list of hidden payment methods, separated by comma
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - user_guid
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {GUID} appId - cashier app_id
 * @property {GUID} msid - msid
 * @property {string} action - more / less
 * @property {string} chosenMethods - list of available payment methods, shown by a default. Separated by comma
 * @return {LogObject<merchantShowMoreLessProvidersClickedDuringOnboardingParams>} LogObject<merchantShowMoreLessProvidersClickedDuringOnboardingParams>
 */
export function merchantShowMoreLessProvidersClickedDuringOnboarding(params) {
    return { evid: 186, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickDoneOnCompleteSummaryWithoutPremium' event (evid: 187)
 *
 * Event description: The User who already has a premium bought, should see the pop-up with an additional button "Done" and click it
 *
 * @param {clickDoneOnCompleteSummaryWithoutPremiumParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} appid - appid
 * @property {GUID} msid - msid
 * @return {LogObject<clickDoneOnCompleteSummaryWithoutPremiumParams>} LogObject<clickDoneOnCompleteSummaryWithoutPremiumParams>
 */
export function clickDoneOnCompleteSummaryWithoutPremium(params) {
    return { evid: 187, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantPosLearnMoreClicked' event (evid: 188)
 *
 * Event description: User clicks on Learn More for POS (must be linked to KB)
 *
 * @param {merchantPosLearnMoreClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} userGuid - uuid
 * @property {GUID} appid - app_id
 * @property {string} origin - Welcome page / Add provider
 * @property {GUID} msid - msid
 * @return {LogObject<merchantPosLearnMoreClickedParams>} LogObject<merchantPosLearnMoreClickedParams>
 */
export function merchantPosLearnMoreClicked(params) {
    return { evid: 188, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'businessLocationConfirmLoaded' event (evid: 190)
 *
 * Event description: Pop-up with @Confirm location@ CTA loaded
 *
 * @param {businessLocationConfirmLoadedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {GUID} userGuid - UUID
 * @property {boolean} status - status
 * @property {GUID} msid - MSID
 * @property {string} device - device_type
 * @return {LogObject<businessLocationConfirmLoadedParams>} LogObject<businessLocationConfirmLoadedParams>
 */
export function businessLocationConfirmLoaded(params) {
    return { evid: 190, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'fastCheckoutButtonClicked' event (evid: 192)
 *
 * Event description: when users click "Checkout with ..." on stores cart page. Any provider
 *
 * @param {fastCheckoutButtonClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} productid - product id where button was clicked
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<fastCheckoutButtonClickedParams>} LogObject<fastCheckoutButtonClickedParams>
 */
export function fastCheckoutButtonClicked(params) {
    return { evid: 192, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transaction3DsForm' event (evid: 193)
 *
 * Event description: when users click "Submit Order" and 3DS form was showed (in case flow_type = 'full redirect' we have only is_Opened = true)
 *
 * @param {transaction3DsFormParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {boolean} isOpened - true if page was open , false if closed
 * @property {string} flowType - type of 3ds flow: modal, new tab, full page redirect
 * @property {boolean} isFinished - true if page was success , false if not
 * @property {GUID} orderId - order_id
 * @property {string} paymentMethod - payment method (in case of CreditCard - 3ds challenge)
 * @property {string} result - result of undergoing 3ds flow
 * @property {string} errorReason - error description for result 'failed'
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} amount - checkout amount
 * @property {number} version3ds - 3DS version (1 or 2)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} paymentProvider - com.stripe/square/wp
 * @property {string} currency - currency
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {boolean} isUserChallenged - mark if uou was challenged
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {boolean} isManualClose - buyer closed modal
 * @property {GUID} transactionId - transaction_id if it exists
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @return {LogObject<transaction3DsFormParams>} LogObject<transaction3DsFormParams>
 */
export function transaction3DsForm(params) {
    return { evid: 193, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantPaymentDashboardRemovedPopUp' event (evid: 195)
 *
 * Event description: When users, who saw payment dashboard previously, goes to Accept Payment Tab we notify him that place changes
 *
 * @param {merchantPaymentDashboardRemovedPopUpParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} uuid - user_guid
 * @property {GUID} msid - msid
 * @return {LogObject<merchantPaymentDashboardRemovedPopUpParams>} LogObject<merchantPaymentDashboardRemovedPopUpParams>
 */
export function merchantPaymentDashboardRemovedPopUp(params) {
    return { evid: 195, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentDashboardRemovedToast' event (evid: 196)
 *
 * Event description: When users, who saw payment dashboard previously, goes to Accept Payment Tab we notify him(through toast) that place changes
 *
 * @param {paymentDashboardRemovedToastParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} uuid - user_guid
 * @property {GUID} msid - site_id
 * @return {LogObject<paymentDashboardRemovedToastParams>} LogObject<paymentDashboardRemovedToastParams>
 */
export function paymentDashboardRemovedToast(params) {
    return { evid: 196, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutValidationErrors' event (evid: 197)
 *
 * Event description: Send BI event if UI validation of checkout mandatory fields fails
 *
 * @param {checkoutValidationErrorsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} errorMessage - Error message/localization key that was shown
 * @property {string} paymentMethod - paymentMethod
 * @property {string} fieldName - Field type/name
 * @property {GUID} sessionId - session_id
 * @property {string} validValue - Validation rule/desired value
 * @property {GUID} appId - vertical ID
 * @property {GUID} msid - msid
 * @return {LogObject<checkoutValidationErrorsParams>} LogObject<checkoutValidationErrorsParams>
 */
export function checkoutValidationErrors(params) {
    return { evid: 197, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbShippingMethodChanged' event (evid: 198)
 *
 * Event description: in case we want to know when user change shipping address or payment method on provider side
 *
 * @param {spbShippingMethodChangedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {GUID} appInstanceId - vertical instance id
 * @property {number} amountLong - new amount ( * 100 000)
 * @property {string} paymentMethod - new method, when method was changed
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} fieldChanged - field which was changed on payment method page
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - new value
 * @property {GUID} msid - site id
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<spbShippingMethodChangedParams>} LogObject<spbShippingMethodChangedParams>
 */
export function spbShippingMethodChanged(params) {
    return { evid: 198, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantSelectUnselectAdditionalPaymentMethodAtManageAccount' event (evid: 199)
 *
 * Event description: Merchant select/unselect additional provider settings at Manage account tab
 *
 * @param {merchantSelectUnselectAdditionalPaymentMethodAtManageAccountParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - creditCard,paypal
 * @property {boolean} selected - Select/unselect options at Manage account tab. True=selected, false=unselected
 * @property {string} paymentProvider - payment provider
 * @property {string} selectedValue - e.g. PayPal Credit
 * @property {string} app_id - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<merchantSelectUnselectAdditionalPaymentMethodAtManageAccountParams>} LogObject<merchantSelectUnselectAdditionalPaymentMethodAtManageAccountParams>
 */
export function merchantSelectUnselectAdditionalPaymentMethodAtManageAccount(params) {
    return { evid: 199, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantFullStorySessionSrc64Evid2' event (evid: 2)
 *
 * Event description: Merchant FullStory session recorded
 *
 * @param {merchantFullStorySessionSrc64Evid2Params} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came to AP
 * @property {boolean} isPartner - is_partner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {string} page - Payments Overview / smart onBoarding welcome / smart onBoarding connect
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} origin - place user came from
 * @property {string} fullstoryUrl - url of FullStory recorded session
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @property {string} experiment - spec name
 * @return {LogObject<merchantFullStorySessionSrc64Evid2Params>} LogObject<merchantFullStorySessionSrc64Evid2Params>
 */
export function merchantFullStorySessionSrc64Evid2(params) {
    return { evid: 2, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutPageStartedLoading' event (evid: 200)
 *
 * Event description: Wix Code Check out - Page started loading
 *
 * @param {wixCodeCheckOutPageStartedLoadingParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - loading time in miliseconds from buyer activity to page started loading
 * @property {boolean} isPartner - is_partner
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} page - name of the page
 * @property {string} direction - NOT IN USE: button name
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {string} pageFrom - page buyer is redirected from
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {string} errorType - NOT IN USE: generic, input
 * @property {string} selectedCountry - country chosen by buyer
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} status - transaction status for thank you page
 * @property {string} pageTo - NOT IN USE: page buyer will be navigated to
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<wixCodeCheckOutPageStartedLoadingParams>} LogObject<wixCodeCheckOutPageStartedLoadingParams>
 */
export function wixCodeCheckOutPageStartedLoading(params) {
    return { evid: 200, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'submitAttestationForm' event (evid: 2000)
 *
 * Event description: User submitted attestation form - CBD flow
 *
 * @param {submitAttestationFormParams} params - The parameters for this event
 * @property {string} pageType - page where user did a click
 * @property {string} flowType - e.g. CBD flow
 * @return {LogObject<submitAttestationFormParams>} LogObject<submitAttestationFormParams>
 */
export function submitAttestationForm(params) {
    return { evid: 2000, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'makingADecisionInFlow' event (evid: 2001)
 *
 * Event description: User made a decision in flow [yes/no].
 *
 * @param {makingADecisionInFlowParams} params - The parameters for this event
 * @property {string} flowType - e.g. CBD flow
 * @property {string} ctaValue - yes/no/etc
 * @property {string} pageType - page where user did a click
 * @return {LogObject<makingADecisionInFlowParams>} LogObject<makingADecisionInFlowParams>
 */
export function makingADecisionInFlow(params) {
    return { evid: 2001, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantIntentCreatedForPayPalBillingAgreement' event (evid: 2005)
 *
 * Event description: Merchant requested PayPal Billing Agreement feature
 *
 * @param {merchantIntentCreatedForPayPalBillingAgreementParams} params - The parameters for this event
 * @property {string} paypalAccountId - account_id on PayPal side
 * @property {GUID} visitorId - visitor id
 * @property {string} paypalMerchantId - merchant_id on PayPal side
 * @property {string} fileName - name of file batch in which merchant sent to Paypal
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantIntentCreatedForPayPalBillingAgreementParams>} LogObject<merchantIntentCreatedForPayPalBillingAgreementParams>
 */
export function merchantIntentCreatedForPayPalBillingAgreement(params) {
    return { evid: 2005, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantIntentSentForPayPalBillingAgreement' event (evid: 2006)
 *
 * Event description: Merchant requested about Billing Agreement feature was sent to PayPal
 *
 * @param {merchantIntentSentForPayPalBillingAgreementParams} params - The parameters for this event
 * @property {string} paypalAccountId - account_id on PayPal side
 * @property {GUID} visitorId - visitor id
 * @property {DateTime} dateSent - timestamp when intent was sent to PayPal
 * @property {string} paypalMerchantId - merchant_id on PayPal side
 * @property {string} fileName - name of file batch in which merchant sent to Paypal
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantIntentSentForPayPalBillingAgreementParams>} LogObject<merchantIntentSentForPayPalBillingAgreementParams>
 */
export function merchantIntentSentForPayPalBillingAgreement(params) {
    return { evid: 2006, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantStatusUpdatesForPayPalBillingAgreement' event (evid: 2007)
 *
 * Event description: Merchant Billing Agreement status update from PayPal
 *
 * @param {merchantStatusUpdatesForPayPalBillingAgreementParams} params - The parameters for this event
 * @property {string} paypalAccountId - account_id on PayPal side
 * @property {GUID} visitorId - visitor ud
 * @property {string} paypalMerchantId - merchant_id on PayPal side
 * @property {string} fileName - name of file batch in which merchant sent to Paypal
 * @property {boolean} isFinalStatus - true if this is final status update for account
 * @property {boolean} isEnabled - true if PayPal Billing Agreement is enabled
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantStatusUpdatesForPayPalBillingAgreementParams>} LogObject<merchantStatusUpdatesForPayPalBillingAgreementParams>
 */
export function merchantStatusUpdatesForPayPalBillingAgreement(params) {
    return { evid: 2007, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantProviderConnectInitialization' event (evid: 2008)
 *
 * Event description: Start of connection for the provider on user click
 *
 * @param {merchantProviderConnectInitializationParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {string} uuid - wix users id
 * @property {string} paymentMethod - creditCard, applePay etc
 * @property {string} businessLocation - location that users selected on site
 * @property {string} providerId - provider id
 * @property {string} origin - origin
 * @property {string} correlationId - request id if accessible
 * @property {GUID} msid - msid
 * @return {LogObject<merchantProviderConnectInitializationParams>} LogObject<merchantProviderConnectInitializationParams>
 */
export function merchantProviderConnectInitialization(params) {
    return { evid: 2008, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantProviderConnectComplete' event (evid: 2009)
 *
 * Event description: End Status of the provider connection process
 *
 * @param {merchantProviderConnectCompleteParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {string} uuid - wix users id
 * @property {string} paymentMethod - creditCard, applePay etc
 * @property {string} businessLocation - location that users selected on site
 * @property {string} accountId - wix payments account_id
 * @property {string} providerId - provider id
 * @property {string} origin - origin
 * @property {string} status - SUCCESS / FAILURE
 * @property {string} errorDescription - optional on status FAILED
 * @property {string} correlationId - request id if accessible
 * @property {GUID} msid - msid
 * @property {string} paypalAccountId - paypal account id to differentiate between flow
 * @return {LogObject<merchantProviderConnectCompleteParams>} LogObject<merchantProviderConnectCompleteParams>
 */
export function merchantProviderConnectComplete(params) {
    return { evid: 2009, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutPageLoaded' event (evid: 201)
 *
 * Event description: Wix Code Check out - Page loaded
 *
 * @param {wixCodeCheckOutPageLoadedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - loading time in miliseconds
 * @property {boolean} isPartner - is_partner
 * @property {number} fullDuration - full duration from buyer intent to finish loaded
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} page - name of the page
 * @property {string} direction - NOT IN USE: button name
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {string} pageFrom - page buyer is redirected from
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {string} errorType - NOT IN USE: generic, input
 * @property {string} selectedCountry - country chosen by buyer
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} status - transaction status for thank you page
 * @property {string} pageTo - NOT IN USE: page buyer will be navigated to
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<wixCodeCheckOutPageLoadedParams>} LogObject<wixCodeCheckOutPageLoadedParams>
 */
export function wixCodeCheckOutPageLoaded(params) {
    return { evid: 201, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantGrantedPaypalAccountPermissionsToWixSrc64Evid2010' event (evid: 2010)
 *
 * Event description: request to PayPal to grant merchant permission to Wix
 *
 * @param {merchantGrantedPaypalAccountPermissionsToWixSrc64Evid2010Params} params - The parameters for this event
 * @property {string} msid - site id
 * @property {string} paypalAccountId - id on provider side
 * @property {string} paypalMerchantId - merchant_id on PayPal
 * @property {string} businessLocation - country selected at payments page
 * @property {string} uuid - wix user guid
 * @return {LogObject<merchantGrantedPaypalAccountPermissionsToWixSrc64Evid2010Params>} LogObject<merchantGrantedPaypalAccountPermissionsToWixSrc64Evid2010Params>
 */
export function merchantGrantedPaypalAccountPermissionsToWixSrc64Evid2010(params) {
    return { evid: 2010, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantPaypalAccountPermissionsGrantedSrc64Evid2011' event (evid: 2011)
 *
 * Event description: Callback from PayPal that merchant permission is granted to Wix
 *
 * @param {merchantPaypalAccountPermissionsGrantedSrc64Evid2011Params} params - The parameters for this event
 * @property {string} msid - site id
 * @property {string} paypalAccountId - id on provider side
 * @property {string} businessLocation - country selected at payments page
 * @property {string} paypalMerchantId - merchant_id on PayPal
 * @return {LogObject<merchantPaypalAccountPermissionsGrantedSrc64Evid2011Params>} LogObject<merchantPaypalAccountPermissionsGrantedSrc64Evid2011Params>
 */
export function merchantPaypalAccountPermissionsGrantedSrc64Evid2011(params) {
    return { evid: 2011, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantPaypalAccountEmailVerificationUpdatedSrc64Evid2012' event (evid: 2012)
 *
 * Event description: Triggered when we receive a change from PayPal regarding email verification
 *
 * @param {merchantPaypalAccountEmailVerificationUpdatedSrc64Evid2012Params} params - The parameters for this event
 * @property {string} msid - site id
 * @property {string} paypalAccountId - id on provider side
 * @property {string} verificationStatus - status of verification: Successful/Failed
 * @property {string} paypalMerchantId - merchant_id on PayPal
 * @property {string} businessLocation - country selected at payments page
 * @property {string} uuid - wix user guid
 * @return {LogObject<merchantPaypalAccountEmailVerificationUpdatedSrc64Evid2012Params>} LogObject<merchantPaypalAccountEmailVerificationUpdatedSrc64Evid2012Params>
 */
export function merchantPaypalAccountEmailVerificationUpdatedSrc64Evid2012(params) {
    return { evid: 2012, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutBuyerMovesInAFunnel' event (evid: 202)
 *
 * Event description: Wix Code Check out - Buyer moves in a funnel
 *
 * @param {wixCodeCheckOutBuyerMovesInAFunnelParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - NOT IN USE: loading time in miliseconds
 * @property {boolean} isPartner - is_partner
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} page - name of the page
 * @property {string} direction - button name (incl. close)
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {string} pageFrom - NOT IN USE: page buyer is redirected from
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {string} errorType - NOT IN USE: generic, input
 * @property {string} selectedCountry - country chosen by buyer
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} status - NOT IN USE: transaction status for thank you page
 * @property {string} pageTo - page buyer will be navigated to
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<wixCodeCheckOutBuyerMovesInAFunnelParams>} LogObject<wixCodeCheckOutBuyerMovesInAFunnelParams>
 */
export function wixCodeCheckOutBuyerMovesInAFunnel(params) {
    return { evid: 202, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutBuyerChoosesCountry' event (evid: 203)
 *
 * Event description: Wix Code Check out - Buyer chooses country
 *
 * @param {wixCodeCheckOutBuyerChoosesCountryParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - NOT IN USE: loading time in miliseconds
 * @property {boolean} isPartner - is_partner
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} page - name of the page
 * @property {string} direction - NOT IN USE: button name (incl. close)
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {string} pageFrom - NOT IN USE: page buyer is redirected from
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {string} errorType - NOT IN USE: generic, input
 * @property {string} selectedCountry - country chosen by buyer
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} status - NOT IN USE: transaction status for thank you page
 * @property {string} pageTo - NOT IN USE: page buyer will be navigated to
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<wixCodeCheckOutBuyerChoosesCountryParams>} LogObject<wixCodeCheckOutBuyerChoosesCountryParams>
 */
export function wixCodeCheckOutBuyerChoosesCountry(params) {
    return { evid: 203, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutBuyerViewsOrderDetails' event (evid: 204)
 *
 * Event description: Wix Code Check out - Buyer views Order details
 *
 * @param {wixCodeCheckOutBuyerViewsOrderDetailsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - NOT IN USE: loading time in miliseconds
 * @property {boolean} isPartner - is_partner
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} page - name of the page
 * @property {string} direction - NOT IN USE: button name (incl. close)
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {string} pageFrom - NOT IN USE: page buyer is redirected from
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {string} errorType - NOT IN USE: generic, input
 * @property {string} selectedCountry - country chosen by buyer
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} status - NOT IN USE: transaction status for thank you page
 * @property {string} pageTo - NOT IN USE: page buyer will be navigated to
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<wixCodeCheckOutBuyerViewsOrderDetailsParams>} LogObject<wixCodeCheckOutBuyerViewsOrderDetailsParams>
 */
export function wixCodeCheckOutBuyerViewsOrderDetails(params) {
    return { evid: 204, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodeCheckOutErrorOccurred' event (evid: 205)
 *
 * Event description: Wix Code Check out - Error occurred
 *
 * @param {wixCodeCheckOutErrorOccurredParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - NOT IN USE: loading time in miliseconds
 * @property {boolean} isPartner - is_partner
 * @property {GUID} cashierInstanceId - cashier_instance_id
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} page - name of the page
 * @property {string} direction - NOT IN USE: button name (incl. close)
 * @property {string} errorDesc - error details
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} paymentMethodsAvailable - list of available payment methods
 * @property {string} pageFrom - NOT IN USE: page buyer is redirected from
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {string} errorType - generic, input
 * @property {string} selectedCountry - country chosen by buyer
 * @property {string} paymentMethodSelected - payment method selected if known
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} status - NOT IN USE: transaction status for thank you page
 * @property {string} pageTo - NOT IN USE: page buyer will be navigated to
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<wixCodeCheckOutErrorOccurredParams>} LogObject<wixCodeCheckOutErrorOccurredParams>
 */
export function wixCodeCheckOutErrorOccurred(params) {
    return { evid: 205, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodePaymentCreationStarted' event (evid: 206)
 *
 * Event description: Payment (Order) creation started after buyer initiates it (button click etc)
 *
 * @param {wixCodePaymentCreationStartedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - NOT IN USE: loading time in miliseconds
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {string} orderSnapshotId - NOT IN USE: order_snapshot_id
 * @property {string} merchantDefinedFields - INIT ONLY
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} status - NOT IN USE: transaction status for thank you page
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - msid
 * @return {LogObject<wixCodePaymentCreationStartedParams>} LogObject<wixCodePaymentCreationStartedParams>
 */
export function wixCodePaymentCreationStarted(params) {
    return { evid: 206, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodePaymentCreationCompleted' event (evid: 207)
 *
 * Event description: Payment (Order) creation completed
 *
 * @param {wixCodePaymentCreationCompletedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - loading time in miliseconds
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} errorDesc - in case of error
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} merchantDefinedFields - INIT ONLY
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} status - creation status: true / false
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - msid
 * @return {LogObject<wixCodePaymentCreationCompletedParams>} LogObject<wixCodePaymentCreationCompletedParams>
 */
export function wixCodePaymentCreationCompleted(params) {
    return { evid: 207, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodePaymentInitializationStarted' event (evid: 208)
 *
 * Event description: Payment Initialization started
 *
 * @param {wixCodePaymentInitializationStartedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - NOT IN USE: loading time in miliseconds from buyer activity to page started loading
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} errorDesc - NOT IN USE: error details
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} status - NOT IN USE: creation status: true / false
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<wixCodePaymentInitializationStartedParams>} LogObject<wixCodePaymentInitializationStartedParams>
 */
export function wixCodePaymentInitializationStarted(params) {
    return { evid: 208, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'wixCodePaymentInitializationCompleted' event (evid: 209)
 *
 * Event description: Payment Initialization completed
 *
 * @param {wixCodePaymentInitializationCompletedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - loading time in miliseconds from buyer activity to page started loading
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id (cashier)
 * @property {string} errorDesc - error details
 * @property {boolean} showThankYouPage - merchant enables thank you page
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {boolean} thunderbolt - if loading was from thunderbolt then TRUE
 * @property {string} merchantDefinedFields - buyer details, specified by merchant
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} status - creation status: true / false
 * @property {boolean} termsAndConditions - true if shown, false is not shown
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<wixCodePaymentInitializationCompletedParams>} LogObject<wixCodePaymentInitializationCompletedParams>
 */
export function wixCodePaymentInitializationCompleted(params) {
    return { evid: 209, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbButtonRendered' event (evid: 210)
 *
 * Event description: Smart Payment Button was rendered
 *
 * @param {spbButtonRenderedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} renderPlace - place on which button was rendered
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - if Stores ask about shipping address
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {number} loadTime - time from initial to render
 * @property {string} productId - product id where button was rendered
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<spbButtonRenderedParams>} LogObject<spbButtonRenderedParams>
 */
export function spbButtonRendered(params) {
    return { evid: 210, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundsServiceUsageSrc64Evid2101' event (evid: 2101)
 *
 * Event description: Event for refunds service usage: Create refund; Get refundability; Query refunds method; Get refund; Sync refund method.
 *
 * @param {refundsServiceUsageSrc64Evid2101Params} params - The parameters for this event
 * @property {string} chargeId - ID of charge for which the funds are returned by this refund.
 * @property {string} refundId - Refund ID
 * @property {string} step - validation result is 2 different steps
 * @property {string} reason - validation error reason
 * @property {string} method - create/sync/get/query/getRefundabiltiy
 * @property {number} refundAmount - amount of refund * 100 000
 * @property {string} refundCurrency - Currency of refund, should be the same as currency of charge.
 * @property {number} previouslyRefundedAmount - Previously refunded amount * 100 000
 * @property {boolean} returnProcessingFee - return or nor processing fee for refund
 * @property {string} initiator - UNKNOWN_INITIATOR / WIX / API /PROVIDER
 * @property {GUID} appDefId - Id of application that calls Refund service
 * @property {string} status - a status of created refund
 * @property {GUID} meta_site_id - msid
 * @property {string} providerId - for example, "com.stripe"
 * @property {string} errorCode - error code like "5006", "6000"
 * @property {string} providerErrorCode - provider error code like "insufficient_funds"
 * @property {string} errorDescription - text description from provider, validation error code, exception message etc
 * @return {LogObject<refundsServiceUsageSrc64Evid2101Params>} LogObject<refundsServiceUsageSrc64Evid2101Params>
 */
export function refundsServiceUsageSrc64Evid2101(params) {
    return { evid: 2101, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbErrorDuringStartInitializationCheckout' event (evid: 211)
 *
 * Event description: Smart Payment Button error during start Initialization Checkout (creating order on PayPal Failed or user is not premium). Send the reason in the event
 *
 * @param {spbErrorDuringStartInitializationCheckoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} errorReason - Error reason
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} productId - product id
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} errorDesc - additional info about occurred error
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @return {LogObject<spbErrorDuringStartInitializationCheckoutParams>} LogObject<spbErrorDuringStartInitializationCheckoutParams>
 */
export function spbErrorDuringStartInitializationCheckout(params) {
    return { evid: 211, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentModalClosed' event (evid: 212)
 *
 * Event description: User click to close payment external modal window/tab
 *
 * @param {paymentModalClosedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} checkoutType - express / regular
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} pageType - page where button is appear
 * @return {LogObject<paymentModalClosedParams>} LogObject<paymentModalClosedParams>
 */
export function paymentModalClosed(params) {
    return { evid: 212, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbButtonStartInitializationCheckout' event (evid: 213)
 *
 * Event description: Smart Payment Button start Initialization Checkout (start creating order on PayPal)
 *
 * @param {spbButtonStartInitializationCheckoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} appId - app id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<spbButtonStartInitializationCheckoutParams>} LogObject<spbButtonStartInitializationCheckoutParams>
 */
export function spbButtonStartInitializationCheckout(params) {
    return { evid: 213, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbButtonSuccessInitializationCheckout' event (evid: 214)
 *
 * Event description: Smart Payment Button success Initialization Checkout (order created on PayPal - user is able to login/select payment method, GooglePay - valid for physical goods, for digital ones is sent by default )
 *
 * @param {spbButtonSuccessInitializationCheckoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} ecToken - PayPal token id
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<spbButtonSuccessInitializationCheckoutParams>} LogObject<spbButtonSuccessInitializationCheckoutParams>
 */
export function spbButtonSuccessInitializationCheckout(params) {
    return { evid: 214, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantAcceptPaymentsClickedAtBreadcrumbTrailSrc64Evid215' event (evid: 215)
 *
 * Event description: Merchant-click  Accept Payments at breadcrumb trail. Please see 85:149 for WP events
 *
 * @param {merchantAcceptPaymentsClickedAtBreadcrumbTrailSrc64Evid215Params} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} accountprofileid - account profile id
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} pageType - what page in the dashboard / origin page
 * @property {GUID} msid - msid
 * @property {string} targetPage - page where you're going to be redirected
 * @property {string} namespace - namespace
 * @return {LogObject<merchantAcceptPaymentsClickedAtBreadcrumbTrailSrc64Evid215Params>} LogObject<merchantAcceptPaymentsClickedAtBreadcrumbTrailSrc64Evid215Params>
 */
export function merchantAcceptPaymentsClickedAtBreadcrumbTrailSrc64Evid215(params) {
    return { evid: 215, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbButtonTrxAuthorized' event (evid: 216)
 *
 * Event description: Transaction successfully authorized and PP modal is closed.
 *
 * @param {spbButtonTrxAuthorizedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<spbButtonTrxAuthorizedParams>} LogObject<spbButtonTrxAuthorizedParams>
 */
export function spbButtonTrxAuthorized(params) {
    return { evid: 216, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbShippingMethodNotAccepted' event (evid: 217)
 *
 * Event description: if Shipping address is not OK (errors: expected -  SHIPPING_CONTACT_INVALID, SHIPPING_ADDRESS_UNSERVICEABLE, SHIPPING_UNKNOWN)
 *
 * @param {spbShippingMethodNotAcceptedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {GUID} appInstanceId - vertical instance id
 * @property {number} amountLong - new amount ( * 100 000)
 * @property {string} paymentMethod - new method, when method was changed
 * @property {string} errorReason - in case of 'UNEXPECTED', it is response from vertical
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} fieldChanged - field which was changed on payment method page
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} currency - new value
 * @property {string} appId - app_id
 * @property {GUID} msid - site id
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} paymentProvider - paypal/ wixpay/ other platform providers
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<spbShippingMethodNotAcceptedParams>} LogObject<spbShippingMethodNotAcceptedParams>
 */
export function spbShippingMethodNotAccepted(params) {
    return { evid: 217, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ctaClick' event (evid: 218)
 *
 * Event description: general event for CTA click
 *
 * @param {ctaClickParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} flowType - e.g. cbd flow
 * @property {string} page - e.g. Attestation form / Accepting Payments / etc.
 * @property {string} popupName - name of popup where click was made
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} cta_value - yes/no/save
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<ctaClickParams>} LogObject<ctaClickParams>
 */
export function ctaClick(params) {
    return { evid: 218, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'openPopUp' event (evid: 219)
 *
 * Event description: popup was opened on the page
 *
 * @param {openPopUpParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} flowType - e.g. cbd flow
 * @property {string} page - e.g. Attestation form / Accepting Payments / etc.
 * @property {string} popupName - popup name
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<openPopUpParams>} LogObject<openPopUpParams>
 */
export function openPopUp(params) {
    return { evid: 219, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletPageStartedLoading' event (evid: 220)
 *
 * Event description: Logged in member enters My Wallet page
 *
 * @param {myWalletPageStartedLoadingParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} member_id - member_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - user_guid
 * @property {number} hasSavedCreditCards - number of saved credit cards
 * @property {string} origin - profile / side menu
 * @property {GUID} memberId - member_id
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @return {LogObject<myWalletPageStartedLoadingParams>} LogObject<myWalletPageStartedLoadingParams>
 */
export function myWalletPageStartedLoading(params) {
    return { evid: 220, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'oneAppPaymentPageWasShownToUser' event (evid: 2200)
 *
 * Event description: User open payment overview page in OneApp
 *
 * @param {oneAppPaymentPageWasShownToUserParams} params - The parameters for this event
 * @property {boolean} isError - T/F
 * @property {string} amount - amount
 * @property {string} selectedValue - today,yesterday,all_time, null
 * @property {string} currency - currency
 * @property {boolean} isEmpty - T/F
 * @return {LogObject<oneAppPaymentPageWasShownToUserParams>} LogObject<oneAppPaymentPageWasShownToUserParams>
 */
export function oneAppPaymentPageWasShownToUser(params) {
    return { evid: 2200, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'oneAppUserClicksOnCta' event (evid: 2201)
 *
 * Event description: User clicks on CTA in OneApp on payments overview/dashboard
 *
 * @param {oneAppUserClicksOnCtaParams} params - The parameters for this event
 * @property {string} pageType - payments_overview, payments_table, transaction_details
 * @property {string} cta - today,yesterday,all_time,refresh, view_payments, see_all, view_order, view_contact
 * @return {LogObject<oneAppUserClicksOnCtaParams>} LogObject<oneAppUserClicksOnCtaParams>
 */
export function oneAppUserClicksOnCta(params) {
    return { evid: 2201, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'oneAppPaymentsTableLoaded' event (evid: 2202)
 *
 * Event description: OneApp - Payments Table load start/end
 *
 * @param {oneAppPaymentsTableLoadedParams} params - The parameters for this event
 * @property {number} bucketNumber - page number that user loaded
 * @property {DateTime} dateTo - date to
 * @property {boolean} isError - T/F
 * @property {string} paymentCategory - payment method - coma separated
 * @property {GUID} actionId - same id for start/end action
 * @property {string} transactionStatus - successful, refunded, declined - coma separated
 * @property {DateTime} dateFrom - date from
 * @property {string} type - start/end
 * @property {boolean} isEmpty - T/F
 * @return {LogObject<oneAppPaymentsTableLoadedParams>} LogObject<oneAppPaymentsTableLoadedParams>
 */
export function oneAppPaymentsTableLoaded(params) {
    return { evid: 2202, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'oneAppUserClicksOnFiltersAtPaymentsTable' event (evid: 2203)
 *
 * Event description: User clicks on filters in OneApp on payments table
 *
 * @param {oneAppUserClicksOnFiltersAtPaymentsTableParams} params - The parameters for this event
 * @property {string} value - value that user selected
 * @property {string} cta - date_from, date_to, method, status,clear_filter, close,apply
 * @return {LogObject<oneAppUserClicksOnFiltersAtPaymentsTableParams>} LogObject<oneAppUserClicksOnFiltersAtPaymentsTableParams>
 */
export function oneAppUserClicksOnFiltersAtPaymentsTable(params) {
    return { evid: 2203, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'oneAppUserClicksOnTransaction' event (evid: 2204)
 *
 * Event description: User clicks on filters in OneApp on payments table
 *
 * @param {oneAppUserClicksOnTransactionParams} params - The parameters for this event
 * @property {string} paymentCategory - payment method
 * @property {string} paymentType - payment type
 * @property {string} transactionStatus - transaction_status
 * @property {string} paymentProvider - payment provider
 * @property {GUID} transactionId - guid
 * @return {LogObject<oneAppUserClicksOnTransactionParams>} LogObject<oneAppUserClicksOnTransactionParams>
 */
export function oneAppUserClicksOnTransaction(params) {
    return { evid: 2204, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'oneAppTransactionDetailsLoaded' event (evid: 2205)
 *
 * Event description: OneApp - Payments Table load start/end
 *
 * @param {oneAppTransactionDetailsLoadedParams} params - The parameters for this event
 * @property {boolean} isError - T/F
 * @property {string} paymentCategory - payment method
 * @property {string} transactionStatus - status
 * @property {string} paymentProvider - provider
 * @property {string} type - start/end
 * @property {boolean} isEmpty - T/F
 * @return {LogObject<oneAppTransactionDetailsLoadedParams>} LogObject<oneAppTransactionDetailsLoadedParams>
 */
export function oneAppTransactionDetailsLoaded(params) {
    return { evid: 2205, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'oneAppUserAppliedFilterAtPaymentTable' event (evid: 2206)
 *
 * Event description: User clicks on filters in OneApp on payments table
 *
 * @param {oneAppUserAppliedFilterAtPaymentTableParams} params - The parameters for this event
 * @property {DateTime} dateTo - date to
 * @property {string} paymentCategory - payment method
 * @property {string} vertical - order type or vertical
 * @property {string} transactionStatus - transaction status
 * @property {string} paymentProvider - provider
 * @property {DateTime} dateFrom - date_from
 * @return {LogObject<oneAppUserAppliedFilterAtPaymentTableParams>} LogObject<oneAppUserAppliedFilterAtPaymentTableParams>
 */
export function oneAppUserAppliedFilterAtPaymentTable(params) {
    return { evid: 2206, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletPageFinishedLoading' event (evid: 221)
 *
 * Event description: Logged in member enters My Wallet page
 *
 * @param {myWalletPageFinishedLoadingParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} member_id - member_id
 * @property {number} duration - milliseconds from load started to load finished
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentAgreementList - List of payment_agreement_id on wallet page
 * @property {GUID} userGuid - user_guid
 * @property {number} hasSavedCreditCards - number of saved credit cards
 * @property {string} origin - profile / side menu
 * @property {GUID} memberId - member_id
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {number} hasSavedPaymentMethods - number of saved payment shown
 * @property {string} paymentMethodsList - list of saved payment method ids on wallet page
 * @return {LogObject<myWalletPageFinishedLoadingParams>} LogObject<myWalletPageFinishedLoadingParams>
 */
export function myWalletPageFinishedLoading(params) {
    return { evid: 221, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletPaymentMethodDetailsView' event (evid: 222)
 *
 * Event description: Logged in member clicks on PaymentMethod to view it's details
 *
 * @param {myWalletPaymentMethodDetailsViewParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id ; starting from June-4th 2020 the value is aid
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - uuid
 * @property {string} cardId - card_id
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} memberId - member id
 * @return {LogObject<myWalletPaymentMethodDetailsViewParams>} LogObject<myWalletPaymentMethodDetailsViewParams>
 */
export function myWalletPaymentMethodDetailsView(params) {
    return { evid: 222, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletPaymentMethodDeleteConfirmed' event (evid: 223)
 *
 * Event description: Logged in member confirms payment method  removing from his wallet
 *
 * @param {myWalletPaymentMethodDeleteConfirmedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} member_id - member_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - uuid
 * @property {string} cardId - card_id
 * @property {GUID} memberId - member_id
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} savedPaymentMethodId - saved_payment_method_id
 * @return {LogObject<myWalletPaymentMethodDeleteConfirmedParams>} LogObject<myWalletPaymentMethodDeleteConfirmedParams>
 */
export function myWalletPaymentMethodDeleteConfirmed(params) {
    return { evid: 223, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletPaymentMethodSetPrimaryClicked' event (evid: 225)
 *
 * Event description: Logged in member confirms setting payment method as primary one
 *
 * @param {myWalletPaymentMethodSetPrimaryClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitorId
 * @property {GUID} member_id - member_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - uuid
 * @property {string} cardId - card_id
 * @property {GUID} memberId - member_id
 * @property {GUID} msid - msid
 * @property {boolean} isRetry - true if it's retry
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} savedPaymentMethodId - saved payment method id
 * @property {string} paymentMethod - CreditCard/PayPal
 * @return {LogObject<myWalletPaymentMethodSetPrimaryClickedParams>} LogObject<myWalletPaymentMethodSetPrimaryClickedParams>
 */
export function myWalletPaymentMethodSetPrimaryClicked(params) {
    return { evid: 225, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletClickToOpenAppSettings' event (evid: 226)
 *
 * Event description: when user click to open the app settings from the component (or double-click the component)
 *
 * @param {myWalletClickToOpenAppSettingsParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} origin - orgin
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @return {LogObject<myWalletClickToOpenAppSettingsParams>} LogObject<myWalletClickToOpenAppSettingsParams>
 */
export function myWalletClickToOpenAppSettings(params) {
    return { evid: 226, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletAppSettingsClickOnCustomizeButton' event (evid: 227)
 *
 * Event description: click on customize button in the main tab on the settings menu
 *
 * @param {myWalletAppSettingsClickOnCustomizeButtonParams} params - The parameters for this event
 * @property {string} multilingual - multilingual lang
 * @property {GUID} userGuid - uuid
 * @property {string} origin - orgin
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @return {LogObject<myWalletAppSettingsClickOnCustomizeButtonParams>} LogObject<myWalletAppSettingsClickOnCustomizeButtonParams>
 */
export function myWalletAppSettingsClickOnCustomizeButton(params) {
    return { evid: 227, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletAppSettingsClickOnLeftBar' event (evid: 228)
 *
 * Event description: when user click on the left bar different tabs
 *
 * @param {myWalletAppSettingsClickOnLeftBarParams} params - The parameters for this event
 * @property {string} tab - design / text / manage / ...
 * @property {GUID} userGuid - uuid
 * @property {string} origin - editor / ...
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @return {LogObject<myWalletAppSettingsClickOnLeftBarParams>} LogObject<myWalletAppSettingsClickOnLeftBarParams>
 */
export function myWalletAppSettingsClickOnLeftBar(params) {
    return { evid: 228, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletAppSettingsDesignTabClickOnTopBar' event (evid: 229)
 *
 * Event description: click on the top bar in the settings menu under design tab
 *
 * @param {myWalletAppSettingsDesignTabClickOnTopBarParams} params - The parameters for this event
 * @property {string} multilingual - multilingual lang
 * @property {string} tab - No Saved Cards / Saved Cards
 * @property {GUID} userGuid - uuid
 * @property {string} origin - editor / ...
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @return {LogObject<myWalletAppSettingsDesignTabClickOnTopBarParams>} LogObject<myWalletAppSettingsDesignTabClickOnTopBarParams>
 */
export function myWalletAppSettingsDesignTabClickOnTopBar(params) {
    return { evid: 229, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletAppSettingsChangeStyleAndColor' event (evid: 230)
 *
 * Event description: when user change the style and color of the My Wallets component parameters
 *
 * @param {myWalletAppSettingsChangeStyleAndColorParams} params - The parameters for this event
 * @property {string} parameter - the changed field
 * @property {GUID} userGuid - uuid
 * @property {string} origin - editor / ...
 * @property {GUID} msid - msid
 * @property {string} value - json of the changed values
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} tab - text_fonts/ buttons/ badges/ dividers/ backgrounds_borders
 * @property {string} subTab - selection in tab
 * text_fonts >> text_fonts_colors, primary_text, secondary_text
 * buttons >> primary_button, secondary_button
 * badges >> badges
 * dividers >> dividers
 * backgrounds_borders >> backgrounds_borders , manage_menu
 * @return {LogObject<myWalletAppSettingsChangeStyleAndColorParams>} LogObject<myWalletAppSettingsChangeStyleAndColorParams>
 */
export function myWalletAppSettingsChangeStyleAndColor(params) {
    return { evid: 230, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletAppSettingsResetStyles' event (evid: 231)
 *
 * Event description: when user reset the style and color of the My Wallet component parameters
 *
 * @param {myWalletAppSettingsResetStylesParams} params - The parameters for this event
 * @property {string} multilingual - multilingual lang
 * @property {GUID} userGuid - uuid
 * @property {string} origin - editor / ...
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} tab - where 'Reset to Original Design' button was clicked:
 * text_fonts/ buttons/ badges/ dividers/ backgrounds_borders
 *
 * @return {LogObject<myWalletAppSettingsResetStylesParams>} LogObject<myWalletAppSettingsResetStylesParams>
 */
export function myWalletAppSettingsResetStyles(params) {
    return { evid: 231, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletAppSettingsChangeTextField' event (evid: 232)
 *
 * Event description: when user change the text of the My Wallet component
 *
 * @param {myWalletAppSettingsChangeTextFieldParams} params - The parameters for this event
 * @property {string} multilingual - multilingual lang
 * @property {string} field - pageTitle/ PageSubtitle/...
 * @property {GUID} userGuid - uuid
 * @property {string} text - new text
 * @property {string} origin - editor / ...
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @return {LogObject<myWalletAppSettingsChangeTextFieldParams>} LogObject<myWalletAppSettingsChangeTextFieldParams>
 */
export function myWalletAppSettingsChangeTextField(params) {
    return { evid: 232, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boletoBankLogin' event (evid: 233)
 *
 * Event description: When User choose bank and click to login
 *
 * @param {boletoBankLoginParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} bankName - Choosed bank
 * @property {number} amountLong - amount
 * @property {GUID} transactionid - transaction id
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {DateTime} due_date - due_date on boleto
 * @property {GUID} msid - msid
 * @return {LogObject<boletoBankLoginParams>} LogObject<boletoBankLoginParams>
 */
export function boletoBankLogin(params) {
    return { evid: 233, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boletoPrintButton' event (evid: 234)
 *
 * Event description: When User click print boleto
 *
 * @param {boletoPrintButtonParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {number} amountLong - amount
 * @property {GUID} transactionid - transaction id
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {DateTime} due_date - due_date on boleto
 * @property {GUID} msid - msid
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<boletoPrintButtonParams>} LogObject<boletoPrintButtonParams>
 */
export function boletoPrintButton(params) {
    return { evid: 234, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boletoPopUpOpened' event (evid: 235)
 *
 * Event description: When User choose pay with boleto and click place order
 *
 * @param {boletoPopUpOpenedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {number} amountLong - amount
 * @property {GUID} transactionid - transaction id
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {DateTime} due_date - due_date on boleto
 * @property {GUID} msid - msid
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<boletoPopUpOpenedParams>} LogObject<boletoPopUpOpenedParams>
 */
export function boletoPopUpOpened(params) {
    return { evid: 235, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boletoPopUpClosed' event (evid: 236)
 *
 * Event description: When User close boleto popup
 *
 * @param {boletoPopUpClosedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {number} amountLong - amount
 * @property {GUID} transactionid - transaction id
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {DateTime} due_date - due_date on boleto
 * @property {boolean} close_button - true if button close ,false if [x]
 * @property {GUID} msid - msid
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<boletoPopUpClosedParams>} LogObject<boletoPopUpClosedParams>
 */
export function boletoPopUpClosed(params) {
    return { evid: 236, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boOpenProcessPayoutLightbox' event (evid: 237)
 *
 * Event description: open process payout lightbox
 *
 * @param {boOpenProcessPayoutLightboxParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {number} loadTime - load time
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boOpenProcessPayoutLightboxParams>} LogObject<boOpenProcessPayoutLightboxParams>
 */
export function boOpenProcessPayoutLightbox(params) {
    return { evid: 237, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantBusinessLocationShowHideModalWindow' event (evid: 238)
 *
 * Event description: Merchant-click on country on the Payment first page of the smart onboarding
 *
 * @param {merchantBusinessLocationShowHideModalWindowParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentMethod - already connected paymentMethods or null
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} businessLocation - country on the Accept Payment tab
 * @property {string} type - first_entry/remove_pm
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantBusinessLocationShowHideModalWindowParams>} LogObject<merchantBusinessLocationShowHideModalWindowParams>
 */
export function merchantBusinessLocationShowHideModalWindow(params) {
    return { evid: 238, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'prohibitedProductsPageWasShown' event (evid: 239)
 *
 * Event description: Prohibited products page was shown to user after a click on Connect Wix Payment
 *
 * @param {prohibitedProductsPageWasShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} businessLocation - business_location
 * @property {string} origin - add_payment_method/connect_me/change_provider
 * @property {string} selectedValue - list of categories, selected by a user to connect, separated by comma
 * @property {string} pageType - prohibited_products, products_list
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<prohibitedProductsPageWasShownParams>} LogObject<prohibitedProductsPageWasShownParams>
 */
export function prohibitedProductsPageWasShown(params) {
    return { evid: 239, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantCtaAtProhibitedProductsPageClicked' event (evid: 240)
 *
 * Event description: Merchant - CTA at Accept Payments clicked
 *
 * @param {merchantCtaAtProhibitedProductsPageClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} ctaType - yes/no/learn_more_cbd/learn_more_prohibited/back/close
 * @property {string} origin - place where user clicks on cta
 * @property {GUID} accountprofileid - account profile id
 * @property {string} pageType - accept_payments / prohibited_products
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantCtaAtProhibitedProductsPageClickedParams>} LogObject<merchantCtaAtProhibitedProductsPageClickedParams>
 */
export function merchantCtaAtProhibitedProductsPageClicked(params) {
    return { evid: 240, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'bruteforceDetectorCheckPerformed' event (evid: 2400)
 *
 * Event description: Bruteforce Detector: check performed.
 *
 * @param {bruteforceDetectorCheckPerformedParams} params - The parameters for this event
 * @property {string} paymentCategory - deprecated
 * @property {GUID} visitor_id - visitor
 * @property {string} paymentProvider - similar to 64:1005
 * @property {boolean} isActive - is active after the check or need to be blocked
 * @property {GUID} msid - msid
 * @property {string} value - metadata of the check
 * @return {LogObject<bruteforceDetectorCheckPerformedParams>} LogObject<bruteforceDetectorCheckPerformedParams>
 */
export function bruteforceDetectorCheckPerformed(params) {
    return { evid: 2400, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClicksConfirmAtProhibitedProductsPageList' event (evid: 241)
 *
 * Event description: Merchant clicks Confirm at Prohibited products page list
 *
 * @param {merchantClicksConfirmAtProhibitedProductsPageListParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} selected_value - values, that user have selected semicolon separated
 * @property {string} pageType - prohibited_products_list
 * @property {GUID} msid - msid
 * @return {LogObject<merchantClicksConfirmAtProhibitedProductsPageListParams>} LogObject<merchantClicksConfirmAtProhibitedProductsPageListParams>
 */
export function merchantClicksConfirmAtProhibitedProductsPageList(params) {
    return { evid: 241, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutCashierWidgetLoadSteps' event (evid: 2412)
 *
 * Event description: Event is sent on each step of loading Cashier widget to measure and monitor its perfomance.  Expected steps: 'first_load', 'lazy_loaded', 'settings_load', 'methods-load', 'cc-form-load','failed-load'
 *
 * @param {checkoutCashierWidgetLoadStepsParams} params - The parameters for this event
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} captchaRequiredForPm - list of payment methods which require passing captcha challenge (e.i. creditCard))
 * @property {string} allowMerchantUseDisplayOption - if pm is supposed to be saved for MIT trx by willing of uou/ required to be saved/ saving is not supported: 'SHOW_ALLOW_MERCHANT_USE_CHECKBOX’, ‘SHOW_NOTHING’
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} appInstanceId - app_instance_id
 * @property {string} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} buyerComponent - react if new
 * @property {string} creditCardId - null if visitor is not logged in or doesn't have saved cc
 * @property {string} currency - payment currency (is sent by vertical)
 * @property {number} hasSavedCreditCards - null if visitor is not logged in or doesn't have saved cc
 * @property {number} hasSavedPaymentAgreements - null if visitor is not logged in or doesn't have saved payment agreements
 * @property {number} hasSavedPaymentMethods - null if visitor is not logged in or doesn't have saved payment methods
 * @property {string} intentCharge - intent of charge: one time/ recurring
 * @property {string} mandatoryFieldsList - list of required mandatory fields that were shown to UoU
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} orderId - vertical order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} passedMandatoryFields - list of required mandatory fields that weren't shown to UoU and were passed by vertical
 * @property {string} paymentAgreementList - list of payment agreements which were received
 * @property {string} paymentCategory - CreditCard/Ewallet,offline
 * @property {string} paymentProvider - com.stripe/net.authorize
 * @property {string} payWith - default payment category
 * @property {string} prefilledMandatoryFields - list of required mandatory fields that were shown to UoU already prefilled
 * @property {string} requiredPmMandatoryFields - list of required mandatory fields from payment provider
 * @property {string} savedPaymentMethodId - saved_payment_method_id (the same guid for CC )
 * @property {string} savePaymentMethodDisplayOption - if pm is supposed to be saved by default/ by willing of uou/ saving is not supported:‘disclaimer’\ ‘checkbox’\‘show_nothing’
 * @property {string} step - step of loading 'first_load', 'lazy_loaded', 'settings_load', 'methods-load', 'cc-form-load','failed-load' etc
 * @property {string} errorDesc - if step is failed
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} timestamp - timestamp of  actual step happen (miliseconds)
 * @property {string} visitorId - visitor_id
 * @property {string} msid - msid
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<checkoutCashierWidgetLoadStepsParams>} LogObject<checkoutCashierWidgetLoadStepsParams>
 */
export function checkoutCashierWidgetLoadSteps(params) {
    return { evid: 2412, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantObNavigation' event (evid: 242)
 *
 * Event description: Merchant-clicked
 * “More Payment Methods”
 * ”Third party credit / debit card providers”
 * Back arrow on OB page
 *
 * @param {merchantObNavigationParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - dependent from page where user located
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} cta - back/see_more_options/choose_provider/connect_page
 * @property {string} paymentProvider - dependent from page where user located
 * @property {string} pageType - what page in the dashboard : accept_payments / more_payment_methods  / third_party_cc_providers
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} flowType - in case of filtering shows type of filter.  For Multicurrency - filtered_multicurrency_provider.
 * @return {LogObject<merchantObNavigationParams>} LogObject<merchantObNavigationParams>
 */
export function merchantObNavigation(params) {
    return { evid: 242, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ashierGenericErrors' event (evid: 243)
 *
 * Event description: Сashier generic errors
 *
 * @param {ashierGenericErrorsParams} params - The parameters for this event
 * @property {string} domain - identifies area of application in which error has occurred
 * @property {string} details - json-formatted error details
 * @property {string} message - message
 * @property {string} msid - msid
 * @return {LogObject<ashierGenericErrorsParams>} LogObject<ashierGenericErrorsParams>
 */
export function ashierGenericErrors(params) {
    return { evid: 243, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickCompleteSetUpWp' event (evid: 244)
 *
 * Event description: Merchant clicked complete set-up WP on cashier payment method page
 * substitution of event 85:116
 *
 * @param {merchantClickCompleteSetUpWpParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} wpGeo - wp geo
 * @property {string} pageType - what page in the dashboard / origin page
 * @property {GUID} accountProfileId - wp profile id
 * @property {GUID} msid - msid
 * @return {LogObject<merchantClickCompleteSetUpWpParams>} LogObject<merchantClickCompleteSetUpWpParams>
 */
export function merchantClickCompleteSetUpWp(params) {
    return { evid: 244, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantManagePageLoadStart' event (evid: 245)
 *
 * Event description: Merchant - Manage page Load Start.
 *
 * @param {merchantManagePageLoadStartParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} paymentMethod - payment_category
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} accountprofileid - account profile id
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantManagePageLoadStartParams>} LogObject<merchantManagePageLoadStartParams>
 */
export function merchantManagePageLoadStart(params) {
    return { evid: 245, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantManagePageLoadComplete' event (evid: 246)
 *
 * Event description: Merchant - Manage page Load Complete.
 *
 * @param {merchantManagePageLoadCompleteParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - loading time in miliseconds
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} paymentMethod - creditCard,paypal
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {boolean} status - status
 * @property {GUID} accountprofileid - account profile id
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantManagePageLoadCompleteParams>} LogObject<merchantManagePageLoadCompleteParams>
 */
export function merchantManagePageLoadComplete(params) {
    return { evid: 246, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantModalWasShownToUser' event (evid: 248)
 *
 * Event description: Modal was shown to user on accept_payments / manage_page / third_party_cc_providers
 *
 * @param {merchantModalWasShownToUserParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - currently connected category
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} page - page where modal appear
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} modal_name - disconnect_last_provider_popup / switch_provider etc
 * @property {GUID} appid - app_id
 * @property {string} paymentProvider - currently connected provider
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantModalWasShownToUserParams>} LogObject<merchantModalWasShownToUserParams>
 */
export function merchantModalWasShownToUser(params) {
    return { evid: 248, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantUserClickedCta' event (evid: 249)
 *
 * Event description: User clicked cta on notification on accept_payments / manage_page / third_party_cc_providers
 *
 * @param {merchantUserClickedCtaParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - currently connected category
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} page - accept_payments / manage_page etc
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} modal_name - leave_without_changes/ confirm_disable_payment_method
 * @property {string} notificationType - notification text as it is seen by merchant
 * @property {GUID} appid - app_id
 * @property {string} cta - keep / disable
 * @property {string} paymentProvider - currently connected provider
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantUserClickedCtaParams>} LogObject<merchantUserClickedCtaParams>
 */
export function merchantUserClickedCta(params) {
    return { evid: 249, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantMorePaymentOptionsPageShowed' event (evid: 250)
 *
 * Event description: Event includes all pages in Accept Payments component
 *
 * @param {merchantMorePaymentOptionsPageShowedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} paymentMethod - creditCard,paypal
 * @property {string} page - page
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {GUID} accountprofileid - account profile id
 * @property {string} paymentProvider - payment_provider
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @property {string} flowType - in case of filtering shows type of filter.  For Multicurrency - filtered_multicurrency_provider.
 * @return {LogObject<merchantMorePaymentOptionsPageShowedParams>} LogObject<merchantMorePaymentOptionsPageShowedParams>
 */
export function merchantMorePaymentOptionsPageShowed(params) {
    return { evid: 250, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'savedPaymentMethodFlowLifeCycle' event (evid: 2501)
 *
 * Event description: Events to track saved payment method life cycle - from saving till deletion; To store snapshots of saved payment method's changes; Events are sent with result of action
 *
 * @param {savedPaymentMethodFlowLifeCycleParams} params - The parameters for this event
 * @property {boolean} success - true if the action was successful [not null]
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} uuid - uuid
 * @property {string} paymentMethod - saved payment method, currently it supports only creditCard
 * @property {string} cardBrand - credit card brand:  amex, dankort, diners, discover, isracard, jcb, maestro, mastercard, unionpay, visa, rupay
 * @property {string} errorReason - error description
 * @property {GUID} meta_site_id - msid
 * @property {string} ownerSiteMemberId - owner of saved payment method if it's site member
 * @property {string} savedPaymentMethodId - saved payment method ID, will be empty in case the upsert operation failed
 * @property {boolean} newSavedPaymentMethodCreated - true if new saved payment method was created during the upsert action and false if the existing payment method was updated  [not null]
 * @property {boolean} merchantUseAllowed - true if buyer choose to allow for MIT trx
 * @property {string} action - action applied: Upsert, Update, MarkPrimary, UnmarkPrimary, Delete, GdprDelete [not null]
 * @property {boolean} primary - true if saved payment method is primary for this siteMember, null if upsert failed
 * @return {LogObject<savedPaymentMethodFlowLifeCycleParams>} LogObject<savedPaymentMethodFlowLifeCycleParams>
 */
export function savedPaymentMethodFlowLifeCycle(params) {
    return { evid: 2501, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickedOnCtaOnConnectPage' event (evid: 252)
 *
 * Event description: Merchant-click complete setup
 *
 * @param {merchantClickedOnCtaOnConnectPageParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,paypal
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} cta - cancel or sign_in
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @return {LogObject<merchantClickedOnCtaOnConnectPageParams>} LogObject<merchantClickedOnCtaOnConnectPageParams>
 */
export function merchantClickedOnCtaOnConnectPage(params) {
    return { evid: 252, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantPremiumBannerShown' event (evid: 253)
 *
 * Event description: Upgrade banner was shown to the user at Accept payment page
 *
 * @param {merchantPremiumBannerShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} place - place where banner is located - accept_payment_top_page,connect_payment_method_modal
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {GUID} msid - msid
 * @property {string} bannerName - You're one step closer to selling online, Upgrade with a Business & eCommerce Premium Plan so customers can pay you at checkout
 * @property {string} namespace - namespace
 * @return {LogObject<merchantPremiumBannerShownParams>} LogObject<merchantPremiumBannerShownParams>
 */
export function merchantPremiumBannerShown(params) {
    return { evid: 253, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnPremiumBanner' event (evid: 254)
 *
 * Event description: User clicks on Upgrade Banner at Accept Payment Page
 *
 * @param {merchantClickOnPremiumBannerParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment_category
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} notificationType - notification name
 * @property {string} cta - upgrade, close
 * @property {string} paymentProvider - payment_provider
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {GUID} msid - msid
 * @property {string} bannerName - You're one step closer to selling online,Upgrade with a Business & eCommerce Premium Plan so customers can pay you at checkout
 * @property {string} namespace - namespace
 * @return {LogObject<merchantClickOnPremiumBannerParams>} LogObject<merchantClickOnPremiumBannerParams>
 */
export function merchantClickOnPremiumBanner(params) {
    return { evid: 254, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickedLinkOnConnectPage' event (evid: 255)
 *
 * Event description: Merchant clicked link on connect page
 *
 * @param {merchantClickedLinkOnConnectPageParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} linkUrl - url where user will be redirected
 * @property {string} paymentCategory - creditCard,paypal
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} page - Connect page, manage payments, accept payments pages
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} link_name - alias for link on a page
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantClickedLinkOnConnectPageParams>} LogObject<merchantClickedLinkOnConnectPageParams>
 */
export function merchantClickedLinkOnConnectPage(params) {
    return { evid: 255, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantGoToDashboardClicked' event (evid: 256)
 *
 * Event description: Go to Dashboard at Payment Page clicked
 *
 * @param {merchantGoToDashboardClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment methods (sofort/iDeal etc.)
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - Stripe
 * @property {GUID} msid - msid
 * @return {LogObject<merchantGoToDashboardClickedParams>} LogObject<merchantGoToDashboardClickedParams>
 */
export function merchantGoToDashboardClicked(params) {
    return { evid: 256, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'installmentBannerLoadStart' event (evid: 257)
 *
 * Event description: start loading widget with data about installments ( Afterpay, laybuy, etc)
 *
 * @param {installmentBannerLoadStartParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} app_id - app id
 * @property {string} productId - product id
 * @property {GUID} msid - msid
 * @property {boolean} delayedCapture - true, if widget will be shown shown with methods which support Auth&Capture
 * @return {LogObject<installmentBannerLoadStartParams>} LogObject<installmentBannerLoadStartParams>
 */
export function installmentBannerLoadStart(params) {
    return { evid: 257, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'installmentBannerLoadFinished' event (evid: 258)
 *
 * Event description: Widget with data about installments ( Afterpay, laybuy, etc) is loaded successfully
 *
 * @param {installmentBannerLoadFinishedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} offerId - offered payment methods that were shown (array)
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} paymentProvider - offered payment providers that were shown (array)
 * @property {string} app_id - app id
 * @property {string} productId - product id
 * @property {GUID} msid - msid
 * @property {boolean} delayedCapture - true, if widget will be shown shown with methods which support Auth&Capture
 * @return {LogObject<installmentBannerLoadFinishedParams>} LogObject<installmentBannerLoadFinishedParams>
 */
export function installmentBannerLoadFinished(params) {
    return { evid: 258, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'installmentBannerClickedMoreInfo' event (evid: 259)
 *
 * Event description: start loading widget with data about installments ( Afterpay, laybuy, etc)
 *
 * @param {installmentBannerClickedMoreInfoParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} paymentProvider - name of provider
 * @property {string} app_id - app id
 * @property {string} productId - product id
 * @property {GUID} msid - msid
 * @property {boolean} delayedCapture - true, if widget will be shown shown with methods which support Auth&Capture
 *
 * @return {LogObject<installmentBannerClickedMoreInfoParams>} LogObject<installmentBannerClickedMoreInfoParams>
 */
export function installmentBannerClickedMoreInfo(params) {
    return { evid: 259, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'installmentBannerListLoaded' event (evid: 260)
 *
 * Event description: Widget with data about installments ( Afterpay, laybuy, etc) has changed because of buyer's change of the order amount
 *
 * @param {installmentBannerListLoadedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} offerId - offered payment methods that were shown (array)
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} paymentProvider - offered payment providers that were shown (array)
 * @property {string} app_id - app id
 * @property {GUID} msid - m
 * @property {boolean} delayedCapture - true, if widget will be shown shown with methods which support Auth&Capture
 *
 * @property {string} productId - product id
 * @return {LogObject<installmentBannerListLoadedParams>} LogObject<installmentBannerListLoadedParams>
 */
export function installmentBannerListLoaded(params) {
    return { evid: 260, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'expressCheckoutWidgetLoadingStart' event (evid: 261)
 *
 * Event description: When express widget loading start
 *
 * @param {expressCheckoutWidgetLoadingStartParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores' pages where ECW appears
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} origin - page type where widget was loaded
 * @property {string} currency - currency
 * @property {string} productId - product_id where widget is loaded
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<expressCheckoutWidgetLoadingStartParams>} LogObject<expressCheckoutWidgetLoadingStartParams>
 */
export function expressCheckoutWidgetLoadingStart(params) {
    return { evid: 261, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'expressCheckoutWidgetLoadingFinished' event (evid: 262)
 *
 * Event description: When express widget loading finished
 *
 * @param {expressCheckoutWidgetLoadingFinishedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} flowType - Apple JS version
 * @property {string} paymentmethod - list of methods that widget contains
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} origin - page type where widget was loaded
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} currency - currency
 * @property {string} productId - product_id where widget is loaded
 * @property {GUID} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} paymentProviderList - list of payment providers and methods that widget shows
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<expressCheckoutWidgetLoadingFinishedParams>} LogObject<expressCheckoutWidgetLoadingFinishedParams>
 */
export function expressCheckoutWidgetLoadingFinished(params) {
    return { evid: 262, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'expressCheckoutWidgetButtonClicked' event (evid: 263)
 *
 * Event description: When user click on button
 *
 * @param {expressCheckoutWidgetButtonClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - amount * 100000
 * @property {string} paymentmethod - payment method that was choosed
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} origin - page type where widget was loaded
 * @property {string} sessionId - session for ECW
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - currency
 * @property {string} productId - product_id where widget is loaded
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<expressCheckoutWidgetButtonClickedParams>} LogObject<expressCheckoutWidgetButtonClickedParams>
 */
export function expressCheckoutWidgetButtonClicked(params) {
    return { evid: 263, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'applePayCallBackReceived' event (evid: 264)
 *
 * Event description: When user change some data on ApplePay side
 *
 * @param {applePayCallBackReceivedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - new amount * 100000
 * @property {string} paymentmethod - payment method that was choosed
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} fieldName - what was changed
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - session for ECW
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} newValue - value that was selected
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} checkoutType - regular / express
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<applePayCallBackReceivedParams>} LogObject<applePayCallBackReceivedParams>
 */
export function applePayCallBackReceived(params) {
    return { evid: 264, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'applePayPaymentAuthorized' event (evid: 265)
 *
 * Event description: When user confirm payment on ApplePay widget
 *
 * @param {applePayPaymentAuthorizedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amountLong - amount * 100000
 * @property {string} orderSnapshotId - cashier order snapshot id
 * @property {string} paymentmethod - applepay
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - session id
 * @property {string} paymentProvider - payment_provider
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} transactionId - transaction_id
 * @property {string} checkoutType - regular / express
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<applePayPaymentAuthorizedParams>} LogObject<applePayPaymentAuthorizedParams>
 */
export function applePayPaymentAuthorized(params) {
    return { evid: 265, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutThankYouPageLoad' event (evid: 267)
 *
 * Event description: Thank You Page was shown to the visitor after the order completed for InApp browser
 *
 * @param {checkoutThankYouPageLoadParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} appInstanceId - app_instance_id
 * @property {string} orderId - vertical order id
 * @property {string} uuid - uuid
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} amount - checkout amount
 * @property {string} transactionStatus - transaction status
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<checkoutThankYouPageLoadParams>} LogObject<checkoutThankYouPageLoadParams>
 */
export function checkoutThankYouPageLoad(params) {
    return { evid: 267, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantSelectPmUnderWixPayment' event (evid: 268)
 *
 * Event description: Merchant click on the toggle to select/unselect additional Payment Method when connecting Wix Payments
 *
 * @param {merchantSelectPmUnderWixPaymentParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,cardReader,applePay
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} coupledCategory - payment category that will be auto selected with current one
 * @property {boolean} isSelected - true/false
 * @property {string} paymentProvider - wixpay_stripe
 * @property {string} pageType - payment_options
 * @property {GUID} msid - msid
 * @return {LogObject<merchantSelectPmUnderWixPaymentParams>} LogObject<merchantSelectPmUnderWixPaymentParams>
 */
export function merchantSelectPmUnderWixPayment(params) {
    return { evid: 268, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnEditAccountDetails' event (evid: 269)
 *
 * Event description: Click on Edit Account details at Wix Payments Manage Page
 *
 * @param {merchantClickOnEditAccountDetailsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,cardReader etc
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} accountStatus - accepting_payments, etc
 * @property {GUID} accountprofileid - account profile id
 * @property {string} paymentProvider - wixpay_adyen, wixpay_stripe
 * @property {string} pageType - manage_page
 * @property {GUID} msid - msid
 * @property {string} referralinfo - place from which user came
 * @return {LogObject<merchantClickOnEditAccountDetailsParams>} LogObject<merchantClickOnEditAccountDetailsParams>
 */
export function merchantClickOnEditAccountDetails(params) {
    return { evid: 269, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnGoToDashboard' event (evid: 270)
 *
 * Event description: Merchant Click on Go to Dashboard at Wix Payments Manage Page
 *
 * @param {merchantClickOnGoToDashboardParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,cardReader etc
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - wixpay_adyen, wixpay_stripe
 * @property {string} pageType - manage_page
 * @property {GUID} msid - msid
 * @return {LogObject<merchantClickOnGoToDashboardParams>} LogObject<merchantClickOnGoToDashboardParams>
 */
export function merchantClickOnGoToDashboard(params) {
    return { evid: 270, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentAgreementFlowLifeCycle' event (evid: 2701)
 *
 * Event description: Event to track payment agreement life cycle - from saving till deletion; To store snapshots of saved payment agreement's changes; Events are sent with result of action
 *
 * @param {paymentAgreementFlowLifeCycleParams} params - The parameters for this event
 * @property {string} visitorId - visitor_id
 * @property {boolean} success - true if the action was successful [not null]
 * @property {string} siteVisitorId - visitor_id
 * @property {string} paymentMethod - saved payment method, currently it supports only creditCard
 * @property {string} paymentAgreementId - payment agreement id
 * @property {string} errorReason - error description
 * @property {string} orderSnapshotId - cashier order id
 * @property {string} providerId - payment provider id
 * @property {string} ownerSiteMemberId - owner of saved payment agreement if it's site member
 * @property {string} contactId - contact Id of owner
 * @property {boolean} sandbox - if action from sandbox
 * @property {string} paymentProvider - payment provider
 * @property {string} savedPaymentMethodId - saved payment method ID
 * @property {string} chargeId - transaction id (charge id) under which PA was used
 * @property {boolean} merchantUseAllowed - true if buyer choose to allow for MIT trx or saved by merchant
 * @property {boolean} merchantInitiated - if action was processed by merchant
 * @property {string} action - action applied: Create, Update, Delete, GdprDelete [not null]
 * @property {string} siteMemberId - owner of saved payment agreement if it's site member
 * @return {LogObject<paymentAgreementFlowLifeCycleParams>} LogObject<paymentAgreementFlowLifeCycleParams>
 */
export function paymentAgreementFlowLifeCycle(params) {
    return { evid: 2701, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutsChangePayoutsFrequencySrc64Evid271' event (evid: 271)
 *
 * Event description: Change toggle for Payouts frequency in settings
 *
 * @param {payoutsChangePayoutsFrequencySrc64Evid271Params} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} accountStatus - accepting_payments, etc
 * @property {string} scheduleType - payout schedule = frequency
 * @property {GUID} accountprofileid - account profile id
 * @property {string} paymentProvider - com.wix.wixpay.us, etc
 * @property {string} wpGeo - the country that the user selected
 * @property {string} pageType - manage_page
 * @property {GUID} msid - msid
 * @property {number} days - # of weekday / # of day of month
 * @return {LogObject<payoutsChangePayoutsFrequencySrc64Evid271Params>} LogObject<payoutsChangePayoutsFrequencySrc64Evid271Params>
 */
export function payoutsChangePayoutsFrequencySrc64Evid271(params) {
    return { evid: 271, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickSaveAtManagePage' event (evid: 272)
 *
 * Event description: Merchant > Click Save at manage page
 *
 * @param {merchantClickSaveAtManagePageParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - CreditCard,cardReader etc
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} fields - softDescriptor
 * @property {string} paymentProvider - payment_provider
 * @property {string} wpGeo - wp geo
 * @property {string} pageType - manage_page
 * @property {string} type - setting types for WixPayments onboarding: Statement| Payout
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantClickSaveAtManagePageParams>} LogObject<merchantClickSaveAtManagePageParams>
 */
export function merchantClickSaveAtManagePage(params) {
    return { evid: 272, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantChangesSavedSuccessfullyAtManagePage' event (evid: 273)
 *
 * Event description: Merchant Successfully Changed settings at Manage Page
 *
 * @param {merchantChangesSavedSuccessfullyAtManagePageParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - CreditCard,cardReader etc
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} accountStatus - accepting_payments, etc
 * @property {string} fields - softDescriptor
 * @property {GUID} accountprofileid - account profile id
 * @property {string} paymentProvider - payment_provider
 * @property {string} wpGeo - wp geo
 * @property {string} pageType - manage_page
 * @property {string} type - setting types for WixPayments onboarding: Statement| Payout
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantChangesSavedSuccessfullyAtManagePageParams>} LogObject<merchantChangesSavedSuccessfullyAtManagePageParams>
 */
export function merchantChangesSavedSuccessfullyAtManagePage(params) {
    return { evid: 273, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'validationErrorAtManagePage' event (evid: 274)
 *
 * Event description: Validation Error at Manage Page
 *
 * @param {validationErrorAtManagePageParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - CreditCard,cardReader etc
 * @property {string} fields - changed fields (list if more than 1)
 * @property {string} invalidFields - invalid fields in the form (list if more than 1)
 * @property {GUID} accountprofileid - account profile id
 * @property {string} paymentProvider - payment_provider
 * @property {string} wpGeo - wp geo
 * @property {string} pageType - manage_page
 * @property {GUID} msid - msid
 * @return {LogObject<validationErrorAtManagePageParams>} LogObject<validationErrorAtManagePageParams>
 */
export function validationErrorAtManagePage(params) {
    return { evid: 274, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickContinueAtPaymentOptions' event (evid: 275)
 *
 * Event description: Merchant Clicks Continue at Payment Options when connecting Wix Payments
 *
 * @param {merchantClickContinueAtPaymentOptionsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} avalableCategory - available payment methods  (creditCard/cardReader/applePay)
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - wixpay_adyen/wixpay_stripe
 * @property {string} selectedCategory - selected payment method (creditCard/cardReader)
 * @property {string} pageType - payment_options
 * @property {GUID} msid - msid
 * @property {string} ctaPlace - location of the button on candyshop, i.e. top/bottom
 * @property {string} preselectedPaymentMethods - list of preselected payment methods
 * @property {string} businessLocation - location that user selected on payments page
 * @return {LogObject<merchantClickContinueAtPaymentOptionsParams>} LogObject<merchantClickContinueAtPaymentOptionsParams>
 */
export function merchantClickContinueAtPaymentOptions(params) {
    return { evid: 275, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutZipAutoFilledFields' event (evid: 276)
 *
 * Event description: list of fields that was filled by zip
 *
 * @param {checkoutZipAutoFilledFieldsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} appInstanceId - app_instance_id
 * @property {string} orderSnapshotId - PbW snapshot id
 * @property {GUID} appId - vetrical
 * @property {GUID} msid - msid
 * @property {string} value - List of fields that was filled by zip. Example "country" or "country, city, address""
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<checkoutZipAutoFilledFieldsParams>} LogObject<checkoutZipAutoFilledFieldsParams>
 */
export function checkoutZipAutoFilledFields(params) {
    return { evid: 276, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantAccountMeetRequirement' event (evid: 277)
 *
 * Event description: Click on Verify Account on Accept Payment
 *
 * @param {merchantAccountMeetRequirementParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} pageType - page where user at
 * @property {GUID} msid - msid
 * @return {LogObject<merchantAccountMeetRequirementParams>} LogObject<merchantAccountMeetRequirementParams>
 */
export function merchantAccountMeetRequirement(params) {
    return { evid: 277, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickReviewRequirements' event (evid: 278)
 *
 * Event description: Click Review Requirements for Account at Accept Payments
 *
 * @param {merchantClickReviewRequirementsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} pageType - page where user at
 * @property {GUID} msid - msid
 * @return {LogObject<merchantClickReviewRequirementsParams>} LogObject<merchantClickReviewRequirementsParams>
 */
export function merchantClickReviewRequirements(params) {
    return { evid: 278, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOrderHardware' event (evid: 279)
 *
 * Event description: Click Order Hardware for POS at Accept Payments
 *
 * @param {merchantClickOrderHardwareParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} place - place at candyshop
 * @property {string} pageType - page where user at
 * @property {GUID} msid - msid
 * @return {LogObject<merchantClickOrderHardwareParams>} LogObject<merchantClickOrderHardwareParams>
 */
export function merchantClickOrderHardware(params) {
    return { evid: 279, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickViewPayouts' event (evid: 280)
 *
 * Event description: Click on View Payouts at Accept Payments
 *
 * @param {merchantClickViewPayoutsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - authorize.net/Stripe/
 * @property {string} pageType - page where user at
 * @property {GUID} msid - msid
 * @return {LogObject<merchantClickViewPayoutsParams>} LogObject<merchantClickViewPayoutsParams>
 */
export function merchantClickViewPayouts(params) {
    return { evid: 280, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'sitePublishWithCurrencyConverterInstalledSrc64Evid2801' event (evid: 2801)
 *
 * Event description: Site publish with Currency Converter installed
 *
 * @param {sitePublishWithCurrencyConverterInstalledSrc64Evid2801Params} params - The parameters for this event
 * @property {string} msid - msid
 * @property {string} uuid - uuid
 * @property {string} builderType - Studio / Editor / etc
 * @property {string} additionalCurrency - a list of secondary  currencies selected in currency converter
 * @property {string} siteCurrency - site currency
 * @return {LogObject<sitePublishWithCurrencyConverterInstalledSrc64Evid2801Params>} LogObject<sitePublishWithCurrencyConverterInstalledSrc64Evid2801Params>
 */
export function sitePublishWithCurrencyConverterInstalledSrc64Evid2801(params) {
    return { evid: 2801, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'revalidateSessionByWidget' event (evid: 281)
 *
 * Event description: BI event for "revalidate session" call by the Widget to help with investigating a mysterious issue with "changing visitorId"
 *
 * @param {revalidateSessionByWidgetParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} msid - msid
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<revalidateSessionByWidgetParams>} LogObject<revalidateSessionByWidgetParams>
 */
export function revalidateSessionByWidget(params) {
    return { evid: 281, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'expressCheckoutMorePaymentOptionsButtonClicked' event (evid: 282)
 *
 * Event description: When user click on button
 *
 * @param {expressCheckoutMorePaymentOptionsButtonClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} origin - page type where widget was loaded
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} currency - currency
 * @property {string} productId - product id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} catalogAppId - A list of verticals which are in this order separated by “ ,“:(stores app id, bookings app_id, ...) .
 * @return {LogObject<expressCheckoutMorePaymentOptionsButtonClickedParams>} LogObject<expressCheckoutMorePaymentOptionsButtonClickedParams>
 */
export function expressCheckoutMorePaymentOptionsButtonClicked(params) {
    return { evid: 282, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClicksOnPublishSite' event (evid: 283)
 *
 * Event description: User clicks on Publish site on the banner in order to be verified
 *
 * @param {merchantClicksOnPublishSiteParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} notificationType - notification name
 * @property {string} cta - publish
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {GUID} msid - msid
 * @property {string} bannerName - wix_payments_card_notification
 * @return {LogObject<merchantClicksOnPublishSiteParams>} LogObject<merchantClicksOnPublishSiteParams>
 */
export function merchantClicksOnPublishSite(params) {
    return { evid: 283, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantVerifyAccountClicked' event (evid: 284)
 *
 * Event description: Click on Verify Account on Accept Payment
 *
 * @param {merchantVerifyAccountClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} notificationType - notification name
 * @property {string} pageType - page where user at
 * @property {GUID} msid - msid
 * @return {LogObject<merchantVerifyAccountClickedParams>} LogObject<merchantVerifyAccountClickedParams>
 */
export function merchantVerifyAccountClicked(params) {
    return { evid: 284, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantGenericErrors' event (evid: 285)
 *
 * Event description: Generic error in BM at payment funnel
 *
 * @param {merchantGenericErrorsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} errorDesc - error description
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} message - message (full text message)
 * @property {string} pageType - page where error was appeared
 * @property {GUID} msid - msid
 * @property {string} referralinfo - place from which user came
 * @property {string} namespace - namespace
 * @return {LogObject<merchantGenericErrorsParams>} LogObject<merchantGenericErrorsParams>
 */
export function merchantGenericErrors(params) {
    return { evid: 285, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClicksOnMakeTestTransaction' event (evid: 286)
 *
 * Event description: User clicks on 'Make Test Transaction' on the banner in order to be verified
 *
 * @param {merchantClicksOnMakeTestTransactionParams} params - The parameters for this event
 * @property {GUID} visitorId - visitorid
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment method
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} notificationType - notification name
 * @property {string} cta - make_test_transaction
 * @property {string} paymentProvider - payment_provider
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {GUID} msid - msid
 * @property {string} bannerName - wix_payments_card_notification
 * @return {LogObject<merchantClicksOnMakeTestTransactionParams>} LogObject<merchantClicksOnMakeTestTransactionParams>
 */
export function merchantClicksOnMakeTestTransaction(params) {
    return { evid: 286, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pixModalOpened' event (evid: 287)
 *
 * Event description: When User choose pay with PIX and click place order
 *
 * @param {pixModalOpenedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {number} amountLong - amount
 * @property {GUID} transactionid - transaction id
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {string} paymentMethodType - payment_method_type
 * @property {string} paymentProvider - Payment Provider
 * @property {GUID} msid - msid
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<pixModalOpenedParams>} LogObject<pixModalOpenedParams>
 */
export function pixModalOpened(params) {
    return { evid: 287, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pixModalClosed' event (evid: 288)
 *
 * Event description: When User close PIX popup
 *
 * @param {pixModalClosedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {number} amountLong - amount
 * @property {GUID} transactionid - transaction id
 * @property {string} reason - close modal reason ( Success, Decline, Timeout)
 * @property {string} orderSnapshotId - Cashier order snapshot id
 * @property {string} paymentMethodType - payment_method_type
 * @property {string} paymentProvider - Payment Provider
 * @property {GUID} msid - msid
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<pixModalClosedParams>} LogObject<pixModalClosedParams>
 */
export function pixModalClosed(params) {
    return { evid: 288, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbCountryMissed' event (evid: 289)
 *
 * Event description: SPB Buyer Country missed.
 *
 * @param {spbCountryMissedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {GUID} appInstanceId - vertical instance id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} currency - new value
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} paymentProvider - paypal/ wixpay/ other platform providers
 * @property {string} paymentMethod - paypal related if possible
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<spbCountryMissedParams>} LogObject<spbCountryMissedParams>
 */
export function spbCountryMissed(params) {
    return { evid: 289, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantViewWixPaymentsNotifications' event (evid: 290)
 *
 * Event description: Merchant saw Wix Payments Notifications (Complete your setup to get verified, Your account is suspended, etc)
 *
 * @param {merchantViewWixPaymentsNotificationsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitorid
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard, applePay, POS
 * @property {string} platformName - null(desktop)/oneApp/partnets etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} account_id - Wix Payments account_id
 * @property {string} notificationType - COMPLIANCE_NOT_FINISHED_V4, etc
 * @property {string} paymentProvider - com.wix.wixpay.us
 * @property {string} pageType - page
 * @property {string} notificationBody - text of notification
 * @property {GUID} msid - msid
 * @return {LogObject<merchantViewWixPaymentsNotificationsParams>} LogObject<merchantViewWixPaymentsNotificationsParams>
 */
export function merchantViewWixPaymentsNotifications(params) {
    return { evid: 290, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantSelectPmUnderWixPaymentPageLoadedSrc64Evid291' event (evid: 291)
 *
 * Event description: Merchant opened Wix Payments Payment Method selection Page
 *
 * @param {merchantSelectPmUnderWixPaymentPageLoadedSrc64Evid291Params} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} preselectedPaymentMethods - list of preselected PM
 * @property {string} pageType - wix_payments_payment_options
 * @property {GUID} msid - msid
 * @property {string} businessLocation - location that user selected on payments page
 * @property {string} avalableCategory - list of available payment methods
 * @return {LogObject<merchantSelectPmUnderWixPaymentPageLoadedSrc64Evid291Params>} LogObject<merchantSelectPmUnderWixPaymentPageLoadedSrc64Evid291Params>
 */
export function merchantSelectPmUnderWixPaymentPageLoadedSrc64Evid291(params) {
    return { evid: 291, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickToSwitchPayPalAccountBetweenWp3rdPartySrc64Evid292' event (evid: 292)
 *
 * Event description: Merchant click to switch to PayPal under Wix Payments or PayPal as 3rd Party provider
 *
 * @param {merchantClickToSwitchPayPalAccountBetweenWp3rdPartySrc64Evid292Params} params - The parameters for this event
 * @property {GUID} msid - site id
 * @property {string} switchTo - provider that users clicked and wanted to switch
 * @property {string} pageType - page where user clicks on CTA, e.g. accept_payments
 * @property {string} referralInfo - place from which user came
 * @property {string} platformName - null if desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel
 * @property {string} place - Place where CTA is located, e.g. wp_payment_method_status_card/ paypal_connect_card
 * @property {string} actiontype - e.g. switch/ switch to Paypal as third-party provider
 * @property {string} paymentProvider - currently connected provider
 * @property {string} businessLocation - location that user selected on Accept Payments
 * @return {LogObject<merchantClickToSwitchPayPalAccountBetweenWp3rdPartySrc64Evid292Params>} LogObject<merchantClickToSwitchPayPalAccountBetweenWp3rdPartySrc64Evid292Params>
 */
export function merchantClickToSwitchPayPalAccountBetweenWp3rdPartySrc64Evid292(params) {
    return { evid: 292, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickToOnSkuIqAppButtonSrc64Evid293' event (evid: 293)
 *
 * Event description: Merchant click to install or view App button on Payments page
 *
 * @param {merchantClickToOnSkuIqAppButtonSrc64Evid293Params} params - The parameters for this event
 * @property {GUID} msid - site id
 * @property {string} paymentProvider - currently connected provider with PayPal
 * @property {string} pageType - page where user clicks on CTA, e.g. accept_payments
 * @property {string} referralInfo - place from which user came
 * @property {string} platformName - null if desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel
 * @property {string} businessLocation - location that user selected on payments page
 * @property {string} actiontype - install_app / view_app
 * @return {LogObject<merchantClickToOnSkuIqAppButtonSrc64Evid293Params>} LogObject<merchantClickToOnSkuIqAppButtonSrc64Evid293Params>
 */
export function merchantClickToOnSkuIqAppButtonSrc64Evid293(params) {
    return { evid: 293, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'confirmationOfSkuIqAppButtonWasInstalledSrc64Evid294' event (evid: 294)
 *
 * Event description: Callback about SKU IQ APP Button was installed
 *
 * @param {confirmationOfSkuIqAppButtonWasInstalledSrc64Evid294Params} params - The parameters for this event
 * @property {GUID} msid - site id
 * @property {string} paymentProvider - currently connected provider with PayPal
 * @property {string} pageType - page where user clicks on CTA, e.g. accept_payments
 * @property {string} referralInfo - place from which user came
 * @property {string} platformName - null if desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel
 * @property {string} businessLocation - location that user selected on payments page
 * @return {LogObject<confirmationOfSkuIqAppButtonWasInstalledSrc64Evid294Params>} LogObject<confirmationOfSkuIqAppButtonWasInstalledSrc64Evid294Params>
 */
export function confirmationOfSkuIqAppButtonWasInstalledSrc64Evid294(params) {
    return { evid: 294, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'showClickAiAssistantCtaSrc64Evid295' event (evid: 295)
 *
 * Event description: Show / Click "AI assistant" CTA to a user
 *
 * @param {showClickAiAssistantCtaSrc64Evid295Params} params - The parameters for this event
 * @property {string} screenResolution - screen_resolution
 * @property {string} actionType - show / click
 * @property {string} skillName - name of the AI skill - wp_fix_id_verification_issue  etc
 * @property {GUID} skillID - skill id
 * @property {string} page - page on which assistant located
 * @return {LogObject<showClickAiAssistantCtaSrc64Evid295Params>} LogObject<showClickAiAssistantCtaSrc64Evid295Params>
 */
export function showClickAiAssistantCtaSrc64Evid295(params) {
    return { evid: 295, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantTechnicalEventOutdatedProviderBulletsSrc64Evid296' event (evid: 296)
 *
 * Event description: logger Ivent for technical check
 *
 * @param {merchantTechnicalEventOutdatedProviderBulletsSrc64Evid296Params} params - The parameters for this event
 * @property {string} bulletsKey - bullets Key
 * @property {string} providerName - provider
 * @return {LogObject<merchantTechnicalEventOutdatedProviderBulletsSrc64Evid296Params>} LogObject<merchantTechnicalEventOutdatedProviderBulletsSrc64Evid296Params>
 */
export function merchantTechnicalEventOutdatedProviderBulletsSrc64Evid296(params) {
    return { evid: 296, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbBuyerMissedShippingOrBillingAddressFields' event (evid: 297)
 *
 * Event description: SPB Buyer Shipping or Billing address fields are empty (e.g. emptyShippingFields: state, addressLine2, phone; emptyBillingFields: phone)
 *
 * @param {spbBuyerMissedShippingOrBillingAddressFieldsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {GUID} appInstanceId - vertical instance id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} currency - new value
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} paymentProvider - paypal/ wixpay/ other platform providers
 * @property {string} paymentMethod - paypal related if possible
 * @property {string} emptyShippingFields - countryCode, state,  addressLine2, phone, city, postalCode, fullName,
 * @property {string} emptyBillingFields - countryCode, firstName, lastName, email, phone, state
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<spbBuyerMissedShippingOrBillingAddressFieldsParams>} LogObject<spbBuyerMissedShippingOrBillingAddressFieldsParams>
 */
export function spbBuyerMissedShippingOrBillingAddressFields(params) {
    return { evid: 297, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbOnPaymentAuthorizedCallbackResult' event (evid: 298)
 *
 * Event description: Response of onPaymentAuthorized callback from vertical on Express Checkout
 *
 * @param {spbOnPaymentAuthorizedCallbackResultParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} result - success / error
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<spbOnPaymentAuthorizedCallbackResultParams>} LogObject<spbOnPaymentAuthorizedCallbackResultParams>
 */
export function spbOnPaymentAuthorizedCallbackResult(params) {
    return { evid: 298, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantSelectPayPalPageLoadedSrc64Evid3' event (evid: 3)
 *
 * Event description: PayPal selector page was loaded after user clicked to connect PP cashier
 *
 * @param {merchantSelectPayPalPageLoadedSrc64Evid3Params} params - The parameters for this event
 * @property {string} referralInfo - place from which user came from
 * @property {boolean} isPartner - is_partner
 * @property {string} platformName - from where users is reaching the page desktop/oneApp/partner etc
 * @property {string} preselectedPaymentMethods - wixpay_paypal or cashier_paypal
 * @property {string} pageType - paypal_selector_page
 * @property {GUID} msid - msid
 * @property {string} businessLocation - location that user selected on payments page
 * @property {string} screenResolution - users's screen resolution
 * @property {string} merchantState - wp_pp_not_connected /wp_connected_pp_not_connected / wp_disconnected_pp_not_connected
 * @return {LogObject<merchantSelectPayPalPageLoadedSrc64Evid3Params>} LogObject<merchantSelectPayPalPageLoadedSrc64Evid3Params>
 */
export function merchantSelectPayPalPageLoadedSrc64Evid3(params) {
    return { evid: 3, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payButtonClickToOpenPaymentSettings' event (evid: 301)
 *
 * Event description: when user click to open the Payment Settings from the component
 *
 * @param {payButtonClickToOpenPaymentSettingsParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {GUID} app_instance_id - app_instance_id
 * @property {string} app_id - app_id
 * @property {GUID} msid - msid
 * @property {string} editorType - Classic/Studio/etc.
 * @return {LogObject<payButtonClickToOpenPaymentSettingsParams>} LogObject<payButtonClickToOpenPaymentSettingsParams>
 */
export function payButtonClickToOpenPaymentSettings(params) {
    return { evid: 301, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payButtonAppSettingsChangePrice' event (evid: 303)
 *
 * Event description: when user change the price
 *
 * @param {payButtonAppSettingsChangePriceParams} params - The parameters for this event
 * @property {number} price - new price
 * @property {GUID} userGuid - uuid
 * @property {GUID} app_instance_id - app_instance_id
 * @property {string} app_id - app_id
 * @property {GUID} msid - msid
 * @property {string} editorType - Classic/Studio/etc.
 * @return {LogObject<payButtonAppSettingsChangePriceParams>} LogObject<payButtonAppSettingsChangePriceParams>
 */
export function payButtonAppSettingsChangePrice(params) {
    return { evid: 303, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payButtonAppSettingsClickOnEditCurrency' event (evid: 304)
 *
 * Event description: when user click on Edit Currency
 *
 * @param {payButtonAppSettingsClickOnEditCurrencyParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {GUID} app_instance_id - app_instance_id
 * @property {string} app_id - app_id
 * @property {GUID} msid - msid
 * @property {string} editorType - Classic/Studio/etc.
 * @return {LogObject<payButtonAppSettingsClickOnEditCurrencyParams>} LogObject<payButtonAppSettingsClickOnEditCurrencyParams>
 */
export function payButtonAppSettingsClickOnEditCurrency(params) {
    return { evid: 304, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouPayButtonClick' event (evid: 305)
 *
 * Event description: When UoU click on any PayButton
 *
 * @param {uouPayButtonClickParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - unique id in app_id
 * @property {GUID} appid - app_id (pay API vertical id)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} origin - page where button located
 * @property {string} sessionId - unique session id for this click
 * @property {GUID} memberId - member_id
 * @property {GUID} paybuttonid - unique button ID
 * @property {GUID} msid - msid
 * @return {LogObject<uouPayButtonClickParams>} LogObject<uouPayButtonClickParams>
 */
export function uouPayButtonClick(params) {
    return { evid: 305, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouPayButtonPageOpen' event (evid: 306)
 *
 * Event description: when exact page of pyButton flow is open
 *
 * @param {uouPayButtonPageOpenParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - unique id in app_id
 * @property {GUID} appid - app_id (pay API vertical id)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} origin - page where button located
 * @property {string} pagename - shipping_address, payment_method, payment_confirm
 * @property {string} sessionId - unique session id for this click
 * @property {GUID} memberId - member_id
 * @property {GUID} paybuttonid - unique button ID
 * @property {GUID} msid - msid
 * @return {LogObject<uouPayButtonPageOpenParams>} LogObject<uouPayButtonPageOpenParams>
 */
export function uouPayButtonPageOpen(params) {
    return { evid: 306, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouPayButtonPaymentStarted' event (evid: 307)
 *
 * Event description: when all fields and pages  filled and  user submit transaction
 *
 * @param {uouPayButtonPaymentStartedParams} params - The parameters for this event
 * @property {string} paymentCategory - paymentCategory
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - unique id in app_id
 * @property {string} orderSnapshotId - orderSnapshotId
 * @property {string} paymentMethodType - paymentMethodType
 * @property {GUID} appid - app_id (pay API vertical id)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} origin - page where button located
 * @property {string} pagename - shipping_address, payment_method, payment_confirm
 * @property {string} sessionId - unique session id for this click
 * @property {string} paymentProvider - paymentProvider
 * @property {GUID} memberId - member_id
 * @property {GUID} paybuttonid - unique button ID
 * @property {GUID} msid - msid
 * @return {LogObject<uouPayButtonPaymentStartedParams>} LogObject<uouPayButtonPaymentStartedParams>
 */
export function uouPayButtonPaymentStarted(params) {
    return { evid: 307, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouPayButtonResultPage' event (evid: 308)
 *
 * Event description: when all fields and pages  filled and  user submit transaction
 *
 * @param {uouPayButtonResultPageParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} errorMessage - in case of decline - cashier_error_code
 * @property {GUID} appInstanceId - unique id in app_id
 * @property {boolean} result - true - success, false- failed
 * @property {GUID} appid - app_id (pay API vertical id)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} origin - page where button located
 * @property {string} sessionId - unique session id for this click
 * @property {GUID} memberId - member_id
 * @property {GUID} paybuttonid - unique button ID
 * @property {GUID} msid - msid
 * @return {LogObject<uouPayButtonResultPageParams>} LogObject<uouPayButtonResultPageParams>
 */
export function uouPayButtonResultPage(params) {
    return { evid: 308, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'uouPayButtonPageSubmited' event (evid: 309)
 *
 * Event description: when all fields  filled and  user  proceed to next page
 *
 * @param {uouPayButtonPageSubmitedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} appInstanceId - unique id in app_id
 * @property {GUID} appid - app_id (pay API vertical id)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} origin - page where button located
 * @property {string} pagename - shipping_address, payment_method, payment_confirm
 * @property {string} sessionId - unique session id for this click
 * @property {GUID} memberId - member_id
 * @property {GUID} paybuttonid - unique button ID
 * @property {GUID} msid - msid
 * @return {LogObject<uouPayButtonPageSubmitedParams>} LogObject<uouPayButtonPageSubmitedParams>
 */
export function uouPayButtonPageSubmited(params) {
    return { evid: 309, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickContinueAtPayPalSelectorPageSrc64Evid4' event (evid: 4)
 *
 * Event description: Merchant Clicks Continue at PayPal selector Page
 *
 * @param {merchantClickContinueAtPayPalSelectorPageSrc64Evid4Params} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} avalableCategory - wixpay_paypal, cashier_paypal
 * @property {string} selectedCategory - wixpay_paypal or cashier_paypal
 * @property {string} pageType - paypal_selector_page
 * @property {GUID} msid - msid
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} screenResolution - user's screen resolution
 * @property {string} preselectedPaymentMethods - wixpay_paypal or cashier_paypal
 * @property {string} businessLocation - location that user selected on payments page
 * @return {LogObject<merchantClickContinueAtPayPalSelectorPageSrc64Evid4Params>} LogObject<merchantClickContinueAtPayPalSelectorPageSrc64Evid4Params>
 */
export function merchantClickContinueAtPayPalSelectorPageSrc64Evid4(params) {
    return { evid: 4, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boMainPageOpen' event (evid: 400)
 *
 * Event description: Employee open Main Page in Back Office
 *
 * @param {boMainPageOpenParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} msid - msid
 * @return {LogObject<boMainPageOpenParams>} LogObject<boMainPageOpenParams>
 */
export function boMainPageOpen(params) {
    return { evid: 400, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boMainPageSearch' event (evid: 401)
 *
 * Event description: Employee input value and clicked 'Search'
 *
 * @param {boMainPageSearchParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {string} searchType - email, url, msid, uuid, etc
 * @property {GUID} msid - msid
 * @property {string} value - value of search
 * @return {LogObject<boMainPageSearchParams>} LogObject<boMainPageSearchParams>
 */
export function boMainPageSearch(params) {
    return { evid: 401, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boMainPageSearchResult' event (evid: 402)
 *
 * Event description: Employee gets search results
 *
 * @param {boMainPageSearchResultParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {string} errorMessage - error if search was failed
 * @property {number} returnedResults - count of returned results
 * @property {string} searchType - email, url, msid, uuid, etc
 * @property {number} loadTime - how long users wait for result
 * @property {GUID} msid - msid
 * @return {LogObject<boMainPageSearchResultParams>} LogObject<boMainPageSearchResultParams>
 */
export function boMainPageSearchResult(params) {
    return { evid: 402, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageLoad' event (evid: 403)
 *
 * Event description: Employee open Site page
 *
 * @param {boSitePageLoadParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageLoadParams>} LogObject<boSitePageLoadParams>
 */
export function boSitePageLoad(params) {
    return { evid: 403, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageClickUserDrawer' event (evid: 404)
 *
 * Event description: Employee click user drawer
 *
 * @param {boSitePageClickUserDrawerParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - uuid
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageClickUserDrawerParams>} LogObject<boSitePageClickUserDrawerParams>
 */
export function boSitePageClickUserDrawer(params) {
    return { evid: 404, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageCloseUserDrawer' event (evid: 405)
 *
 * Event description: Employee click close in user drawer
 *
 * @param {boSitePageCloseUserDrawerParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - uuid
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageCloseUserDrawerParams>} LogObject<boSitePageCloseUserDrawerParams>
 */
export function boSitePageCloseUserDrawer(params) {
    return { evid: 405, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageUserDrawerClickOnUm' event (evid: 406)
 *
 * Event description: Employee click open in User Manager
 *
 * @param {boSitePageUserDrawerClickOnUmParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {Url} url - user manager url
 * @property {GUID} userGuid - uuid
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageUserDrawerClickOnUmParams>} LogObject<boSitePageUserDrawerClickOnUmParams>
 */
export function boSitePageUserDrawerClickOnUm(params) {
    return { evid: 406, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageClickOnTransaction' event (evid: 407)
 *
 * Event description: Employee click on exact transaction
 *
 * @param {boSitePageClickOnTransactionParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} transactionid - transaction id
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageClickOnTransactionParams>} LogObject<boSitePageClickOnTransactionParams>
 */
export function boSitePageClickOnTransaction(params) {
    return { evid: 407, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionDrawerOpen' event (evid: 408)
 *
 * Event description: Employee open transaction drawer
 *
 * @param {boSitePageTransactionDrawerOpenParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} transactionid - transaction id
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageTransactionDrawerOpenParams>} LogObject<boSitePageTransactionDrawerOpenParams>
 */
export function boSitePageTransactionDrawerOpen(params) {
    return { evid: 408, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionDrawerClose' event (evid: 409)
 *
 * Event description: Employee close transaction drawer
 *
 * @param {boSitePageTransactionDrawerCloseParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} transactionid - transaction id
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageTransactionDrawerCloseParams>} LogObject<boSitePageTransactionDrawerCloseParams>
 */
export function boSitePageTransactionDrawerClose(params) {
    return { evid: 409, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionRefundClicked' event (evid: 410)
 *
 * Event description: Employee click refund from drawer
 *
 * @param {boSitePageTransactionRefundClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} transactionid - transaction id
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageTransactionRefundClickedParams>} LogObject<boSitePageTransactionRefundClickedParams>
 */
export function boSitePageTransactionRefundClicked(params) {
    return { evid: 410, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionRefundComfirmed' event (evid: 411)
 *
 * Event description: Employee choose amount and confirm refund
 *
 * @param {boSitePageTransactionRefundComfirmedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} transactionid - transaction id
 * @property {string} reason - reason
 * @property {number} amount - refunded amount
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageTransactionRefundComfirmedParams>} LogObject<boSitePageTransactionRefundComfirmedParams>
 */
export function boSitePageTransactionRefundComfirmed(params) {
    return { evid: 411, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionRefundClickCancel' event (evid: 412)
 *
 * Event description: Employee click cancel on refund dialogue
 *
 * @param {boSitePageTransactionRefundClickCancelParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} transactionid - transaction id
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageTransactionRefundClickCancelParams>} LogObject<boSitePageTransactionRefundClickCancelParams>
 */
export function boSitePageTransactionRefundClickCancel(params) {
    return { evid: 412, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageRefundResults' event (evid: 414)
 *
 * Event description: Refund sent and we show result pop-up
 *
 * @param {boSitePageRefundResultsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} transactionid - transaction id
 * @property {number} amount - amount
 * @property {boolean} status - request status
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageRefundResultsParams>} LogObject<boSitePageRefundResultsParams>
 */
export function boSitePageRefundResults(params) {
    return { evid: 414, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageLoad' event (evid: 415)
 *
 * Event description: Employee open Account page
 *
 * @param {boAccountPageLoadParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageLoadParams>} LogObject<boAccountPageLoadParams>
 */
export function boAccountPageLoad(params) {
    return { evid: 415, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickOnOpenInWpDashboard' event (evid: 416)
 *
 * Event description: Employee click Open in WP Dashboard
 *
 * @param {boAccountPageClickOnOpenInWpDashboardParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {Url} url - WP dashboard url
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickOnOpenInWpDashboardParams>} LogObject<boAccountPageClickOnOpenInWpDashboardParams>
 */
export function boAccountPageClickOnOpenInWpDashboard(params) {
    return { evid: 416, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickUnsuspendAccount' event (evid: 417)
 *
 * Event description: Employee click  unsuspend account
 *
 * @param {boAccountPageClickUnsuspendAccountParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickUnsuspendAccountParams>} LogObject<boAccountPageClickUnsuspendAccountParams>
 */
export function boAccountPageClickUnsuspendAccount(params) {
    return { evid: 417, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickConfirmUnsuspendAccount' event (evid: 418)
 *
 * Event description: Employee click Confirm  unsuspend account
 *
 * @param {boAccountPageClickConfirmUnsuspendAccountParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickConfirmUnsuspendAccountParams>} LogObject<boAccountPageClickConfirmUnsuspendAccountParams>
 */
export function boAccountPageClickConfirmUnsuspendAccount(params) {
    return { evid: 418, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickCancelUnsuspendAccount' event (evid: 419)
 *
 * Event description: Employee click Cancel unsuspend account
 *
 * @param {boAccountPageClickCancelUnsuspendAccountParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickCancelUnsuspendAccountParams>} LogObject<boAccountPageClickCancelUnsuspendAccountParams>
 */
export function boAccountPageClickCancelUnsuspendAccount(params) {
    return { evid: 419, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickXUnsuspendAccount' event (evid: 420)
 *
 * Event description: Employee click   [x] unsuspend account
 *
 * @param {boAccountPageClickXUnsuspendAccountParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickXUnsuspendAccountParams>} LogObject<boAccountPageClickXUnsuspendAccountParams>
 */
export function boAccountPageClickXUnsuspendAccount(params) {
    return { evid: 420, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickProcessPayout' event (evid: 421)
 *
 * Event description: Employee click   Process Payout in account
 *
 * @param {boAccountPageClickProcessPayoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant_id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickProcessPayoutParams>} LogObject<boAccountPageClickProcessPayoutParams>
 */
export function boAccountPageClickProcessPayout(params) {
    return { evid: 421, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickConfirmProcessPayout' event (evid: 422)
 *
 * Event description: Employee click Confirm   Process Payout
 *
 * @param {boAccountPageClickConfirmProcessPayoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {number} amount - payout amount
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickConfirmProcessPayoutParams>} LogObject<boAccountPageClickConfirmProcessPayoutParams>
 */
export function boAccountPageClickConfirmProcessPayout(params) {
    return { evid: 422, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickCancelProcessPayout' event (evid: 423)
 *
 * Event description: Employee click Cancel  Process Payout
 *
 * @param {boAccountPageClickCancelProcessPayoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant_id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickCancelProcessPayoutParams>} LogObject<boAccountPageClickCancelProcessPayoutParams>
 */
export function boAccountPageClickCancelProcessPayout(params) {
    return { evid: 423, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickXProcessPayout' event (evid: 424)
 *
 * Event description: Employee click   [x]  Process Payout
 *
 * @param {boAccountPageClickXProcessPayoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickXProcessPayoutParams>} LogObject<boAccountPageClickXProcessPayoutParams>
 */
export function boAccountPageClickXProcessPayout(params) {
    return { evid: 424, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickRefundPayout' event (evid: 425)
 *
 * Event description: Employee click    Refund Payout in account
 *
 * @param {boAccountPageClickRefundPayoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickRefundPayoutParams>} LogObject<boAccountPageClickRefundPayoutParams>
 */
export function boAccountPageClickRefundPayout(params) {
    return { evid: 425, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickConfirmRefundPayout' event (evid: 426)
 *
 * Event description: Employee click Confirm    Refund Payout
 *
 * @param {boAccountPageClickConfirmRefundPayoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} reason - reason
 * @property {number} amount - payout amount
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickConfirmRefundPayoutParams>} LogObject<boAccountPageClickConfirmRefundPayoutParams>
 */
export function boAccountPageClickConfirmRefundPayout(params) {
    return { evid: 426, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickCancelRefundPayout' event (evid: 427)
 *
 * Event description: Employee click Cancel   Refund Payout
 *
 * @param {boAccountPageClickCancelRefundPayoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickCancelRefundPayoutParams>} LogObject<boAccountPageClickCancelRefundPayoutParams>
 */
export function boAccountPageClickCancelRefundPayout(params) {
    return { evid: 427, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickXRefundPayout' event (evid: 428)
 *
 * Event description: Employee click   [x]  Refund Payout
 *
 * @param {boAccountPageClickXRefundPayoutParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickXRefundPayoutParams>} LogObject<boAccountPageClickXRefundPayoutParams>
 */
export function boAccountPageClickXRefundPayout(params) {
    return { evid: 428, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickManualReview' event (evid: 429)
 *
 * Event description: Employee click Manual Review in account
 *
 * @param {boAccountPageClickManualReviewParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickManualReviewParams>} LogObject<boAccountPageClickManualReviewParams>
 */
export function boAccountPageClickManualReview(params) {
    return { evid: 429, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickConfirmManualReview' event (evid: 430)
 *
 * Event description: Employee click Confirm Manual Review
 *
 * @param {boAccountPageClickConfirmManualReviewParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {boolean} delayPayouts - true false
 * @property {GUID} accountId - provider account id
 * @property {string} reason - reason
 * @property {string} finding - finding
 * @property {boolean} status - Status
 * @property {string} category - category
 * @property {string} tenantId - tenant
 * @property {GUID} msid - msid
 * @property {number} days - # of days until payout
 * @return {LogObject<boAccountPageClickConfirmManualReviewParams>} LogObject<boAccountPageClickConfirmManualReviewParams>
 */
export function boAccountPageClickConfirmManualReview(params) {
    return { evid: 430, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickCancelManualReview' event (evid: 431)
 *
 * Event description: Employee click Cancel Manual Review
 *
 * @param {boAccountPageClickCancelManualReviewParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickCancelManualReviewParams>} LogObject<boAccountPageClickCancelManualReviewParams>
 */
export function boAccountPageClickCancelManualReview(params) {
    return { evid: 431, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickXManualReview' event (evid: 432)
 *
 * Event description: Employee click   [x]  Manual Review
 *
 * @param {boAccountPageClickXManualReviewParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {string} tenantId - tenantid
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickXManualReviewParams>} LogObject<boAccountPageClickXManualReviewParams>
 */
export function boAccountPageClickXManualReview(params) {
    return { evid: 432, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickSwitchAccountToggle' event (evid: 433)
 *
 * Event description: Employee  click switch account toggle
 *
 * @param {boAccountPageClickSwitchAccountToggleParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - new account_id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickSwitchAccountToggleParams>} LogObject<boAccountPageClickSwitchAccountToggleParams>
 */
export function boAccountPageClickSwitchAccountToggle(params) {
    return { evid: 433, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageUserDrawerOpened' event (evid: 434)
 *
 * Event description: Employee open user drawer
 *
 * @param {boSitePageUserDrawerOpenedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - uuid
 * @property {number} loadTime - loadtime
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageUserDrawerOpenedParams>} LogObject<boSitePageUserDrawerOpenedParams>
 */
export function boSitePageUserDrawerOpened(params) {
    return { evid: 434, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boSitePageTransactionRefundOpened' event (evid: 435)
 *
 * Event description: Employee open refund from drawer
 *
 * @param {boSitePageTransactionRefundOpenedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} transactionid - transaction id
 * @property {GUID} msid - msid
 * @return {LogObject<boSitePageTransactionRefundOpenedParams>} LogObject<boSitePageTransactionRefundOpenedParams>
 */
export function boSitePageTransactionRefundOpened(params) {
    return { evid: 435, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageUnsuspendAccountOpened' event (evid: 436)
 *
 * Event description: Employee open unsuspend account
 *
 * @param {boAccountPageUnsuspendAccountOpenedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {number} loadTime - load time
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageUnsuspendAccountOpenedParams>} LogObject<boAccountPageUnsuspendAccountOpenedParams>
 */
export function boAccountPageUnsuspendAccountOpened(params) {
    return { evid: 436, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageManualReviewOpened' event (evid: 438)
 *
 * Event description: Employee open manual Review in account
 *
 * @param {boAccountPageManualReviewOpenedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {GUID} accountId - provider account id
 * @property {number} loadTime - loadtime
 * @property {string} tenantId - tenant id
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageManualReviewOpenedParams>} LogObject<boAccountPageManualReviewOpenedParams>
 */
export function boAccountPageManualReviewOpened(params) {
    return { evid: 438, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'boAccountPageClickAdyenUrl' event (evid: 439)
 *
 * Event description: Employee  click adyen url
 *
 * @param {boAccountPageClickAdyenUrlParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {string} displayName - employee displayName
 * @property {boolean} isPartner - is_partner
 * @property {Url} url - Adyen BO url
 * @property {GUID} accountId - accountid
 * @property {string} tenantId - tenant
 * @property {GUID} msid - msid
 * @return {LogObject<boAccountPageClickAdyenUrlParams>} LogObject<boAccountPageClickAdyenUrlParams>
 */
export function boAccountPageClickAdyenUrl(params) {
    return { evid: 439, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutBuyerFillsMandatoryFields' event (evid: 440)
 *
 * Event description: Buyer input data in mandatory fields
 *
 * @param {checkoutBuyerFillsMandatoryFieldsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} mandatoryField - mandatory field which was filled
 * @property {string} appInstanceId - app_instance_id
 * @property {string} orderId - order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {GUID} user_id - uuid
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 *
 * @return {LogObject<checkoutBuyerFillsMandatoryFieldsParams>} LogObject<checkoutBuyerFillsMandatoryFieldsParams>
 */
export function checkoutBuyerFillsMandatoryFields(params) {
    return { evid: 440, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutReturnPaymentAgreement' event (evid: 441)
 *
 * Event description: Checkout widget returns payment agreement: in case creating a new one or assigning previously created
 *
 * @param {checkoutReturnPaymentAgreementParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id ; starting from June-4th 2020 the value is aid
 * @property {boolean} isPartner - is_partner
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} paymentAgreementId - payment_agreement_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} paymentProvider - PayPal, Stripe, etc
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} transactionId - transaction_id in case of creating a new payment agreement
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<checkoutReturnPaymentAgreementParams>} LogObject<checkoutReturnPaymentAgreementParams>
 */
export function checkoutReturnPaymentAgreement(params) {
    return { evid: 441, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletAddPaymentMethodButtonRendered' event (evid: 442)
 *
 * Event description: 'Add Payment Method' button  was rendered
 *
 * @param {myWalletAddPaymentMethodButtonRenderedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitorId
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - uuid
 * @property {string} origin - profile / side menu
 * @property {number} loadTime - time from initial to render
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} memberId - member id
 * @property {string} renderPlace - place where 'AddPaymentMethod' button rendered: list, empty
 * @return {LogObject<myWalletAddPaymentMethodButtonRenderedParams>} LogObject<myWalletAddPaymentMethodButtonRenderedParams>
 */
export function myWalletAddPaymentMethodButtonRendered(params) {
    return { evid: 442, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletAddPaymentMethodButtonClicked' event (evid: 443)
 *
 * Event description: 'Add Payment Method' button  was clicked by member
 *
 * @param {myWalletAddPaymentMethodButtonClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitorId
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - uuid
 * @property {string} origin - profile / side menu
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} memberId - member id
 * @return {LogObject<myWalletAddPaymentMethodButtonClickedParams>} LogObject<myWalletAddPaymentMethodButtonClickedParams>
 */
export function myWalletAddPaymentMethodButtonClicked(params) {
    return { evid: 443, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletModalStartedLoading' event (evid: 444)
 *
 * Event description: Modal window started loading on wallet page (depend on modal_type: 'Adding CC', 'Deletion CC')
 *
 * @param {myWalletModalStartedLoadingParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} modalType - modal type: 'adding_pm', 'removing_pm'
 * @property {GUID} userGuid - user_guid
 * @property {string} cardId - card_guid
 * @property {number} hasSavedCreditCards - number of saved credit cards
 * @property {string} origin - profile / side menu
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} memberId - member id
 * @property {string} savedPaymentMethodId - saved payment method id
 * @property {string} paymentMethod - CreditCard/ PayPal
 * @property {boolean} isPrimary - true/ false
 * @property {boolean} isExpired - true/ false
 * @return {LogObject<myWalletModalStartedLoadingParams>} LogObject<myWalletModalStartedLoadingParams>
 */
export function myWalletModalStartedLoading(params) {
    return { evid: 444, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletModalFinishedLoading' event (evid: 445)
 *
 * Event description: Modal window finished loading on wallet page (depend on modal_type: 'Adding CC', 'Deletion CC')
 *
 * @param {myWalletModalFinishedLoadingParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {number} duration - milliseconds from load started to load finished (without full widget loading time )
 * @property {boolean} isPartner - is_partner
 * @property {string} modalType - modal type: 'adding_pm', 'removing_pm'
 * @property {GUID} userGuid - user_guid
 * @property {string} cardId - card_guid
 * @property {string} origin - profile / side menu
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} memberId - member id
 * @property {string} savedPaymentMethodId - saved payment method id
 * @property {string} paymentMethod - CreditCard/ PayPal
 * @property {boolean} isPrimary - true/ false
 * @property {boolean} IsExpired - true/false
 * @return {LogObject<myWalletModalFinishedLoadingParams>} LogObject<myWalletModalFinishedLoadingParams>
 */
export function myWalletModalFinishedLoading(params) {
    return { evid: 445, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletNewPaymentMethodWasAdded' event (evid: 446)
 *
 * Event description: New Payment Method was added on wallet page successfully. List of previously saved PMs was updated
 *
 * @param {myWalletNewPaymentMethodWasAddedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitorId
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentAgreementList - List of payment_agreement_id on wallet page
 * @property {GUID} userGuid - uuid
 * @property {string} origin - profile / side menu
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} memberId - member id
 * @property {string} savedPaymentMethodId - saved payment method id
 * @property {string} paymentMethod - CreditCard/ PayPal
 * @return {LogObject<myWalletNewPaymentMethodWasAddedParams>} LogObject<myWalletNewPaymentMethodWasAddedParams>
 */
export function myWalletNewPaymentMethodWasAdded(params) {
    return { evid: 446, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'creditCardTokenization' event (evid: 447)
 *
 * Event description: Once uou/ user entered CC details and is ready to proceed with payment, payment provider requires tokenize CC
 *
 * @param {creditCardTokenizationParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} orderId - order_id from vertical
 * @property {string} result - result of undergoing 3ds flow
 * @property {string} errorReason - error description for result 'failed'
 * @property {string} orderSnapshotId - order_snapshot_id from cashier
 * @property {string} stepName - step of cc tokenization
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} paymentProvider - com.stripe/square/wp
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @return {LogObject<creditCardTokenizationParams>} LogObject<creditCardTokenizationParams>
 */
export function creditCardTokenization(params) {
    return { evid: 447, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletModalClosed' event (evid: 448)
 *
 * Event description: Any interaction by member (submit, cancel or click on cross X) in both modals - adding pm and removing pm
 *
 * @param {myWalletModalClosedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitorId
 * @property {boolean} isPartner - is_partner
 * @property {string} modalType - modal type: 'adding_pm', 'removing_pm'
 * @property {GUID} userGuid - uuid
 * @property {string} cardId - card_guid
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} origin - profile / side menu
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} memberId - member id
 * @property {string} savedPaymentMethodId - saved payment method id
 * @property {string} paymentMethod - CreditCard/ PayPal
 * @property {boolean} isPrimary - true/ false
 *
 * @property {boolean} IsExpired - true/false
 * @property {string} action - action which was done as a result of closing modal: 'add/ cancel/ click_close'
 * @return {LogObject<myWalletModalClosedParams>} LogObject<myWalletModalClosedParams>
 */
export function myWalletModalClosed(params) {
    return { evid: 448, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutBuyerFillsFormFields' event (evid: 449)
 *
 * Event description: Buyer input data in CC form fields (if is_empty = true, we should consider it as is_valid = false)
 *
 * @param {checkoutBuyerFillsFormFieldsParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} mandatoryField - field which is going to be filled/ was filled
 * @property {boolean} isValid - chosen field is valid or not after interaction (action_type = 'finish')
 * @property {string} appInstanceId - app_instance_id
 * @property {string} orderId - order id
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {GUID} user_id - uuid
 * @property {boolean} isAutoFill - for action finish is valid result, for action start - change after autofill
 * @property {string} actionType - type of interaction: start/ finish
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {boolean} isEmpty - chosen field is empty or not after interaction (action_type = 'finish')
 * @property {GUID} msid - msid
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<checkoutBuyerFillsFormFieldsParams>} LogObject<checkoutBuyerFillsFormFieldsParams>
 */
export function checkoutBuyerFillsFormFields(params) {
    return { evid: 449, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'braintree3DsForm' event (evid: 450)
 *
 * Event description: 3DS integration for premium flow, which covers only 3DS challenge screen, not modal
 *
 * @param {braintree3DsFormParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {boolean} isOpened - true if page was open , false if closed
 * @property {string} flowType - type of 3ds flow(Should be modal)
 * @property {boolean} isFinished - true if page was success , false if not
 * @property {GUID} orderId - order_id
 * @property {string} paymentMethod - payment method (in case of CreditCard - 3ds challenge)
 * @property {string} result - result of undergoing 3ds flow
 * @property {string} errorReason - error description for result 'failed'
 * @property {string} infoThreeDS - threeDSecureInfo from Braintree
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} version3ds - 3DS version (Should be 2)
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} paymentProvider - payment_provider
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id (should be Premium)
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {boolean} isManualClose - buyer closed modal
 * @property {GUID} transactionId - transaction_id if it exists
 * @return {LogObject<braintree3DsFormParams>} LogObject<braintree3DsFormParams>
 */
export function braintree3DsForm(params) {
    return { evid: 450, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletDeletePaymentMethodButtonRendered' event (evid: 451)
 *
 * Event description: 'Delete Payment Method' button  was rendered
 *
 * @param {myWalletDeletePaymentMethodButtonRenderedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - uuid
 * @property {string} cardId - card_guid
 * @property {string} origin - profile / side menu
 * @property {number} loadTime - time from initial to render
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @return {LogObject<myWalletDeletePaymentMethodButtonRenderedParams>} LogObject<myWalletDeletePaymentMethodButtonRenderedParams>
 */
export function myWalletDeletePaymentMethodButtonRendered(params) {
    return { evid: 451, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletDeletePaymentMethodButtonClicked' event (evid: 452)
 *
 * Event description: My Wallet - 'Delete Payment Method' button clicked.
 *
 * @param {myWalletDeletePaymentMethodButtonClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} result - Yes/ No (in case there are no active subscriptions related to selected CC)
 * @property {GUID} userGuid - uuid
 * @property {string} cardId - card_guid
 * @property {string} stepName - start: click on "Delete CC", finish: click on "Yes/No"
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} savedPaymentMethodId - saved_payment_method_id
 * @property {string} memberId - member id
 * @return {LogObject<myWalletDeletePaymentMethodButtonClickedParams>} LogObject<myWalletDeletePaymentMethodButtonClickedParams>
 */
export function myWalletDeletePaymentMethodButtonClicked(params) {
    return { evid: 452, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletDeletePaymentMethodModalResponse' event (evid: 453)
 *
 * Event description: Once Member chose one of the suggested options in "Delete Payment Method" modal: 1) Click “go to my subscriptions”; 2) Click ‘Cancel’
 *
 * @param {myWalletDeletePaymentMethodModalResponseParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} result - yes: “go to my subscriptions”; no:"cancel"
 * @property {GUID} userGuid - uuid
 * @property {string} cardId - card_guid
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} assetVersion - indication - Cairo or old
 * @property {string} savedPaymentMethodId - saved_payment_method_id
 * @return {LogObject<myWalletDeletePaymentMethodModalResponseParams>} LogObject<myWalletDeletePaymentMethodModalResponseParams>
 */
export function myWalletDeletePaymentMethodModalResponse(params) {
    return { evid: 453, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutPaymentsComponentWasnTShown' event (evid: 454)
 *
 * Event description: Classic Checkout Widget wasn't shown
 *
 * @param {checkoutPaymentsComponentWasnTShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} source - 1.pay with, 2.buyer choose, 3.one payment method
 * @property {string} paymentCategory - CreditCard/Ewallet,offline (not avaliable)
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} buyerComponent - defines new buyer component (null for old, 'react' for new one)
 * @property {string} errorDesc - A technical error string.
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} paymentProvider - com.stripe/net.authorize (not avaliable)
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<checkoutPaymentsComponentWasnTShownParams>} LogObject<checkoutPaymentsComponentWasnTShownParams>
 */
export function checkoutPaymentsComponentWasnTShown(params) {
    return { evid: 454, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutErrorOccurredAfterSubmitOrderClicked' event (evid: 455)
 *
 * Event description: When buyers click "Submit Order", but payments don't finish successfully
 *
 * @param {checkoutErrorOccurredAfterSubmitOrderClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - payment category
 * @property {GUID} orderId - order_id
 * @property {string} errorDesc - description of error code if exists
 * @property {string} errorReason - error code
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} paymentProvider - payment_provider
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {GUID} transactionId - transaction_id if it exists
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 *
 * @return {LogObject<checkoutErrorOccurredAfterSubmitOrderClickedParams>} LogObject<checkoutErrorOccurredAfterSubmitOrderClickedParams>
 */
export function checkoutErrorOccurredAfterSubmitOrderClicked(params) {
    return { evid: 455, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'expressCheckoutMorePaymentOptionsButtonRendered' event (evid: 456)
 *
 * Event description: 'More payment options' button was render to buyer
 *
 * @param {expressCheckoutMorePaymentOptionsButtonRenderedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} origin - page type where widget was loaded
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} currency - currency
 * @property {string} productId - product id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @return {LogObject<expressCheckoutMorePaymentOptionsButtonRenderedParams>} LogObject<expressCheckoutMorePaymentOptionsButtonRenderedParams>
 */
export function expressCheckoutMorePaymentOptionsButtonRendered(params) {
    return { evid: 456, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbErrorDuringValidationFromVertical' event (evid: 457)
 *
 * Event description: Express Checkout Widget error during validation from vertical (once buyer click on payment method on Express Checkout Widget, the validation from vertical will be held). In case of failure, the reason will be sent in the event
 *
 * @param {spbErrorDuringValidationFromVerticalParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} errorReason - Error reason
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc., G-Pay)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} productId - product id
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @return {LogObject<spbErrorDuringValidationFromVerticalParams>} LogObject<spbErrorDuringValidationFromVerticalParams>
 */
export function spbErrorDuringValidationFromVertical(params) {
    return { evid: 457, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ecwGettingPaymentDataFromVertical' event (evid: 458)
 *
 * Event description: Once buyer sees modal with the appropriate payment method and was successfully logged in, we ask vertical for the initial payment data. Any provider, any payment method
 *
 * @param {ecwGettingPaymentDataFromVerticalParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} result - success/failure
 * @property {string} productid - product id where button was clicked
 * @property {string} errorReason - for step = 'result' and result = 'failure': error description
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - payment amount - multiply by 100,000
 * @property {number} taxAmount - for step = 'result' and result = 'success': tax amount - multiply by 100,000
 * @property {string} stepName - step of request: initialization/ result
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {number} shippingAmount - for step = 'result' and result = 'success': shipping amount - multiply by 100,000
 * @property {string} orderType - for step = 'result' and result = 'success': physical / digital
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {number} discountAmount - for step = 'result' and result = 'success': discount amount - multiply by 100,000
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} catalogAppId - A list of verticals which are in this order separated by “ ,“:(stores app id, bookings app_id, ...) .
 *
 * @return {LogObject<ecwGettingPaymentDataFromVerticalParams>} LogObject<ecwGettingPaymentDataFromVerticalParams>
 */
export function ecwGettingPaymentDataFromVertical(params) {
    return { evid: 458, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkoutMitLegalStatementFlow' event (evid: 459)
 *
 * Event description: Applied for MIT trx, once merchant undergo payment flow, he/she is supposed to see legal statement, can check chargeback liability and cards on file and download authorization form
 *
 * @param {checkoutMitLegalStatementFlowParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} creditCardId - null if visitor is not logged in or doesn't have saved cc
 * @property {string} paymentCategory - payment method
 * @property {string} appInstanceId - app_instance_id
 * @property {GUID} orderId - vertical order id
 * @property {GUID} membershipId - membership_id
 * @property {string} intentCharge - intent of charge: one time/ recurring
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {number} amount - money amount - multiply by 100,000
 * @property {boolean} isDefaultLanguage - true/false for step redirect_to_download
 * @property {string} stepName - steps of MIT Legal statement flow: render_statement, click_chargeback, click_download
 * @property {string} language - selected language in dropdown
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} paymentProvider - com.stripe/net.authorize
 * @property {string} currency - payment currency (is sent by vertical)
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {string} searchResults - result of search
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {boolean} merchantInitiated - Checkout Component was rendered for merchant initiated order
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<checkoutMitLegalStatementFlowParams>} LogObject<checkoutMitLegalStatementFlowParams>
 */
export function checkoutMitLegalStatementFlow(params) {
    return { evid: 459, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantTechnicalErrorsInSentry' event (evid: 460)
 *
 * Event description: Errors that appear in Sentry for merchants in the cashier
 *
 * @param {merchantTechnicalErrorsInSentryParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} errorMessage - error message
 * @property {string} loggingPlatform - sentry, etc
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} businessLocation - 2-letter country code of location selected by a user
 * @property {string} sentryEventLink - link to sentry event
 * @property {string} wpAccountId - wix payments account_id
 * @property {string} sentryEventId - setry event id
 * @property {string} pageType - page name
 * @property {GUID} msid - msid
 * @property {string} artifact - artifact where error was catched
 * @property {string} namespace - namespace
 * @return {LogObject<merchantTechnicalErrorsInSentryParams>} LogObject<merchantTechnicalErrorsInSentryParams>
 */
export function merchantTechnicalErrorsInSentry(params) {
    return { evid: 460, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'captchaChallengeFlow' event (evid: 461)
 *
 * Event description: Once bruteforce attack is detected on site, BE sends flag to enable captcha challenge. This event track all action during passing captcha challenge on client side
 *
 * @param {captchaChallengeFlowParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} reTry - true/false
 * @property {boolean} isPartner - is_partner
 * @property {string} flowType - type of captcha flow: modal, new tab, full page redirect
 * @property {GUID} orderId - order_id
 * @property {string} paymentMethod - payment method
 * @property {string} result - result of undergoing 3ds flow
 * @property {boolean} isTokenExisted - true/false: for action captchaSolve
 * @property {string} errorReason - error description for result 'failed'
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} amount - checkout amount
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} paymentProvider - com.stripe/square/wp
 * @property {string} currency - currency
 * @property {string} pageType - intent of showing Checkout Widget: payment/upm component
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} action - all possible action during captcha challenge: captchaLoadStart, captchaLoadEnd, captchaShow, captchaErrorMessage (in case captcha wasn't completed), captchaExpirationMessage, captchaSolve, captchaReset
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} mobileAppVersion - mobile app version
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} mobileAppId - Wix Mobile Apps (Spaces, Owners, Events, Fit etc).
 * @return {LogObject<captchaChallengeFlowParams>} LogObject<captchaChallengeFlowParams>
 */
export function captchaChallengeFlow(params) {
    return { evid: 461, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'ecwPsd2OvercaptureFlow' event (evid: 462)
 *
 * Event description: Transaction was sent to be overcaptured because of changing amount and PSD2 regulation
 *
 * @param {ecwPsd2OvercaptureFlowParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} externalOrderId - provider order id // for SPB = payPal order id
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} transactionStatus - transaction_status
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {GUID} transactionId - transaction_id
 * @return {LogObject<ecwPsd2OvercaptureFlowParams>} LogObject<ecwPsd2OvercaptureFlowParams>
 */
export function ecwPsd2OvercaptureFlow(params) {
    return { evid: 462, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'spbAnyErrorDuringGpayFlow' event (evid: 463)
 *
 * Event description: Fires in case of any other errors which can occur during payment flow through Gpay
 *
 * @param {spbAnyErrorDuringGpayFlowParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} widgetId - the origin of stores’ pages where ECW appears
 * @property {string} cartId - cart id
 * @property {string} errorReason - Error reason
 * @property {string} paymentmethod - payment method in provider (Venmo, PayPal Credit etc.)
 * @property {number} amount - money amount - multiply by 100,000
 * @property {string} checkoutSessionId - unique UoU payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {boolean} requestShipping - If requestShipping={true}, then order is shippable
 * @property {string} sessionId - unique payment session to connect orders and trxs
 * @property {string} paymentProvider - payment_provider that chosen (PayPal, Wixpayments etc.)
 * @property {string} currency - currency
 * @property {string} productId - product id
 * @property {string} appId - app_id
 * @property {GUID} msid - msid
 * @property {string} orderSnapshotId - order_snapshot_id (cashier order)
 * @property {string} transactionId - transaction_id
 * @property {string} siteCurrency - currency which is set on site by merchant
 * @property {string} purchaseFlowId - key to identify ecom flow
 * @return {LogObject<spbAnyErrorDuringGpayFlowParams>} LogObject<spbAnyErrorDuringGpayFlowParams>
 */
export function spbAnyErrorDuringGpayFlow(params) {
    return { evid: 463, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantAcceptPaymentsPageScrolled' event (evid: 464)
 *
 * Event description: Merchant scrolled Accept Payments Page
 *
 * @param {merchantAcceptPaymentsPageScrolledParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {string} paymentCategory - array of connected payment_methods
 * @property {GUID} appInstanceId - app_instance_id
 * @property {number} screenScrolledPercentageStarting - start position (lower bound)
 * @property {string} page - Payments Overview / smart onBoarding welcome / smart onBoarding connect
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} screenResolution - screen resolution in pixels (width x height)
 * @property {number} screenScrolledPercentageEnd - end position (lower bound)
 * @property {number} viewportHeight - viewport height in pixels
 * @property {GUID} appId - TPA guid
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {number} pageHeight - max page Y axis in pixels
 * @property {string} namespace - identifier of user origin
 * @return {LogObject<merchantAcceptPaymentsPageScrolledParams>} LogObject<merchantAcceptPaymentsPageScrolledParams>
 */
export function merchantAcceptPaymentsPageScrolled(params) {
    return { evid: 464, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantTooltipWasShownToUser' event (evid: 465)
 *
 * Event description: Tooltip was shown to user on accept_payments / manage_page / connect_page / third_party_cc_providers
 *
 * @param {merchantTooltipWasShownToUserParams} params - The parameters for this event
 * @property {GUID} visitorId - guid
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - currently connected category
 * @property {string} tooltipName - e.g. connected / not_connected / wix_pos etc
 * @property {GUID} appInstanceId - app_instance_id
 * @property {string} page - page where tooltip appear
 * @property {string} platformName - desktop/oneApp etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} tooltipText - e.g. "These payment options are connected and available to your customers" etc
 * @property {GUID} appid - app_id
 * @property {string} paymentProvider - currently connected provider
 * @property {GUID} msid - msid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantTooltipWasShownToUserParams>} LogObject<merchantTooltipWasShownToUserParams>
 */
export function merchantTooltipWasShownToUser(params) {
    return { evid: 465, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantFullStorySession' event (evid: 466)
 *
 * Event description: Merchant FullStory session recorded
 *
 * @param {merchantFullStorySessionParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came to AP
 * @property {boolean} isPartner - is_partner
 * @property {string} merchantResolvedCountry - the country we get from the server
 * @property {string} page - Payments Overview / smart onBoarding welcome / smart onBoarding connect
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {GUID} userGuid - user_guid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} origin - place user came from
 * @property {string} fullstoryUrl - url of FullStory recorded session
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @property {string} namespace - identifier of user origin
 * @property {string} experiment - spec name
 * @return {LogObject<merchantFullStorySessionParams>} LogObject<merchantFullStorySessionParams>
 */
export function merchantFullStorySession(params) {
    return { evid: 466, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantToastBannerShown' event (evid: 467)
 *
 * Event description: Toast banner was shown to the user at Accept payment page
 *
 * @param {merchantToastBannerShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} paymentCategory - creditCard,paypal
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} paymentProvider - stripe,paypal,eWay
 * @property {string} place - place where banner is located - accept_payment_top_page,connect_payment_method_modal
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {GUID} msid - msid
 * @property {string} bannerName - e.g. back-to-getting-paid
 * @property {string} namespace - namespace
 * @property {string} notificationType - notification name
 * @return {LogObject<merchantToastBannerShownParams>} LogObject<merchantToastBannerShownParams>
 */
export function merchantToastBannerShown(params) {
    return { evid: 467, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnToastBanner' event (evid: 468)
 *
 * Event description: User clicks on Toast at Accept Payment Page
 *
 * @param {merchantClickOnToastBannerParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor id
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is partner
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} notificationType - notification name
 * @property {string} cta - upgrade, close
 * @property {string} pageType - page where banner was shown - accept_payment/manage_page etc
 * @property {GUID} msid - msid
 * @property {string} bannerName - e.g. back to getting paid
 * @property {string} namespace - namespace
 * @return {LogObject<merchantClickOnToastBannerParams>} LogObject<merchantClickOnToastBannerParams>
 */
export function merchantClickOnToastBanner(params) {
    return { evid: 468, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantDataScienceBusinessTypePrediction' event (evid: 469)
 *
 * Event description: flag that indicates whether the merchant sells prohibited goods or services, Wix Payments-related.
 * events are fired when users first open payments page
 *
 * @param {merchantDataScienceBusinessTypePredictionParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} offeredPaymentCategory - array of offered payment_methods
 * @property {string} page - accept_payments, getting_paid
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {boolean} isEligible - true is merchanteligible to connect WP in terms of selling not restricted goods
 * @property {string} user_agent - user_agent
 * @property {GUID} msid - synthetic msid for sites_by_src
 * @return {LogObject<merchantDataScienceBusinessTypePredictionParams>} LogObject<merchantDataScienceBusinessTypePredictionParams>
 */
export function merchantDataScienceBusinessTypePrediction(params) {
    return { evid: 469, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'razorpayPaymentPageForPremiumFlowIndiaSrc64Evid470' event (evid: 470)
 *
 * Event description: Razorpay Payment Page for premium flow [India]
 *
 * @param {razorpayPaymentPageForPremiumFlowIndiaSrc64Evid470Params} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {string} orderId - order_id
 * @property {string} paymentMethod - payment method
 * @property {string} errorReason - error description for result 'failed'
 * @property {string} orderSnapshotId - order_snapshot_id
 * @property {string} checkoutSessionId - unique payment session since widget/ SPB/ ApplePay loads till order placement, it is needed to follow UoU's flow and connection between order&trx
 * @property {string} paymentProvider - payment_provider
 * @property {string} appId - app_id (should be Premium)
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {GUID} transactionId - transaction_id if it exists
 * @property {string} keyId - key_id
 * @property {string} customerId - Unique identifier of the customer
 * @property {string} stepName - expected steps: initialization >> opened >> closed manually/ finished successfully/ failed
 * @return {LogObject<razorpayPaymentPageForPremiumFlowIndiaSrc64Evid470Params>} LogObject<razorpayPaymentPageForPremiumFlowIndiaSrc64Evid470Params>
 */
export function razorpayPaymentPageForPremiumFlowIndiaSrc64Evid470(params) {
    return { evid: 470, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletManageButtonClicked' event (evid: 471)
 *
 * Event description: 'Manage' Button Clicked on specific saved payment method on wallet page
 *
 * @param {myWalletManageButtonClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - user_guid
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} memberId - member id
 * @property {string} savedPaymentMethodId - saved payment method id
 * @property {string} paymentMethod - CreditCard/ PayPal
 * @property {boolean} isPrimary - true/ false
 * @property {boolean} isExpired - true/ false
 * @return {LogObject<myWalletManageButtonClickedParams>} LogObject<myWalletManageButtonClickedParams>
 */
export function myWalletManageButtonClicked(params) {
    return { evid: 471, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletRemovePaymentMethodButtonClicked' event (evid: 472)
 *
 * Event description: "Remove Payment Method"  Button Clicked on specific saved payment method on wallet page
 *
 * @param {myWalletRemovePaymentMethodButtonClickedParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - user_guid
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget:
 * @property {GUID} siteMemberId - member_id
 * @property {string} memberId - member id
 * @property {string} savedPaymentMethodId - saved payment method id
 * @property {string} paymentMethod - CreditCard/ PayPal
 * @property {boolean} isPrimary - true/ false
 * @property {boolean} isExpired - true/ false
 * @return {LogObject<myWalletRemovePaymentMethodButtonClickedParams>} LogObject<myWalletRemovePaymentMethodButtonClickedParams>
 */
export function myWalletRemovePaymentMethodButtonClicked(params) {
    return { evid: 472, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletToastMessageShown' event (evid: 473)
 *
 * Event description: Toast message was shown on wallet page as result of action
 *
 * @param {myWalletToastMessageShownParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {GUID} member_id - member_id
 * @property {boolean} isPartner - is_partner
 * @property {GUID} userGuid - user_guid
 * @property {GUID} memberId - member_id
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @property {string} toastText - actual text of toast message
 * @property {string} toastType - Added/ Removed/Error
 * @return {LogObject<myWalletToastMessageShownParams>} LogObject<myWalletToastMessageShownParams>
 */
export function myWalletToastMessageShown(params) {
    return { evid: 473, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myWalletAppSettingsDesignTabTabInDesignSettings' event (evid: 474)
 *
 * Event description: select particular tab in the design settings menu
 *
 * @param {myWalletAppSettingsDesignTabTabInDesignSettingsParams} params - The parameters for this event
 * @property {string} multilingual - multilingual lang
 * @property {string} tab - text_fonts/ buttons/ badges/ dividers/ backgrounds
 * @property {GUID} userGuid - uuid
 * @property {string} origin - editor / ...
 * @property {GUID} msid - msid
 * @property {GUID} appSessionId - session_id by the app which invokes checkout widget
 * @return {LogObject<myWalletAppSettingsDesignTabTabInDesignSettingsParams>} LogObject<myWalletAppSettingsDesignTabTabInDesignSettingsParams>
 */
export function myWalletAppSettingsDesignTabTabInDesignSettings(params) {
    return { evid: 474, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cashAppPaymentFlow' event (evid: 475)
 *
 * Event description: This event contains info about different steps of CashApp payment flow after 'Click on Pay':
 * 1. modal_opened
 * 2. modal_crushed
 * 3.buyer_interaction (qr code scanned, payment approved or modal closed)
 * 4.response_to_be
 *
 * @param {cashAppPaymentFlowParams} params - The parameters for this event
 * @property {GUID} visitorId - visitor_id
 * @property {string} paymentMethod - payment_method
 * @property {string} errorReason - error description
 * @property {string} orderSnapshotId - cashier order
 * @property {string} amount - checkout amount
 * @property {string} currency - currency
 * @property {string} checkoutSessionId - unique UoU payment session since widget loads till order placement
 * @property {string} paymentProvider - square/stripe/wixpay
 * @property {string} appId - app_id
 * @property {string} msid - msid
 * @property {GUID} transactionId - transaction_id
 * @property {string} stepName - step of CashApp payment flow: modal_opened or modal_crushed >> buyer_interaction (qr code scanned, payment approved or modal closed) >> response_to_be
 * @property {string} result - sucess/ failure/ closed
 * @property {string} token - token if payment is approved
 * @return {LogObject<cashAppPaymentFlowParams>} LogObject<cashAppPaymentFlowParams>
 */
export function cashAppPaymentFlow(params) {
    return { evid: 475, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnButtonToSelectPayPalOnSelectorPageSrc64Evid5' event (evid: 5)
 *
 * Event description: Merchant Clicks on tile to choose between Wix Payments PayPal and Cashier PayPal
 *
 * @param {merchantClickOnButtonToSelectPayPalOnSelectorPageSrc64Evid5Params} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} avalableCategory - wixpay_paypal, cashier_paypal
 * @property {string} selectedCategory - wixpay_paypal or cashier_paypal
 * @property {string} pageType - paypal_selector_page
 * @property {GUID} msid - msid
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} screenResolution - user's screen resolution
 * @property {string} preselectedPaymentMethods - wixpay_paypal or cashier_paypal
 * @property {string} businessLocation - location that user selected on payments page
 * @return {LogObject<merchantClickOnButtonToSelectPayPalOnSelectorPageSrc64Evid5Params>} LogObject<merchantClickOnButtonToSelectPayPalOnSelectorPageSrc64Evid5Params>
 */
export function merchantClickOnButtonToSelectPayPalOnSelectorPageSrc64Evid5(params) {
    return { evid: 5, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'badgeWasShownSrc64Evid501' event (evid: 501)
 *
 * Event description: Triggers when user saw badge on a page
 *
 * @param {badgeWasShownSrc64Evid501Params} params - The parameters for this event
 * @property {string} type - badge type
 * @property {string} pageType - page where badge appeared
 * @property {GUID} msid - msid
 * @property {string} businessLocation - businessLocation
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} platformName - desktop/oneApp etc
 * @property {boolean} isPartner - is_partner
 * @return {LogObject<badgeWasShownSrc64Evid501Params>} LogObject<badgeWasShownSrc64Evid501Params>
 */
export function badgeWasShownSrc64Evid501(params) {
    return { evid: 501, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userClickedOnCtaInBadgeSrc64Evid502' event (evid: 502)
 *
 * Event description: Triggers when user clicked on badge on accept_payments / manage_page
 *
 * @param {userClickedOnCtaInBadgeSrc64Evid502Params} params - The parameters for this event
 * @property {string} type - badge type
 * @property {string} pageType - page where badge appeared
 * @property {GUID} msid - msid
 * @property {string} app_id - app_id
 * @property {string} appInstance - app_instance_id
 * @property {string} businessLocation - businessLocation
 * @property {string} referralInfo - place from which user came
 * @property {string} viewmode - siteLevel / accountLevel
 * @property {string} platformName - desktop/oneApp etc
 * @property {boolean} isPartner - is_partner
 * @property {string} cta - click
 * @return {LogObject<userClickedOnCtaInBadgeSrc64Evid502Params>} LogObject<userClickedOnCtaInBadgeSrc64Evid502Params>
 */
export function userClickedOnCtaInBadgeSrc64Evid502(params) {
    return { evid: 502, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantClickOnPaymentMethodIconSrc64Evid6' event (evid: 6)
 *
 * Event description: Merchant Clicks on iconsof Payment method
 *
 * @param {merchantClickOnPaymentMethodIconSrc64Evid6Params} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - is_partner
 * @property {string} pageType - paypal_selector_page, accept_payments, manage_page
 * @property {string} msid - msid
 * @property {string} platformName - desktop/oneApp/partner etc
 * @property {string} paymentCategory - which PM icon was clicked:payPal, venmo, applePay etc
 * @property {string} screenResolution - user's screen resolution
 * @property {string} businessLocation - location that user selected on payments page
 * @return {LogObject<merchantClickOnPaymentMethodIconSrc64Evid6Params>} LogObject<merchantClickOnPaymentMethodIconSrc64Evid6Params>
 */
export function merchantClickOnPaymentMethodIconSrc64Evid6(params) {
    return { evid: 6, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaWelcomePageLoadingStartSrc64Evid600' event (evid: 600)
 *
 * Event description: MCA - load welcome page started
 *
 * @param {mcaWelcomePageLoadingStartSrc64Evid600Params} params - The parameters for this event
 * @property {string} refferalUrl - a place from where user comes to this page
 * @property {GUID} accountId - wix account if
 * @property {string} wpAccountId - wp account id
 * @property {GUID} msid - msid
 * @return {LogObject<mcaWelcomePageLoadingStartSrc64Evid600Params>} LogObject<mcaWelcomePageLoadingStartSrc64Evid600Params>
 */
export function mcaWelcomePageLoadingStartSrc64Evid600(params) {
    return { evid: 600, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaWelcomePageLoadingStartCopySrc64Evid601' event (evid: 601)
 *
 * Event description: MCA - load welcome page loaded
 *
 * @param {mcaWelcomePageLoadingStartCopySrc64Evid601Params} params - The parameters for this event
 * @property {string} refferalUrl - a place from where user comes to this page
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {boolean} offerState - true if we have an offer, false if we don't have an offer
 * @property {number} current_offer - amount of offer that we show on UI
 * @property {string} pageCategory - New (no submitted request)/
 * Review (request in review)/
 * Approved (request approved)/
 * Decline (request declined)
 * @return {LogObject<mcaWelcomePageLoadingStartCopySrc64Evid601Params>} LogObject<mcaWelcomePageLoadingStartCopySrc64Evid601Params>
 */
export function mcaWelcomePageLoadingStartCopySrc64Evid601(params) {
    return { evid: 601, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaWelcomePageClickOnCtaSrc64Evid602' event (evid: 602)
 *
 * Event description: MCA - click cta on welcome page
 *
 * @param {mcaWelcomePageClickOnCtaSrc64Evid602Params} params - The parameters for this event
 * @property {string} refferalUrl - a place from where user comes to this page
 * @property {string} wpAccountId - wp account id
 * @property {string} ctaName - See options/How it works /GoToDashboard/ Cancel
 * @property {string} pageCategory - New (no submitted request)/
 * Review (request in review)/
 * Approved (request approved)/
 * Decline (request declined)
 * @property {GUID} msid - msid
 * @return {LogObject<mcaWelcomePageClickOnCtaSrc64Evid602Params>} LogObject<mcaWelcomePageClickOnCtaSrc64Evid602Params>
 */
export function mcaWelcomePageClickOnCtaSrc64Evid602(params) {
    return { evid: 602, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaStep1LoadingFinishedSrc64Evid603' event (evid: 603)
 *
 * Event description: MCA - loaded step 1
 *
 * @param {mcaStep1LoadingFinishedSrc64Evid603Params} params - The parameters for this event
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {number} MaxOfferAmount - High offer amount (top)
 * @property {number} MidOfferAmount - Middle offer amount
 * @property {number} MinOfferAmount - Minimal offer amount (from custom field description)
 * @property {GUID} msid - msid
 * @return {LogObject<mcaStep1LoadingFinishedSrc64Evid603Params>} LogObject<mcaStep1LoadingFinishedSrc64Evid603Params>
 */
export function mcaStep1LoadingFinishedSrc64Evid603(params) {
    return { evid: 603, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaStep1OfferSelectedSrc64Evid604' event (evid: 604)
 *
 * Event description: MCA - step 1 offer selected
 *
 * @param {mcaStep1OfferSelectedSrc64Evid604Params} params - The parameters for this event
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {number} MaxOfferAmount - High offer amount (top)
 * @property {number} MidOfferAmount - Middle offer amount
 * @property {number} MinOfferAmount - Minimal offer amount (from custom field description)
 * @property {string} selectedOffer - High/Mid/Custom
 * @property {number} selectedAmount - $ that user want to take
 * @property {GUID} msid - msid
 * @return {LogObject<mcaStep1OfferSelectedSrc64Evid604Params>} LogObject<mcaStep1OfferSelectedSrc64Evid604Params>
 */
export function mcaStep1OfferSelectedSrc64Evid604(params) {
    return { evid: 604, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaStep2LoadedSrc64Evid605' event (evid: 605)
 *
 * Event description: MCA - step 2 loaded
 *
 * @param {mcaStep2LoadedSrc64Evid605Params} params - The parameters for this event
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {string} selectedOffer - High/Mid/Custom
 * @property {number} selectedAmount - $ that user want to take
 * @property {GUID} msid - msid
 * @return {LogObject<mcaStep2LoadedSrc64Evid605Params>} LogObject<mcaStep2LoadedSrc64Evid605Params>
 */
export function mcaStep2LoadedSrc64Evid605(params) {
    return { evid: 605, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaStep2ClickOnCtaSrc64Evid606' event (evid: 606)
 *
 * Event description: MCA - click cta on Step 2
 *
 * @param {mcaStep2ClickOnCtaSrc64Evid606Params} params - The parameters for this event
 * @property {string} refferalUrl - a place from where user comes to this page
 * @property {string} wpAccountId - wp account id
 * @property {string} ctaName - Confirm/Fee/Total/Remittance Rate/Period/Terms/MCA Agreement
 * @property {GUID} msid - msid
 * @return {LogObject<mcaStep2ClickOnCtaSrc64Evid606Params>} LogObject<mcaStep2ClickOnCtaSrc64Evid606Params>
 */
export function mcaStep2ClickOnCtaSrc64Evid606(params) {
    return { evid: 606, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaStep2ClickOnCtaCopySrc64Evid607' event (evid: 607)
 *
 * Event description: MCA - MCA Agreement page
 *
 * @param {mcaStep2ClickOnCtaCopySrc64Evid607Params} params - The parameters for this event
 * @property {string} refferalUrl - a place from where user comes to this page
 * @property {string} wpAccountId - wp account id
 * @property {GUID} msid - msid
 * @return {LogObject<mcaStep2ClickOnCtaCopySrc64Evid607Params>} LogObject<mcaStep2ClickOnCtaCopySrc64Evid607Params>
 */
export function mcaStep2ClickOnCtaCopySrc64Evid607(params) {
    return { evid: 607, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaStep2AgreementPageCtaSrc64Evid608' event (evid: 608)
 *
 * Event description: MCA - MCA Agreement page cta clicked
 *
 * @param {mcaStep2AgreementPageCtaSrc64Evid608Params} params - The parameters for this event
 * @property {string} refferalUrl - a place from where user comes to this page
 * @property {string} wpAccountId - wp account id
 * @property {string} ctaName - Print/Cancel/Agree
 * @property {GUID} msid - msid
 * @return {LogObject<mcaStep2AgreementPageCtaSrc64Evid608Params>} LogObject<mcaStep2AgreementPageCtaSrc64Evid608Params>
 */
export function mcaStep2AgreementPageCtaSrc64Evid608(params) {
    return { evid: 608, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaStep2ThankYouPageSrc64Evid609' event (evid: 609)
 *
 * Event description: MCA - MCA request submitted thank you page loaded
 *
 * @param {mcaStep2ThankYouPageSrc64Evid609Params} params - The parameters for this event
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {string} selectedOffer - High/Mid/Custom
 * @property {number} selectedAmount - $ that user want to take
 * @property {GUID} msid - msid
 * @return {LogObject<mcaStep2ThankYouPageSrc64Evid609Params>} LogObject<mcaStep2ThankYouPageSrc64Evid609Params>
 */
export function mcaStep2ThankYouPageSrc64Evid609(params) {
    return { evid: 609, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaCancelPopupShownSrc64Evid610' event (evid: 610)
 *
 * Event description: MCA - cancel popup shown
 *
 * @param {mcaCancelPopupShownSrc64Evid610Params} params - The parameters for this event
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {number} selectedAmount - $ that user want to take
 * @property {GUID} msid - msid
 * @return {LogObject<mcaCancelPopupShownSrc64Evid610Params>} LogObject<mcaCancelPopupShownSrc64Evid610Params>
 */
export function mcaCancelPopupShownSrc64Evid610(params) {
    return { evid: 610, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaCancelPopupClickOnCtaSrc64Evid611' event (evid: 611)
 *
 * Event description: MCA - cancel popup click on CTA
 *
 * @param {mcaCancelPopupClickOnCtaSrc64Evid611Params} params - The parameters for this event
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {number} selectedAmount - $ that user want to take
 * @property {string} ctaName - Keep/Cancel
 * @property {GUID} msid - msid
 * @return {LogObject<mcaCancelPopupClickOnCtaSrc64Evid611Params>} LogObject<mcaCancelPopupClickOnCtaSrc64Evid611Params>
 */
export function mcaCancelPopupClickOnCtaSrc64Evid611(params) {
    return { evid: 611, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaWelcomePageLoadingFinishedSrc64Evid612' event (evid: 612)
 *
 * Event description: MCA - load welcome page loaded
 *
 * @param {mcaWelcomePageLoadingFinishedSrc64Evid612Params} params - The parameters for this event
 * @property {string} refferalUrl - a place from where user comes to this page
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {boolean} offerState - true if we have an offer, false if we don't have an offer
 * @property {number} current_offer - amount of offer that we show on UI
 * @property {string} pageCategory - New (no submitted request)/
 * Review (request in review)/
 * Approved (request approved)/
 * Decline (request declined)
 * @property {GUID} msid - msid
 * @return {LogObject<mcaWelcomePageLoadingFinishedSrc64Evid612Params>} LogObject<mcaWelcomePageLoadingFinishedSrc64Evid612Params>
 */
export function mcaWelcomePageLoadingFinishedSrc64Evid612(params) {
    return { evid: 612, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaWelcomePageLoadingFailedSrc64Evid613' event (evid: 613)
 *
 * Event description: MCA - load welcome page failed to load
 *
 * @param {mcaWelcomePageLoadingFailedSrc64Evid613Params} params - The parameters for this event
 * @property {string} refferalUrl - a place from where user comes to this page
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {boolean} offerState - true if we have an offer, false if we don't have an offer
 * @property {number} current_offer - amount of offer that we show on UI
 * @property {string} errorDesc - error description
 * @property {string} pageCategory - New (no submitted request)/
 * Review (request in review)/
 * Approved (request approved)/
 * Decline (request declined)
 * @property {GUID} msid - msid
 * @return {LogObject<mcaWelcomePageLoadingFailedSrc64Evid613Params>} LogObject<mcaWelcomePageLoadingFailedSrc64Evid613Params>
 */
export function mcaWelcomePageLoadingFailedSrc64Evid613(params) {
    return { evid: 613, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaStep1CustomAmountLoadingFinishedSrc64Evid614' event (evid: 614)
 *
 * Event description: MCA - loaded step 1 custom amount
 *
 * @param {mcaStep1CustomAmountLoadingFinishedSrc64Evid614Params} params - The parameters for this event
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {number} MaxOfferAmount - High offer amount (top)
 * @property {number} MidOfferAmount - Middle offer amount
 * @property {number} MinOfferAmount - Minimal offer amount (from custom field description)
 * @property {GUID} msid - msid
 * @return {LogObject<mcaStep1CustomAmountLoadingFinishedSrc64Evid614Params>} LogObject<mcaStep1CustomAmountLoadingFinishedSrc64Evid614Params>
 */
export function mcaStep1CustomAmountLoadingFinishedSrc64Evid614(params) {
    return { evid: 614, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaWelcomePageLoadingStartCopySrc64Evid650' event (evid: 650)
 *
 * Event description: MCA dashboard loading start
 *
 * @param {mcaWelcomePageLoadingStartCopySrc64Evid650Params} params - The parameters for this event
 * @property {string} refferalUrl - a place from where user comes to this page
 * @property {string} wpAccountId - wp account id
 * @property {GUID} msid - msid
 * @return {LogObject<mcaWelcomePageLoadingStartCopySrc64Evid650Params>} LogObject<mcaWelcomePageLoadingStartCopySrc64Evid650Params>
 */
export function mcaWelcomePageLoadingStartCopySrc64Evid650(params) {
    return { evid: 650, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaDashboardLoadedSrc64Evid651' event (evid: 651)
 *
 * Event description: MCA dashboard loaded
 *
 * @param {mcaDashboardLoadedSrc64Evid651Params} params - The parameters for this event
 * @property {string} wpAccountId - wp account id
 * @property {number} loadTime - time in s to load page
 * @property {string} offerId - offer id from BE
 * @property {GUID} msid - msid
 * @return {LogObject<mcaDashboardLoadedSrc64Evid651Params>} LogObject<mcaDashboardLoadedSrc64Evid651Params>
 */
export function mcaDashboardLoadedSrc64Evid651(params) {
    return { evid: 651, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'mcaDashboardClickOnCtaSrc64Evid652' event (evid: 652)
 *
 * Event description: MCA - click cta on dashboard page
 *
 * @param {mcaDashboardClickOnCtaSrc64Evid652Params} params - The parameters for this event
 * @property {string} refferalUrl - a place from where user comes to this page
 * @property {string} wpAccountId - wp account id
 * @property {string} ctaName - Show MCA details\ Overview\ History \ View Settlement Report
 * @property {GUID} msid - msid
 * @return {LogObject<mcaDashboardClickOnCtaSrc64Evid652Params>} LogObject<mcaDashboardClickOnCtaSrc64Evid652Params>
 */
export function mcaDashboardClickOnCtaSrc64Evid652(params) {
    return { evid: 652, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'multicurrencySettingsUpdatedSrc64Evid701' event (evid: 701)
 *
 * Event description: Merchant Enables or Disables Multicurrency
 *
 * @param {multicurrencySettingsUpdatedSrc64Evid701Params} params - The parameters for this event
 * @property {string} msid - msid
 * @property {GUID} uuid - uuid
 * @property {boolean} restrictedByMerchant - if MP is enabled or disabled
 * @return {LogObject<multicurrencySettingsUpdatedSrc64Evid701Params>} LogObject<multicurrencySettingsUpdatedSrc64Evid701Params>
 */
export function multicurrencySettingsUpdatedSrc64Evid701(params) {
    return { evid: 701, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'entityWasAddedToThePopulationSrc64Evid801' event (evid: 801)
 *
 * Event description: A user or a site was added to the population. Triggered when entity was added to the population by backend.
 *
 * @param {entityWasAddedToThePopulationSrc64Evid801Params} params - The parameters for this event
 * @property {string} msid - wix msid
 * @property {string} uuid - wix uuid
 * @property {string} populationId - population_id
 * @return {LogObject<entityWasAddedToThePopulationSrc64Evid801Params>} LogObject<entityWasAddedToThePopulationSrc64Evid801Params>
 */
export function entityWasAddedToThePopulationSrc64Evid801(params) {
    return { evid: 801, src: 64, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentModuleCreditCardsAddClicked' event (evid: 375)
 *
 * Event description: add clicked
 *
 * @param {paymentModuleCreditCardsAddClickedParams} params - The parameters for this event
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {number} ts - ts
 * @property {string} moduleName - module_name
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {string} origin - add first / add more / checkout
 * @property {GUID} contactId - contact_id
 * @property {string} appNameInstance - app_name_instance
 * @property {string} msid - msid
 * @return {LogObject<paymentModuleCreditCardsAddClickedParams>} LogObject<paymentModuleCreditCardsAddClickedParams>
 */
export function paymentModuleCreditCardsAddClicked(params) {
    return { evid: 375, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentModuleCreditCardsSaveClicked' event (evid: 376)
 *
 * Event description: 'Save' button was clicked (from both screen 'Saved CC' and 'Checkout')
 *
 * @param {paymentModuleCreditCardsSaveClickedParams} params - The parameters for this event
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {string} mandatoryFieldsList - list of mandatory fields for user to fulfill on editing CC
 * @property {number} ts - ts
 * @property {string} moduleName - module_name
 * @property {GUID} cardId - card_id
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {boolean} saveDetails - when user keeps toggle on in check out flow
 * @property {string} origin - add first / add more / checkout / edit
 * @property {GUID} contactId - contact_id
 * @property {string} appNameInstance - app_name_instance
 * @property {string} msid - msid
 * @return {LogObject<paymentModuleCreditCardsSaveClickedParams>} LogObject<paymentModuleCreditCardsSaveClickedParams>
 */
export function paymentModuleCreditCardsSaveClicked(params) {
    return { evid: 376, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentModuleCreditCardsShowOptionsClicked' event (evid: 377)
 *
 * Event description: show options clicked
 *
 * @param {paymentModuleCreditCardsShowOptionsClickedParams} params - The parameters for this event
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {number} ts - ts
 * @property {string} moduleName - module_name
 * @property {GUID} cardId - card_id
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {GUID} contactId - contact_id
 * @property {string} appNameInstance - app_name_instance
 * @property {string} msid - msid
 * @return {LogObject<paymentModuleCreditCardsShowOptionsClickedParams>} LogObject<paymentModuleCreditCardsShowOptionsClickedParams>
 */
export function paymentModuleCreditCardsShowOptionsClicked(params) {
    return { evid: 377, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentModuleCreditCardsMenuOptionClicked' event (evid: 378)
 *
 * Event description: menu option clicked
 *
 * @param {paymentModuleCreditCardsMenuOptionClickedParams} params - The parameters for this event
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {number} ts - ts
 * @property {string} moduleName - module_name
 * @property {GUID} cardId - card_id
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {GUID} contactId - contact_id
 * @property {string} appNameInstance - app_name_instance
 * @property {string} optionName - make primary / delete / edit
 * @property {string} msid - msid
 * @return {LogObject<paymentModuleCreditCardsMenuOptionClickedParams>} LogObject<paymentModuleCreditCardsMenuOptionClickedParams>
 */
export function paymentModuleCreditCardsMenuOptionClicked(params) {
    return { evid: 378, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentModuleCreditCardsConfirmDeleteClicked' event (evid: 380)
 *
 * Event description: confirm delete clicked
 *
 * @param {paymentModuleCreditCardsConfirmDeleteClickedParams} params - The parameters for this event
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {number} ts - ts
 * @property {string} moduleName - module_name
 * @property {GUID} cardId - card_id
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {GUID} contactId - contact_id
 * @property {string} appNameInstance - app_name_instance
 * @property {string} msid - msid
 * @return {LogObject<paymentModuleCreditCardsConfirmDeleteClickedParams>} LogObject<paymentModuleCreditCardsConfirmDeleteClickedParams>
 */
export function paymentModuleCreditCardsConfirmDeleteClicked(params) {
    return { evid: 380, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'moveInPaymentFunnel' event (evid: 381)
 *
 * Event description: pay clicked
 *
 * @param {moveInPaymentFunnelParams} params - The parameters for this event
 * @property {boolean} hasSavedCard - does user have saved cc
 * @property {string} paymentCategory - payment_category: creditCard, payPal etc
 * @property {boolean} saveCreditCard - save_credit_card
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {GUID} orderId - cashier order_snapshot_id
 * @property {number} ts - ts
 * @property {string} paymentAgreementId - payment_agreement_id
 * @property {string} moduleName - module_name
 * @property {GUID} cardId - previously saved credit_card_id  when known
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} paymentMethodType - For credit card- credit card type:jcb mastercard discover isracard maestro visa amex diners
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {string} stepName - checkout / save credit card / mandatory fields
 * @property {GUID} verticalid - vertical order id
 * @property {GUID} contactId - contact_id
 * @property {string} paymentProvider - merchant's connected provider to pay with
 * @property {string} app_id - vertical id
 * @property {string} appNameInstance - app_name_instance
 * @property {GUID} clubId - when known
 * @property {GUID} transactionId - transaction_id
 * @property {string} stepNext - screen where buyer will be leaded to after clicking on next in payment flow
 * @property {string} msid - msid
 * @return {LogObject<moveInPaymentFunnelParams>} LogObject<moveInPaymentFunnelParams>
 */
export function moveInPaymentFunnel(params) {
    return { evid: 381, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentModulePaymentThankYouPageShown' event (evid: 382)
 *
 * Event description: thank you page shown
 *
 * @param {paymentModulePaymentThankYouPageShownParams} params - The parameters for this event
 * @property {string} paymentCategory - payPal, creditCard
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {string} paymentStatus - success / error / pending
 * @property {GUID} orderId - TPA order id
 * @property {number} ts - ts
 * @property {string} moduleName - module_name
 * @property {string} cardId - card_id
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {GUID} contactId - contact_id
 * @property {string} paymentProvider - payPal, com.stripe
 * @property {string} appNameInstance - app_name_instance
 * @property {string} msid - msid
 * @return {LogObject<paymentModulePaymentThankYouPageShownParams>} LogObject<paymentModulePaymentThankYouPageShownParams>
 */
export function paymentModulePaymentThankYouPageShown(params) {
    return { evid: 382, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'iapStartedProcess' event (evid: 4576)
 *
 * Event description: When the user started the in-app purchase
 *
 * @param {iapStartedProcessParams} params - The parameters for this event
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {string} orderId - order id
 * @property {number} ts - ts
 * @property {string} moduleName - module_name
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {GUID} contactId - contact_id
 * @property {string} appNameInstance - app_name_instance
 * @property {string} msid - msid
 * @return {LogObject<iapStartedProcessParams>} LogObject<iapStartedProcessParams>
 */
export function iapStartedProcess(params) {
    return { evid: 4576, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'iapFinishedSuccessfully' event (evid: 4577)
 *
 * Event description: When the user finished the in-app purchase
 *
 * @param {iapFinishedSuccessfullyParams} params - The parameters for this event
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {string} orderId - order id
 * @property {number} ts - ts
 * @property {string} moduleName - module_name
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {GUID} contactId - contact_id
 * @property {string} appNameInstance - app_name_instance
 * @property {string} msid - msid
 * @return {LogObject<iapFinishedSuccessfullyParams>} LogObject<iapFinishedSuccessfullyParams>
 */
export function iapFinishedSuccessfully(params) {
    return { evid: 4577, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentModuleCreditCardsCcSavedSuccessfully' event (evid: 4610)
 *
 * Event description: Credit Card was successfully saved (from both screen 'Saved CC' and 'Checkout')
 *
 * @param {paymentModuleCreditCardsCcSavedSuccessfullyParams} params - The parameters for this event
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {number} ts - ts
 * @property {string} moduleName - module_name
 * @property {GUID} cardId - card_id
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {string} origin - add first / add more / checkout / edit
 * @property {GUID} contactId - contact_id
 * @property {string} appNameInstance - app_name_instance
 * @property {string} msid - msid
 * @return {LogObject<paymentModuleCreditCardsCcSavedSuccessfullyParams>} LogObject<paymentModuleCreditCardsCcSavedSuccessfullyParams>
 */
export function paymentModuleCreditCardsCcSavedSuccessfully(params) {
    return { evid: 4610, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'paymentModuleCreditCardsCreditCardsScreenShown' event (evid: 4611)
 *
 * Event description: 'Credit Cards'  screen (wallet) was shown to buyer with previously saved CCs if they exist. Also there is ability yo save a new one.
 *
 * @param {paymentModuleCreditCardsCreditCardsScreenShownParams} params - The parameters for this event
 * @property {boolean} isPreviewMode - is_preview_mode
 * @property {number} ts - ts
 * @property {boolean} hasCreditCard - true/false
 * @property {string} moduleName - module_name
 * @property {boolean} isSplitInstance - is_split_instance
 * @property {string} moduleVersion - module_version
 * @property {string} user_agent - user_agent
 * @property {GUID} contactId - contact_id
 * @property {string} paymentProvider - payment provider
 * @property {string} appNameInstance - app_name_instance
 * @property {string} msid - msid
 * @return {LogObject<paymentModuleCreditCardsCreditCardsScreenShownParams>} LogObject<paymentModuleCreditCardsCreditCardsScreenShownParams>
 */
export function paymentModuleCreditCardsCreditCardsScreenShown(params) {
    return { evid: 4611, src: 67, endpoint: '', params };
}
/**
 * A function to create a log object for the 'obUserUseToggleForSupportPhoneSrc85Evid1' event (evid: 1)
 *
 * Event description: The user chooses if his tel number is used for Contact support in KYC from
 *
 * @param {obUserUseToggleForSupportPhoneSrc85Evid1Params} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} telInUse - true if 'Yes' for telephone user for Contact support
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @return {LogObject<obUserUseToggleForSupportPhoneSrc85Evid1Params>} LogObject<obUserUseToggleForSupportPhoneSrc85Evid1Params>
 */
export function obUserUseToggleForSupportPhoneSrc85Evid1(params) {
    return { evid: 1, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionRequest' event (evid: 1000)
 *
 * Event description: request to the Transaction Service. IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {transactionRequestParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {string} externalOrderId - order id
 * @property {string} externalMerchantId - wix user guid
 * @property {GUID} uuid - wix uuid
 * @property {string} mandateStatus - status of mandate (applicable for Stripe India)
 * @property {GUID} visitor_id - visitor_id
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount of the transaction - multiply by 100,000
 * @property {GUID} externalAccountId - msid
 * @property {GUID} pricingPlanId - fees pricing plan
 * @property {string} operation - name of the operation, i.e. Create, Capture, Refund, Retrieve
 * @property {string} additionalData - json data
 * @property {string} wpGeo - wp_geo
 * @property {string} currency - currency
 * @property {string} adapterName - adapter
 * @property {string} externalTransactionId - Cashier trans
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} accountProfileId - internal profile id
 * @property {GUID} msid - msid
 * @property {string} transactionId - id of transaction
 * @return {LogObject<transactionRequestParams>} LogObject<transactionRequestParams>
 */
export function transactionRequest(params) {
    return { evid: 1000, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionApproval' event (evid: 1001)
 *
 * Event description: Transaction Response IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {transactionApprovalParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {string} providerAccountId - PagarMe/Adyen account identifier
 * @property {string} externalOrderId - order id
 * @property {string} externalMerchantId - wix user guid
 * @property {GUID} uuid - wix uuid
 * @property {string} mandateStatus - status of mandate (applicable for Stripe India)
 * @property {GUID} visitor_id - visitor_id
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount of transaction - multiply by 100,000
 * @property {GUID} externalAccountId - msid
 * @property {GUID} pricingPlanId - fees pricing plan
 * @property {string} operation - name of the operation, i.e. Create, Capture, Refund, Retrieve
 * @property {string} additionalData - json data
 * @property {string} status - status of the transaction
 * @property {string} wpGeo - wp_geo
 * @property {string} currency - currency
 * @property {string} adapterName - adapter
 * @property {string} externalTransactionId - Cashier trans
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} accountProfileId - internal profile id
 * @property {GUID} msid - msid
 * @property {string} transactionId - id of transaction
 * @return {LogObject<transactionApprovalParams>} LogObject<transactionApprovalParams>
 */
export function transactionApproval(params) {
    return { evid: 1001, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionDeclined' event (evid: 1002)
 *
 * Event description: Transaction Failure   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {transactionDeclinedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {string} externalOrderId - order id
 * @property {string} providerErrorCode - payment gateway/processor error code
 * @property {string} externalMerchantId - wix user guid
 * @property {GUID} uuid - wix uuid
 * @property {string} mandateStatus - status of mandate (applicable for Stripe India)
 * @property {GUID} visitor_id - visitor_id
 * @property {string} errorReason - wix payments error reason
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount of transaction - multiply by 100,000
 * @property {GUID} externalAccountId - msid
 * @property {GUID} pricingPlanId - fees pricing plan
 * @property {string} errorCode - wix payments error code
 * @property {string} operation - name of the operation, i.e. Create, Capture, Refund, Retrieve
 * @property {string} additionalData - json data
 * @property {string} status - status of the transaction
 * @property {string} wpGeo - wp_geo
 * @property {string} providerReason - payment gateway/processor error reason
 * @property {string} currency - currency
 * @property {string} adapterName - adapter
 * @property {string} externalTransactionId - Cashier trans
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} accountProfileId - internal profile id
 * @property {GUID} msid - msid
 * @property {string} transactionId - id of transaction
 * @return {LogObject<transactionDeclinedParams>} LogObject<transactionDeclinedParams>
 */
export function transactionDeclined(params) {
    return { evid: 1002, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionTechnicalFailure' event (evid: 1003)
 *
 * Event description: technical or validation error happened during request to Transaction Service   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {transactionTechnicalFailureParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {string} externalOrderId - order id
 * @property {string} externalMerchantId - wix user guid
 * @property {GUID} uuid - wix uuid
 * @property {string} mandateStatus - status of mandate (applicable for Stripe India)
 * @property {GUID} visitor_id - visitor_id
 * @property {string} errorReason - wix payments error reason
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} pricingPlanId - fees pricing plan
 * @property {string} operation - name of the operation, i.e. Create, Capture, Refund, Retrieve
 * @property {string} additionalData - json data
 * @property {string} wpGeo - wp_geo
 * @property {string} adapterName - adapter
 * @property {string} externalTransactionId - external tx id
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} accountProfileId - internal profile id
 * @property {GUID} msid - msid
 * @property {string} transactionId - id of transaction
 * @return {LogObject<transactionTechnicalFailureParams>} LogObject<transactionTechnicalFailureParams>
 */
export function transactionTechnicalFailure(params) {
    return { evid: 1003, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionPending' event (evid: 1004)
 *
 * Event description: The request from the adapter is pending   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {transactionPendingParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {string} externalOrderId - order id
 * @property {string} externalMerchantId - wix user guid
 * @property {GUID} uuid - wix uuid
 * @property {string} mandateStatus - status of mandate (applicable for Stripe India)
 * @property {GUID} visitor_id - visitor_id
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount of transaction - multiply by 100,000
 * @property {GUID} externalAccountId - msid
 * @property {GUID} pricingPlanId - fees pricing plan
 * @property {string} operation - name of the operation, i.e. Create, Capture, Refund, Retrieve
 * @property {string} additionalData - json data
 * @property {string} wpGeo - wp_geo
 * @property {string} currency - currency
 * @property {string} adapterName - name of the adapter that handles concrete payment gateway/processor (Stone)
 * @property {string} externalTransactionId - external tx id
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} accountProfileId - internal profile id
 * @property {GUID} msid - msid
 * @property {string} transactionId - id of transaction
 * @return {LogObject<transactionPendingParams>} LogObject<transactionPendingParams>
 */
export function transactionPending(params) {
    return { evid: 1004, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionChargeback' event (evid: 1005)
 *
 * Event description: The transaction was chargedback by the processor   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {transactionChargebackParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {string} externalOrderId - order id
 * @property {string} externalMerchantId - wix user guid
 * @property {GUID} uuid - wix user guid // externalMerchantId on WP side
 * @property {GUID} visitor_id - visitor_id
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount of transaction - multiply by 100,000
 * @property {GUID} externalAccountId - msid
 * @property {GUID} pricingPlanId - fees pricing plan
 * @property {string} operation - name of the operation, i.e. Create, Capture, Refund, Retrieve
 * @property {string} additionalData - json data
 * @property {string} wpGeo - wp_geo
 * @property {string} currency - currency
 * @property {string} adapterName - name of the adapter that handles concrete payment gateway/processor (Stone)
 * @property {string} externalTransactionId - external tx id
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} accountProfileId - internal profile Id
 * @property {GUID} msid - msid
 * @property {string} transactionId - id of transaction
 * @return {LogObject<transactionChargebackParams>} LogObject<transactionChargebackParams>
 */
export function transactionChargeback(params) {
    return { evid: 1005, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionRefundRequest' event (evid: 1006)
 *
 * Event description: a refund request was sent to the adapter   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {transactionRefundRequestParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {string} externalOrderId - order id
 * @property {string} externalMerchantId - wix user guid
 * @property {GUID} uuid - wix uuid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount of transaction - multiply by 100,000
 * @property {GUID} externalAccountId - msid
 * @property {GUID} pricingPlanId - fees pricing plan
 * @property {string} operation - name of the operation, i.e. Create, Capture, Refund, Retrieve
 * @property {string} additionalData - json data
 * @property {string} wpGeo - wp_geo
 * @property {string} currency - currency
 * @property {number} refundAmount - amount of refund - multiply by 100,000
 * @property {string} adapterName - name of the adapter that handles concrete payment gateway/processor (Stone)
 * @property {string} externalTransactionId - external tx id
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} accountProfileId - internal profile id
 * @property {GUID} msid - msid
 * @property {string} transactionId - id of transaction
 * @return {LogObject<transactionRefundRequestParams>} LogObject<transactionRefundRequestParams>
 */
export function transactionRefundRequest(params) {
    return { evid: 1006, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionRefundSuccess' event (evid: 1007)
 *
 * Event description: The transaction was successfully refunded   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {transactionRefundSuccessParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {string} externalOrderId - order id
 * @property {string} externalMerchantId - wix user guid
 * @property {GUID} uuid - wix uuid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount of transaction - multiply by 100,000
 * @property {GUID} externalAccountId - msid
 * @property {GUID} pricingPlanId - fees pricing plan
 * @property {string} operation - name of the operation, i.e. Create, Capture, Refund, Retrieve
 * @property {string} additionalData - json data
 * @property {string} wpGeo - wp_geo
 * @property {string} currency - currency
 * @property {number} refundAmount - amount of refund - multiply by 100,000
 * @property {string} adapterName - name of the adapter that handles concrete payment gateway/processor (Stone)
 * @property {string} externalTransactionId - external tx id
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} accountProfileId - internal profile id
 * @property {GUID} msid - msid
 * @property {string} transactionId - id of transaction
 * @return {LogObject<transactionRefundSuccessParams>} LogObject<transactionRefundSuccessParams>
 */
export function transactionRefundSuccess(params) {
    return { evid: 1007, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionRefundFailed' event (evid: 1008)
 *
 * Event description: Transaction refund failed   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {transactionRefundFailedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} accoountProfileId - internal profile id
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {string} externalOrderId - order id
 * @property {string} externalMerchantId - wix user guid
 * @property {GUID} uuid - wix uuid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} errorReason - wix payments error reason
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount of transaction - multiply by 100,000
 * @property {GUID} externalAccountId - msid
 * @property {GUID} pricingPlanId - fees pricing plan
 * @property {string} operation - name of the operation, i.e. Create, Capture, Refund, Retrieve
 * @property {string} additionalData - json data
 * @property {GUID} accountprofileid - internal profile id
 * @property {string} wpGeo - wp_geo
 * @property {string} currency - currency
 * @property {number} refundAmount - amount of refund - multiply by 100,000
 * @property {string} adapterName - name of the adapter that handles concrete payment gateway/processor (Stone)
 * @property {string} externalTransactionId - external tx id
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} msid - msid
 * @property {string} transactionId - id of transaction
 * @return {LogObject<transactionRefundFailedParams>} LogObject<transactionRefundFailedParams>
 */
export function transactionRefundFailed(params) {
    return { evid: 1008, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userClickAccountType' event (evid: 101)
 *
 * Event description: User select account type on the on-boarding page
 *
 * @param {userClickAccountTypeParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} clickType - personal or business
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account_status on WP side
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {boolean} isPremium - is site premium
 * @property {GUID} accountProfileId - account_profile_id on wp side
 * @property {GUID} msid - msid
 * @return {LogObject<userClickAccountTypeParams>} LogObject<userClickAccountTypeParams>
 */
export function userClickAccountType(params) {
    return { evid: 101, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionStatusChange' event (evid: 1011)
 *
 * Event description: transaction status has changed   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {transactionStatusChangeParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {string} externalMerchantId - wix user guid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {string} operation - name of the operation, i.e. Create, Capture, Refund, Retrieve
 * @property {string} additionalData - json data
 * @property {string} status - status
 * @property {string} wpGeo - wp_geo
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} msid - msid
 * @property {string} transactionId - id of transaction
 * @return {LogObject<transactionStatusChangeParams>} LogObject<transactionStatusChangeParams>
 */
export function transactionStatusChange(params) {
    return { evid: 1011, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'reservesReserveCreated' event (evid: 1012)
 *
 * Event description: Reserve was created
 * IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {reservesReserveCreatedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {DateTime} releaseDate - date when release will be available
 * @property {string} flowType - payout / risk team
 * @property {number} amountLong - amount of the reserve - multiply by 100,000
 * @property {GUID} externalMerchantId - wix user guid
 * @property {string} reserveId - reserve_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {string} wpGeo - wp geo
 * @property {string} currency - currency
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} accountProfileId - internal profile id
 * @property {GUID} msid - msid
 * @return {LogObject<reservesReserveCreatedParams>} LogObject<reservesReserveCreatedParams>
 */
export function reservesReserveCreated(params) {
    return { evid: 1012, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'reservesReserveDiscarded' event (evid: 1013)
 *
 * Event description: Reserve was discarded   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {reservesReserveDiscardedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {DateTime} releaseDate - date when release will be available
 * @property {string} flowType - payout / risk team
 * @property {number} amountLong - amount of the reserve - multiply by 100,000
 * @property {GUID} externalMerchantId - wix user guid
 * @property {string} reserveId - reserve_id
 * @property {GUID} visitor_id - visitor_id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {string} wpGeo - wp geo
 * @property {string} currency - currency
 * @property {string} tenantId - id that represents the tenant (wix cashier)
 * @property {GUID} accountProfileId - internal profile id
 * @property {GUID} msid - msid
 * @return {LogObject<reservesReserveDiscardedParams>} LogObject<reservesReserveDiscardedParams>
 */
export function reservesReserveDiscarded(params) {
    return { evid: 1013, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserClickRegulationsRequirement' event (evid: 102)
 *
 * Event description: User clicked regulations requirements
 *
 * @param {onboardingUserClickRegulationsRequirementParams} params - The parameters for this event
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. partners_dashboard
 * @property {boolean} isPremium - is site premium
 * @property {GUID} msid - msid
 * @return {LogObject<onboardingUserClickRegulationsRequirementParams>} LogObject<onboardingUserClickRegulationsRequirementParams>
 */
export function onboardingUserClickRegulationsRequirement(params) {
    return { evid: 102, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserClickUpgradeNow' event (evid: 103)
 *
 * Event description: User clicked on upgrade now
 *
 * @param {onboardingUserClickUpgradeNowParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. partners_dashboard
 * @property {boolean} isPremium - is site premium
 * @property {GUID} msid - msid
 * @return {LogObject<onboardingUserClickUpgradeNowParams>} LogObject<onboardingUserClickUpgradeNowParams>
 */
export function onboardingUserClickUpgradeNow(params) {
    return { evid: 103, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userChangeAcceptInstallments' event (evid: 104)
 *
 * Event description: User choose accept installments settings
 *
 * @param {userChangeAcceptInstallmentsParams} params - The parameters for this event
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} clickType - enable or disable
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. partners_dashboard
 * @property {boolean} isPremium - is site premium
 * @property {GUID} msid - msid
 * @return {LogObject<userChangeAcceptInstallmentsParams>} LogObject<userChangeAcceptInstallmentsParams>
 */
export function userChangeAcceptInstallments(params) {
    return { evid: 104, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userClickLearnAboutAnticipations' event (evid: 105)
 *
 * Event description: User clicked “learn more” about anticipation
 *
 * @param {userClickLearnAboutAnticipationsParams} params - The parameters for this event
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {boolean} isPremium - is site premium
 * @property {GUID} accountProfileId - profile_id
 * @property {GUID} msid - msid
 * @return {LogObject<userClickLearnAboutAnticipationsParams>} LogObject<userClickLearnAboutAnticipationsParams>
 */
export function userClickLearnAboutAnticipations(params) {
    return { evid: 105, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserOpenTermsConditions' event (evid: 106)
 *
 * Event description: User opened terms and conditions
 *
 * @param {onboardingUserOpenTermsConditionsParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account_status on wp side
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {boolean} isPremium - is site premium
 * @property {GUID} accountProfileId - account_profile_id on wp side
 * @property {GUID} msid - msid
 * @return {LogObject<onboardingUserOpenTermsConditionsParams>} LogObject<onboardingUserOpenTermsConditionsParams>
 */
export function onboardingUserOpenTermsConditions(params) {
    return { evid: 106, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserClickCancel' event (evid: 107)
 *
 * Event description: User clicked cancel on the onboarding page. !!!!!!!!!!!! UPD: use page type to define was it shareholder or account set up.
 *
 * @param {onboardingUserClickCancelParams} params - The parameters for this event
 * @property {string} page_type - onboarding / shareholder modal etc.
 * @property {string} referralInfo - place from which user came
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {boolean} isPremium - is site premium
 * @property {GUID} msid - msid
 * @return {LogObject<onboardingUserClickCancelParams>} LogObject<onboardingUserClickCancelParams>
 */
export function onboardingUserClickCancel(params) {
    return { evid: 107, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingLeavingBannerClickType' event (evid: 108)
 *
 * Event description: User click type in “leaving already?”  LB
 *
 * @param {onboardingLeavingBannerClickTypeParams} params - The parameters for this event
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} clickType - what the user clicked
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status; e.g. for Adyen: Pending / Active / AcceptingPayments / Suspended / Blocked;
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {boolean} isPremium - is site premium
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @return {LogObject<onboardingLeavingBannerClickTypeParams>} LogObject<onboardingLeavingBannerClickTypeParams>
 */
export function onboardingLeavingBannerClickType(params) {
    return { evid: 108, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userClickCreateAccount' event (evid: 109)
 *
 * Event description: User clicked create my account. !!!!!!!!!!!! UPD: use page type to define was it shareholder or account set up.
 *
 * @param {userClickCreateAccountParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} role - Title in company / managing role in the company
 * @property {boolean} shared - own 25% or more of the business
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} businessType - business_type
 * @property {string} country - country of citizenship
 * @property {boolean} managingPosition - has C-position / high level position = shareholder
 * @property {string} screenResolution - screen_resolution
 * @property {string} businessDescription - field "Doing business as"
 * @property {string} productDescription - product_description
 * @property {string} actionType - Submit OB / Add shareholder / Edit shareholder
 * @property {string} predictedMccList - List of predicted MCCs with comma separator, eg "4789,4812"
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {boolean} isPremium - is site premium
 * @property {GUID} msid - msid
 * @return {LogObject<userClickCreateAccountParams>} LogObject<userClickCreateAccountParams>
 */
export function userClickCreateAccount(params) {
    return { evid: 109, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingFormSubmitted' event (evid: 110)
 *
 * Event description: Form was submitted. !!!!!!!!!!!! UPD: use page type to define was it shareholder or account set up.
 *
 * @param {onboardingFormSubmittedParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} role - Title in company / managing role in the company
 * @property {boolean} shared - own 25% or more of the business
 * @property {string} anticipationTerms - anticipation terms
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {boolean} acceptInstallments - installments settings
 * @property {GUID} userGuid - uuid
 * @property {string} businessType - mcc business_type
 * @property {string} country - country of citizenship
 * @property {boolean} managingPosition - C-position / high level position = shareholder
 * @property {string} screenResolution - screen_resolution
 * @property {string} businessDescription - field "Doing business as"
 * @property {string} productDescription - description of goods and services at KYC
 * @property {string} actionType - Submit OB / Add shareholder / Edit shareholder
 * @property {string} predictedMccList - List of predicted MCCs with comma separator, eg "4789,4812"
 * @property {string} maxInstallmentsDigit - max installments
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {boolean} isPremium - is site premium
 * @property {GUID} msid - msid
 * @property {string} minInstallmentsDigit - min installments
 * @property {number} applyAmount - installments threshold
 * @property {boolean} isNeedAddDirectors - true if "I need to add directors" checked on mini-KYC
 * @property {boolean} isBusinessPhoneSameSupportNumber - true if "Is your business phone number same as customer support phone number?" checked as "yes" on mini-KYC
 * @return {LogObject<onboardingFormSubmittedParams>} LogObject<onboardingFormSubmittedParams>
 */
export function onboardingFormSubmitted(params) {
    return { evid: 110, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingFormValidationError' event (evid: 111)
 *
 * Event description: Form validation error after a user choose to create the account. !!!!!!!!!!!! UPD: use page type to define was it shareholder or account set up.
 *
 * @param {onboardingFormValidationErrorParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} role - Title in company / managing role in the company
 * @property {boolean} shared - own 25% or more of the business
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} businessType - business_type
 * @property {string} country - country of citizenship
 * @property {boolean} managingPosition - C-position / high level position = shareholder
 * @property {string} screenResolution - screen_resolution
 * @property {string} businessDescription - field "Doing business as"
 * @property {string} productDescription - description of goods and services at KYC
 * @property {string} actionType - Submit OB / Add shareholder / Edit shareholder
 * @property {string} predictedMccList - List of predicted MCCs with comma separator, eg "4789,4812"
 * @property {string} invalidFields - invalid fields in the form
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {boolean} isPremium - is site premium
 * @property {GUID} msid - msid
 * @property {string} emptyFields - empty fields in the form
 * @property {string} documentErrors - array of values: document type / error
 * @return {LogObject<onboardingFormValidationErrorParams>} LogObject<onboardingFormValidationErrorParams>
 */
export function onboardingFormValidationError(params) {
    return { evid: 111, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingFormFatalError' event (evid: 112)
 *
 * Event description: “Fatal error”/exception when trying to send the onboarding form. !!!!!!!!!!!! UPD: use page type to define was it shareholder or account set up.
 *
 * @param {onboardingFormFatalErrorParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} role - Title in company / managing role in the company
 * @property {boolean} shared - own 25% or more of the business
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} businessType - business_type
 * @property {string} country - country of citizenship
 * @property {boolean} managingPosition - C-position / high level position = shareholder
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - accountStatus
 * @property {string} businessDescription - field "Doing business as"
 * @property {string} productDescription - description of goods and services at KYC
 * @property {string} actionType - Submit OB / Add shareholder / Edit shareholder
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} message - error message
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {boolean} isPremium - is site premium
 * @property {GUID} accountProfileId - profile_id
 * @property {GUID} msid - msid
 * @return {LogObject<onboardingFormFatalErrorParams>} LogObject<onboardingFormFatalErrorParams>
 */
export function onboardingFormFatalError(params) {
    return { evid: 112, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'viewWixPaymentsOnboardingPage' event (evid: 113)
 *
 * Event description: Wix Payments onboarding page loaded
 *
 * @param {viewWixPaymentsOnboardingPageParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} predictedMccList - List of predicted MCCs with comma separator, eg "4789,4812"
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {boolean} isPremium - is site premium
 * @property {GUID} msid - msid
 * @return {LogObject<viewWixPaymentsOnboardingPageParams>} LogObject<viewWixPaymentsOnboardingPageParams>
 */
export function viewWixPaymentsOnboardingPage(params) {
    return { evid: 113, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onBoardingWizardView' event (evid: 114)
 *
 * Event description: The user viewed the onboarding wizard in the Wix Payments dashboard
 *
 * @param {onBoardingWizardViewParams} params - The parameters for this event
 * @property {string} checklist - Missing actions to complete onboarding 0-isPremium 1-isPublished 2-buySomething 3-SetupComplete
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {GUID} accountProfileId - Wix Payments profile ID
 * @property {GUID} msid - site guid
 * @return {LogObject<onBoardingWizardViewParams>} LogObject<onBoardingWizardViewParams>
 */
export function onBoardingWizardView(params) {
    return { evid: 114, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onBoardingWizardClickFinish' event (evid: 115)
 *
 * Event description: A user clicked finish / I'm done / refresh button
 *
 * @param {onBoardingWizardClickFinishParams} params - The parameters for this event
 * @property {string} checklist - missing actions to complete onboarding 0-ispremium 1-ispublished 2-buysomething 3-setupcomplete
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - site guid
 * @return {LogObject<onBoardingWizardClickFinishParams>} LogObject<onBoardingWizardClickFinishParams>
 */
export function onBoardingWizardClickFinish(params) {
    return { evid: 115, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickCompleteSetUp' event (evid: 116)
 *
 * Event description: A user clicked complete account set-up in the complete setup page
 *
 * @param {clickCompleteSetUpParams} params - The parameters for this event
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g next payout / some empty state
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - site guid
 * @return {LogObject<clickCompleteSetUpParams>} LogObject<clickCompleteSetUpParams>
 */
export function clickCompleteSetUp(params) {
    return { evid: 116, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onBoardingWizardClickUpgrade' event (evid: 117)
 *
 * Event description: user click upgrade now
 *
 * @param {onBoardingWizardClickUpgradeParams} params - The parameters for this event
 * @property {string} checklist - enum for the missing actions 0-ispremium 1-ispublished 2-buysomething 3-setupcomplete
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {GUID} accountProfileId - PROFILE ID
 * @property {GUID} msid - msid
 * @return {LogObject<onBoardingWizardClickUpgradeParams>} LogObject<onBoardingWizardClickUpgradeParams>
 */
export function onBoardingWizardClickUpgrade(params) {
    return { evid: 117, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onBoardingWizardClickGoToEditor' event (evid: 118)
 *
 * Event description: user click go to editor
 *
 * @param {onBoardingWizardClickGoToEditorParams} params - The parameters for this event
 * @property {string} checklist - enum for the missing actions 0-ispremium 1-ispublished 2-buysomething 3-setupcomplete
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {GUID} accountProfileId - PROFILE ID
 * @property {GUID} msid - msid
 * @return {LogObject<onBoardingWizardClickGoToEditorParams>} LogObject<onBoardingWizardClickGoToEditorParams>
 */
export function onBoardingWizardClickGoToEditor(params) {
    return { evid: 118, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserChangedShareOfBusiness' event (evid: 119)
 *
 * Event description: User choose accept installments settings
 *
 * @param {onboardingUserChangedShareOfBusinessParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} shared - own 25% or more of the business
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {boolean} isPremium - is site premium
 * @return {LogObject<onboardingUserChangedShareOfBusinessParams>} LogObject<onboardingUserChangedShareOfBusinessParams>
 */
export function onboardingUserChangedShareOfBusiness(params) {
    return { evid: 119, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserChangedManagingRole' event (evid: 120)
 *
 * Event description: User changed managing position
 *
 * @param {onboardingUserChangedManagingRoleParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {boolean} managingPosition - has C-position / high level position = shareholder
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {boolean} isPremium - is site premium
 * @return {LogObject<onboardingUserChangedManagingRoleParams>} LogObject<onboardingUserChangedManagingRoleParams>
 */
export function onboardingUserChangedManagingRole(params) {
    return { evid: 120, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserClickAddShareholder' event (evid: 121)
 *
 * Event description: User clicked Add Shareholder
 *
 * @param {onboardingUserClickAddShareholderParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc
 * @property {boolean} isPremium - is site premium
 * @return {LogObject<onboardingUserClickAddShareholderParams>} LogObject<onboardingUserClickAddShareholderParams>
 */
export function onboardingUserClickAddShareholder(params) {
    return { evid: 121, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserClickEditShareholder' event (evid: 122)
 *
 * Event description: User clicked Edit Shareholder
 *
 * @param {onboardingUserClickEditShareholderParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} role - Title in company / managing role in the company
 * @property {boolean} shared - own 25% or more of the business
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {string} country - country of citizenship
 * @property {boolean} managingPosition - C-position / high level position = shareholder
 * @property {string} screenResolution - screen_resolution
 * @property {string} id - shareholder id
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc
 * @property {boolean} isPremium - is site premium
 * @return {LogObject<onboardingUserClickEditShareholderParams>} LogObject<onboardingUserClickEditShareholderParams>
 */
export function onboardingUserClickEditShareholder(params) {
    return { evid: 122, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserClickDeleteShareholder' event (evid: 123)
 *
 * Event description: User clicked Delete Shareholder
 *
 * @param {onboardingUserClickDeleteShareholderParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} role - Title in company / managing role in the company
 * @property {boolean} shared - own 25% or more of the business
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {string} country - country of citizenship
 * @property {boolean} managingPosition - C-position / high level position = shareholder
 * @property {string} screenResolution - screen_resolution
 * @property {string} id - shareholder id
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc
 * @property {boolean} isPremium - is site premium
 * @return {LogObject<onboardingUserClickDeleteShareholderParams>} LogObject<onboardingUserClickDeleteShareholderParams>
 */
export function onboardingUserClickDeleteShareholder(params) {
    return { evid: 123, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserClickViewShareholder' event (evid: 124)
 *
 * Event description: User clicked View Shareholder
 *
 * @param {onboardingUserClickViewShareholderParams} params - The parameters for this event
 * @property {string} role - Title in company / managing role in the company
 * @property {boolean} shared - own 25% or more of the business
 * @property {string} country - country of citizenship
 * @property {boolean} managingPosition - C-position / high level position = shareholder
 * @property {string} screenResolution - screen_resolution
 * @property {string} id - shareholder id
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} pageType - onboarding / shareholder modal etc
 * @return {LogObject<onboardingUserClickViewShareholderParams>} LogObject<onboardingUserClickViewShareholderParams>
 */
export function onboardingUserClickViewShareholder(params) {
    return { evid: 124, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingDeleteShareholderSubmitDeletion' event (evid: 125)
 *
 * Event description: User clicked Submit Deletion in Modal view
 *
 * @param {onboardingDeleteShareholderSubmitDeletionParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} role - Title in company / managing role in the company
 * @property {boolean} shared - own 25% or more of the business
 * @property {string} country - country of citizenship
 * @property {boolean} managingPosition - C-position / high level position = shareholder
 * @property {string} screenResolution - screen_resolution
 * @property {string} id - shareholder id
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc
 * @return {LogObject<onboardingDeleteShareholderSubmitDeletionParams>} LogObject<onboardingDeleteShareholderSubmitDeletionParams>
 */
export function onboardingDeleteShareholderSubmitDeletion(params) {
    return { evid: 125, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingUserChangedCitizenship' event (evid: 126)
 *
 * Event description: User changed country of citizenship
 *
 * @param {onboardingUserChangedCitizenshipParams} params - The parameters for this event
 * @property {boolean} hasQualifiedPremium - site has the right premium plan for WP
 * @property {string} country - country of citizenship
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {boolean} isPremium - is site premium
 * @return {LogObject<onboardingUserChangedCitizenshipParams>} LogObject<onboardingUserChangedCitizenshipParams>
 */
export function onboardingUserChangedCitizenship(params) {
    return { evid: 126, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbAcceptButton' event (evid: 127)
 *
 * Event description: User agreed with ChB
 *
 * @param {chbAcceptButtonParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @return {LogObject<chbAcceptButtonParams>} LogObject<chbAcceptButtonParams>
 */
export function chbAcceptButton(params) {
    return { evid: 127, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbAcceptConfirmationAction' event (evid: 128)
 *
 * Event description: User agreed with ChB click "Accept" and see confirmation windows with 2 buttons. Here we define which button is clicked
 *
 * @param {chbAcceptConfirmationActionParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp_geo
 * @property {string} selected_value - Accept or Cancel
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @return {LogObject<chbAcceptConfirmationActionParams>} LogObject<chbAcceptConfirmationActionParams>
 */
export function chbAcceptConfirmationAction(params) {
    return { evid: 128, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputButtonClicked' event (evid: 129)
 *
 * Event description: User not agreed with ChB  and click "Open Dispute"
 *
 * @param {chbDisputButtonClickedParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} ctaName - dispute / continue dispute
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @return {LogObject<chbDisputButtonClickedParams>} LogObject<chbDisputButtonClickedParams>
 */
export function chbDisputButtonClicked(params) {
    return { evid: 129, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputDocumentUploaded' event (evid: 130)
 *
 * Event description: User not agreed with ChB  and click "Open Dispute"
 *
 * @param {chbDisputDocumentUploadedParams} params - The parameters for this event
 * @property {string} file_name - file name
 * @property {GUID} userGuid - uuid
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} fileType - file type
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - proof type
 * @return {LogObject<chbDisputDocumentUploadedParams>} LogObject<chbDisputDocumentUploadedParams>
 */
export function chbDisputDocumentUploaded(params) {
    return { evid: 130, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputeConfirmationAction' event (evid: 131)
 *
 * Event description: User agreed with ChB click "Dispute" upload documents and click submit he see confirmation windows with 2 buttons. Here we define which button is clicked
 *
 * @param {chbDisputeConfirmationActionParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp_geo
 * @property {string} selected_value - Accept or Cancel
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @return {LogObject<chbDisputeConfirmationActionParams>} LogObject<chbDisputeConfirmationActionParams>
 */
export function chbDisputeConfirmationAction(params) {
    return { evid: 131, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputeDocumentClickUploadFile' event (evid: 133)
 *
 * Event description: User clicked on "+" to upload documents for dispute confirmation
 *
 * @param {chbDisputeDocumentClickUploadFileParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - proof type
 * @return {LogObject<chbDisputeDocumentClickUploadFileParams>} LogObject<chbDisputeDocumentClickUploadFileParams>
 */
export function chbDisputeDocumentClickUploadFile(params) {
    return { evid: 133, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputeDocumentUploadStarted' event (evid: 134)
 *
 * Event description: Dispute document upload started
 *
 * @param {chbDisputeDocumentUploadStartedParams} params - The parameters for this event
 * @property {string} file_name - file name
 * @property {GUID} userGuid - uuid
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} fileType - file type
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - proof type
 * @return {LogObject<chbDisputeDocumentUploadStartedParams>} LogObject<chbDisputeDocumentUploadStartedParams>
 */
export function chbDisputeDocumentUploadStarted(params) {
    return { evid: 134, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputeDocumentUploadFailed' event (evid: 135)
 *
 * Event description: Dispute document upload issue
 *
 * @param {chbDisputeDocumentUploadFailedParams} params - The parameters for this event
 * @property {string} file_name - file name
 * @property {GUID} userGuid - uuid
 * @property {string} errorReason - reason of fail
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} fileType - file type
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - proof type
 * @return {LogObject<chbDisputeDocumentUploadFailedParams>} LogObject<chbDisputeDocumentUploadFailedParams>
 */
export function chbDisputeDocumentUploadFailed(params) {
    return { evid: 135, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputeDocumentClickDeleteFile' event (evid: 136)
 *
 * Event description: Dispute document click delete
 *
 * @param {chbDisputeDocumentClickDeleteFileParams} params - The parameters for this event
 * @property {string} file_name - file name
 * @property {GUID} userGuid - uuid
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} fileType - file type
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - proof type
 * @return {LogObject<chbDisputeDocumentClickDeleteFileParams>} LogObject<chbDisputeDocumentClickDeleteFileParams>
 */
export function chbDisputeDocumentClickDeleteFile(params) {
    return { evid: 136, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputeDocumentClickUpdateFile' event (evid: 137)
 *
 * Event description: Dispute document click update file
 *
 * @param {chbDisputeDocumentClickUpdateFileParams} params - The parameters for this event
 * @property {string} file_name - file name
 * @property {GUID} userGuid - uuid
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} fileType - file type
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - proof type
 * @return {LogObject<chbDisputeDocumentClickUpdateFileParams>} LogObject<chbDisputeDocumentClickUpdateFileParams>
 */
export function chbDisputeDocumentClickUpdateFile(params) {
    return { evid: 137, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputeClickSubmitTheDisputeForm' event (evid: 138)
 *
 * Event description: User click "submit" button on the form
 *
 * @param {chbDisputeClickSubmitTheDisputeFormParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} clickType - Submit / Preview & Submit / Close / Save Draft
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} service - service type selected by merchant
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @return {LogObject<chbDisputeClickSubmitTheDisputeFormParams>} LogObject<chbDisputeClickSubmitTheDisputeFormParams>
 */
export function chbDisputeClickSubmitTheDisputeForm(params) {
    return { evid: 138, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputeDisputeFormWasSubmittedSuccessfully' event (evid: 139)
 *
 * Event description: Dispute form was Submitted successfully
 *
 * @param {chbDisputeDisputeFormWasSubmittedSuccessfullyParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} service - service type selected by merchant
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @return {LogObject<chbDisputeDisputeFormWasSubmittedSuccessfullyParams>} LogObject<chbDisputeDisputeFormWasSubmittedSuccessfullyParams>
 */
export function chbDisputeDisputeFormWasSubmittedSuccessfully(params) {
    return { evid: 139, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'chbDisputeDisputeFormSubmitFailed' event (evid: 140)
 *
 * Event description: Dispute form submit failed
 *
 * @param {chbDisputeDisputeFormSubmitFailedParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} errorReason - error reason
 * @property {GUID} transactionid - ChB transaction ID
 * @property {string} service - service type selected by merchant
 * @property {string} screenResolution - screen_resolution
 * @property {string} origin - from which channel user are coming (email, dashboard, etc)
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - page_type
 * @return {LogObject<chbDisputeDisputeFormSubmitFailedParams>} LogObject<chbDisputeDisputeFormSubmitFailedParams>
 */
export function chbDisputeDisputeFormSubmitFailed(params) {
    return { evid: 140, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutsClickOnInfoButton' event (evid: 141)
 *
 * Event description: User clicked on on Info button
 * e.g. how payouts work / where is my payout (by type)
 *
 * @param {payoutsClickOnInfoButtonParams} params - The parameters for this event
 * @property {GUID} accountId - account_id
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - e.g. how payouts work / where is my payout
 * @property {GUID} accountProfileId - profile id
 * @return {LogObject<payoutsClickOnInfoButtonParams>} LogObject<payoutsClickOnInfoButtonParams>
 */
export function payoutsClickOnInfoButton(params) {
    return { evid: 141, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutsClickOnSettingsIcon' event (evid: 142)
 *
 * Event description: User clicked on "Settings icon" on payout page
 *
 * @param {payoutsClickOnSettingsIconParams} params - The parameters for this event
 * @property {GUID} accountId - account_id
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - type of settings: payout cycle / update document
 * @property {GUID} accountProfileId - profile id
 * @return {LogObject<payoutsClickOnSettingsIconParams>} LogObject<payoutsClickOnSettingsIconParams>
 */
export function payoutsClickOnSettingsIcon(params) {
    return { evid: 142, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dashboardBannerWasShown' event (evid: 143)
 *
 * Event description: User interact with banner in the Wix Payments dashboard. Banner/Tooltip was showed
 *
 * @param {dashboardBannerWasShownParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - what page in the dashboard
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @property {string} bannerName - forbid refund, reservation info etc.
 * @property {GUID} transactionGuid - transaction_guid for tooltip on exact trx
 * @return {LogObject<dashboardBannerWasShownParams>} LogObject<dashboardBannerWasShownParams>
 */
export function dashboardBannerWasShown(params) {
    return { evid: 143, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutsOpenModalWindow' event (evid: 144)
 *
 * Event description: Modal window was opened
 *
 * @param {payoutsOpenModalWindowParams} params - The parameters for this event
 * @property {number} balance - available balance / multiplied by 100000
 * @property {GUID} accountId - account_id
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - type of window
 * @property {GUID} accountProfileId - profile id
 * @return {LogObject<payoutsOpenModalWindowParams>} LogObject<payoutsOpenModalWindowParams>
 */
export function payoutsOpenModalWindow(params) {
    return { evid: 144, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutsChangePayoutsFrequency' event (evid: 145)
 *
 * Event description: Change toggle for Payouts frequency in settings
 *
 * @param {payoutsChangePayoutsFrequencyParams} params - The parameters for this event
 * @property {GUID} accountId - account_id
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} schedule_type - payout schedule = frequency
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - profile id
 * @property {number} days - # of weekday / # of day of month
 * @return {LogObject<payoutsChangePayoutsFrequencyParams>} LogObject<payoutsChangePayoutsFrequencyParams>
 */
export function payoutsChangePayoutsFrequency(params) {
    return { evid: 145, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dashboardInteractionWithBanner' event (evid: 146)
 *
 * Event description: User interact with banner in the Wix Payments dashboard
 *
 * @param {dashboardInteractionWithBannerParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} ctaName - what CTA was on banner
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - what page in the dashboard
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @property {string} bannerName - forbid refund, reservation info etc.
 * @property {string} errorReason - error code/reason on which user clicked view the issue
 * @return {LogObject<dashboardInteractionWithBannerParams>} LogObject<dashboardInteractionWithBannerParams>
 */
export function dashboardInteractionWithBanner(params) {
    return { evid: 146, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundsTopupModalWindow' event (evid: 147)
 *
 * Event description: Modal window was opened
 *
 * @param {refundsTopupModalWindowParams} params - The parameters for this event
 * @property {number} refundable - the amount which can be refunded / multiplied by 100000
 * @property {number} topupAmount - suggested / applied amount to topup
 * @property {string} orderId - order id
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {string} errorReason - errorReason if topup was failed
 * @property {number} balance - available balance / multiplied by 100000
 * @property {GUID} accountId - account_id
 * @property {string} screenResolution - screen_resolution
 * @property {number} amount - amount merchant what to refund / multiplied by 100000
 * @property {string} accountStatus - account status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - not enough to refund / topup account / ... / succeed
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} transactionId - transaction_id in WP
 * @property {number} attemptNumber - Number of retries for Refund
 * @return {LogObject<refundsTopupModalWindowParams>} LogObject<refundsTopupModalWindowParams>
 */
export function refundsTopupModalWindow(params) {
    return { evid: 147, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundsCtaClickOnTopupModalWindow' event (evid: 148)
 *
 * Event description: CTA click on Topup Modal window
 *
 * @param {refundsCtaClickOnTopupModalWindowParams} params - The parameters for this event
 * @property {string} orderId - order id
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {string} cta_value - confirm / cancel / go back etc
 * @property {GUID} accountId - account_id
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - wp geo
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - not enough to refund / topup account / ... / succeed
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} transactionId - transaction_id in WP
 * @return {LogObject<refundsCtaClickOnTopupModalWindowParams>} LogObject<refundsCtaClickOnTopupModalWindowParams>
 */
export function refundsCtaClickOnTopupModalWindow(params) {
    return { evid: 148, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantAcceptPaymentsClickedAtBreadcrumbTrail' event (evid: 149)
 *
 * Event description: Merchant-click at breadcrumb trail (Accepting payments or Account level dashboard)
 *
 * @param {merchantAcceptPaymentsClickedAtBreadcrumbTrailParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} paymentProvider - stripe, paypal, wixpay etc.
 * @property {string} wpGeo - WP geo
 * @property {string} place - e.g. partners_dashboard
 * @property {string} pageType - what page in the dashboard / origin page
 * @property {GUID} accountProfileId - profile_id
 * @property {string} targetPage - page where you're going to be redirected
 * @return {LogObject<merchantAcceptPaymentsClickedAtBreadcrumbTrailParams>} LogObject<merchantAcceptPaymentsClickedAtBreadcrumbTrailParams>
 */
export function merchantAcceptPaymentsClickedAtBreadcrumbTrail(params) {
    return { evid: 149, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickUploadDocument' event (evid: 150)
 *
 * Event description: Click on button to upload document on the KYC form
 *
 * @param {clickUploadDocumentParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - settings/onboarding
 * @property {string} type - bank statement/personal_id etc
 * @property {GUID} accountProfileId - profile id
 * @property {string} action_type - upload/change
 * @return {LogObject<clickUploadDocumentParams>} LogObject<clickUploadDocumentParams>
 */
export function clickUploadDocument(params) {
    return { evid: 150, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'preciseErrorMessageWasShown' event (evid: 152)
 *
 * Event description: User clicks on CTA at modals on Onboarding form
 *
 * @param {preciseErrorMessageWasShownParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account_status on wp side
 * @property {string} actionType - Save As Draft / How to/ Cancel / Got it/ Need more help
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - WP Geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / modal etc.
 * @property {GUID} accountProfileId - account_profile_id
 * @property {string} bannerName - name of the banner where CTA is located
 * @return {LogObject<preciseErrorMessageWasShownParams>} LogObject<preciseErrorMessageWasShownParams>
 */
export function preciseErrorMessageWasShown(params) {
    return { evid: 152, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'preciseErrorMessageWasShownToUser' event (evid: 153)
 *
 * Event description: User receives error bucket from Payment Provider under WP and we show him Error message in BM
 *
 * @param {preciseErrorMessageWasShownToUserParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} verificationType - BANK_ACCOUNT_VERIFICATION/COMPANY_VERIFICATION/IDENTITY_VERIFICATION etc
 * @property {string} verificationStatus - AWAITING_DATA/PASSED etc
 * @property {string} errorMessage - message
 * @property {string} url - learn more link
 * @property {string} cta_type - BucketCTA
 * @property {string} errorReason - error code/reason - new field instead of error_code
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {number} errorCode - code
 * @property {string} wpGeo - GEO
 * @property {string} place - Bucket Error Position
 * @property {string} pageType - settings/onboarding etc
 * @property {string} notificationBody - Text shown to the user without interpolation. The same as errorMessage passed through the translation function
 * @property {GUID} accountProfileId - profile id
 * @return {LogObject<preciseErrorMessageWasShownToUserParams>} LogObject<preciseErrorMessageWasShownToUserParams>
 */
export function preciseErrorMessageWasShownToUser(params) {
    return { evid: 153, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'obBannerWasShown' event (evid: 154)
 *
 * Event description: The banner was shown to user
 *
 * @param {obBannerWasShownParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account_status on wp side
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - Onboarding
 * @property {GUID} accountProfileId - account_profile_id
 * @property {string} bannerName - Business Type Info
 * @return {LogObject<obBannerWasShownParams>} LogObject<obBannerWasShownParams>
 */
export function obBannerWasShown(params) {
    return { evid: 154, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'componentLoadedPerformanceMeasuring' event (evid: 155)
 *
 * Event description: Event to check loading time of specific component on the page + check if loading was succeed
 *
 * @param {componentLoadedPerformanceMeasuringParams} params - The parameters for this event
 * @property {string} componentName - transaction list / payments overview / etc
 * @property {boolean} isSuccess - if component was loaded
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account_status on wp side
 * @property {number} loadingTime - loading_time
 * @property {string} wpGeo - wp_geo
 * @property {number} pageNumber - current page number on the board
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - page_type
 * @property {GUID} accountProfileId - account_profile_id of WP account
 * @return {LogObject<componentLoadedPerformanceMeasuringParams>} LogObject<componentLoadedPerformanceMeasuringParams>
 */
export function componentLoadedPerformanceMeasuring(params) {
    return { evid: 155, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'obUserClickOnInputSuggestedValue' event (evid: 157)
 *
 * Event description: OB > User Click on Input Suggested Value.
 *
 * @param {obUserClickOnInputSuggestedValueParams} params - The parameters for this event
 * @property {string} screenResolution - screen_resolution
 * @property {string} fieldName - filed with validation
 * @property {string} usageType - personal or business
 * @property {string} selectedValue - selected_value
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} userInput - value that user entered
 * @property {string} pageType - onboarding / settings
 * @return {LogObject<obUserClickOnInputSuggestedValueParams>} LogObject<obUserClickOnInputSuggestedValueParams>
 */
export function obUserClickOnInputSuggestedValue(params) {
    return { evid: 157, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'obClickAcceptTermsAndConditions' event (evid: 158)
 *
 * Event description: User Clicks Accept Terms and Conditions at setting
 *
 * @param {obClickAcceptTermsAndConditionsParams} params - The parameters for this event
 * @property {string} errorMessage - message key
 * @property {string} platformName - null(desktop)/oneApp/partnets etc
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account_status on wp side
 * @property {string} wpGeo - GEO
 * @property {string} place - bm/wp-settings
 * @property {string} pageType - settings
 * @return {LogObject<obClickAcceptTermsAndConditionsParams>} LogObject<obClickAcceptTermsAndConditionsParams>
 */
export function obClickAcceptTermsAndConditions(params) {
    return { evid: 158, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onBoardingWizardClickGoToWebsiteSrc85Evid159' event (evid: 159)
 *
 * Event description: user click "Go to Website"
 *
 * @param {onBoardingWizardClickGoToWebsiteSrc85Evid159Params} params - The parameters for this event
 * @property {string} checklist - enum for the missing actions 0-ispremium 1-ispublished 2-buysomething 3-setupcomplete
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {GUID} accountProfileId - PROFILE ID
 * @property {GUID} msid - msid
 * @return {LogObject<onBoardingWizardClickGoToWebsiteSrc85Evid159Params>} LogObject<onBoardingWizardClickGoToWebsiteSrc85Evid159Params>
 */
export function onBoardingWizardClickGoToWebsiteSrc85Evid159(params) {
    return { evid: 159, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'obUserChangedHasDirectorTitleSrc85Evid160' event (evid: 160)
 *
 * Event description: The user chooses if his position is a director on KYC form
 *
 * @param {obUserChangedHasDirectorTitleSrc85Evid160Params} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isDirector - true if 'Yes' for director title
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @return {LogObject<obUserChangedHasDirectorTitleSrc85Evid160Params>} LogObject<obUserChangedHasDirectorTitleSrc85Evid160Params>
 */
export function obUserChangedHasDirectorTitleSrc85Evid160(params) {
    return { evid: 160, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'obSummaryErrorMessageWasShownToUserSrc85Evid161' event (evid: 161)
 *
 * Event description: User saw error summary notification on Account Settings page
 *
 * @param {obSummaryErrorMessageWasShownToUserSrc85Evid161Params} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} verificationType - array of errors type in displayed order (BANK_ACCOUNT_VERIFICATION/COMPANY_VERIFICATION/IDENTITY_VERIFICATION)
 * @property {string} verificationStatus - array of statuses in displayed order (AWAITING_DATA/PASSED etc)
 * @property {string} errorMessage - array of error messeges
 * @property {string} cta_type - View Issue
 * @property {string} errorReason - array of error code/reason
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - GEO
 * @property {string} place - number of errors showed in summary
 * @property {string} pageType - settings/onboarding etc
 * @property {string} notificationBody - array of Text shown to the user without interpolation. The same as errorMessage passed through the translation function
 * @property {GUID} accountProfileId - profile id
 * @property {number} errorCount - number of errors in list
 * @return {LogObject<obSummaryErrorMessageWasShownToUserSrc85Evid161Params>} LogObject<obSummaryErrorMessageWasShownToUserSrc85Evid161Params>
 */
export function obSummaryErrorMessageWasShownToUserSrc85Evid161(params) {
    return { evid: 161, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'fullStoryUrlSrc85Evid171' event (evid: 171)
 *
 * Event description: to gather Full Story URL
 *
 * @param {fullStoryUrlSrc85Evid171Params} params - The parameters for this event
 * @property {string} msid - msid
 * @property {string} uuid - uuid
 * @property {GUID} accountProfileId - Wix AccountProfileId
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} type - Type of the page: empty page no premium / empty page no connect / etc
 * @property {string} fullStoryUrl - Full Story URL
 * @return {LogObject<fullStoryUrlSrc85Evid171Params>} LogObject<fullStoryUrlSrc85Evid171Params>
 */
export function fullStoryUrlSrc85Evid171(params) {
    return { evid: 171, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'viewWixPaymentsDashboardPage' event (evid: 200)
 *
 * Event description: The user view one of the pages in the dashboard
 *
 * @param {viewWixPaymentsDashboardPageParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} verificationType - verification message
 * @property {GUID} userGuid - uuid
 * @property {boolean} verification - verification result
 * @property {string} screenResolution - Return screen resolution for device
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {boolean} hasTransfers - 1 if the user has transfers, 0 if no transfers history, null if not available
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - referralAdditionalInfo
 * @property {number} pageNumber - current page number on the board
 * @property {string} message - top bar message
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @property {string} fullStoryUrl - Full Story URL
 * @property {string} type - Type of the page: empty page no premium / empty page no connect / etc
 * @return {LogObject<viewWixPaymentsDashboardPageParams>} LogObject<viewWixPaymentsDashboardPageParams>
 */
export function viewWixPaymentsDashboardPage(params) {
    return { evid: 200, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'openForBusinessSubmitted' event (evid: 2001)
 *
 * Event description: Open for business
 *
 * @param {openForBusinessSubmittedParams} params - The parameters for this event
 * @property {string} providerAccountId - PagarMe account id
 * @property {GUID} externalMerchantId - uuid
 * @property {GUID} accountId - internal id for wix payments
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - internal id for wix payments
 * @property {string} wpGeo - wp_geo
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {string} tenantId - currently just wix
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @return {LogObject<openForBusinessSubmittedParams>} LogObject<openForBusinessSubmittedParams>
 */
export function openForBusinessSubmitted(params) {
    return { evid: 2001, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'openForBusinessSubmissionFailure' event (evid: 2002)
 *
 * Event description: Open for business failure
 *
 * @param {openForBusinessSubmissionFailureParams} params - The parameters for this event
 * @property {string} providerAccountId - PagarMe account id
 * @property {GUID} externalMerchantId - uuid
 * @property {GUID} accountId - internal id for wix payments
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - internal id for wix payments
 * @property {string} wpGeo - wp_geo
 * @property {string} message - error message
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {string} tenantId - currently just wix
 * @property {GUID} accountProfileId - internal profile if for wix payments
 * @return {LogObject<openForBusinessSubmissionFailureParams>} LogObject<openForBusinessSubmissionFailureParams>
 */
export function openForBusinessSubmissionFailure(params) {
    return { evid: 2002, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'statusUpdated' event (evid: 2003)
 *
 * Event description: account status update
 *
 * @param {statusUpdatedParams} params - The parameters for this event
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {string} providerStatus - provider status
 * @property {GUID} externalMerchantId - uuid
 * @property {string} newStatus - new status
 * @property {GUID} accountId - internal id for wix payments
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - internal id for wix payments
 * @property {string} paymentProvider - provider under Wix Payments
 * @property {string} wpGeo - wp_geo
 * @property {string} oldStatus - old status
 * @property {string} message - error message
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {string} tenantId - currently just wix
 * @property {GUID} accountProfileId - internal profile id
 * @return {LogObject<statusUpdatedParams>} LogObject<statusUpdatedParams>
 */
export function statusUpdated(params) {
    return { evid: 2003, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'siteUrlUpdated' event (evid: 2004)
 *
 * Event description: site url is updated after site publishing
 *
 * @param {siteUrlUpdatedParams} params - The parameters for this event
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {GUID} externalMerchantId - uuid
 * @property {GUID} accountId - internal id for wix payments
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - internal id for wix payments
 * @property {string} wpGeo - wp_geo
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {string} tenantId - currently just wix
 * @property {GUID} accountProfileId - internal id
 * @property {string} siteUrl - site url
 * @return {LogObject<siteUrlUpdatedParams>} LogObject<siteUrlUpdatedParams>
 */
export function siteUrlUpdated(params) {
    return { evid: 2004, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createOrUpdateAccount' event (evid: 2005)
 *
 * Event description: New account was created / update
 *
 * @param {createOrUpdateAccountParams} params - The parameters for this event
 * @property {string} providerAccountId - PagarMe account identifier
 * @property {string} provider - provider on wp side
 * @property {string} anticipationTerms - anticipation terms
 * @property {GUID} externalMerchantId - uuid
 * @property {GUID} accountId - internal id for wix payments
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - merchant id in wix pay
 * @property {boolean} isNew - is it a new account
 * @property {string} accountType - personal or business
 * @property {string} wpGeo - wp_geo
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {string} tenantId - currently just wix
 * @property {GUID} accountProfileId - internal profile id
 * @return {LogObject<createOrUpdateAccountParams>} LogObject<createOrUpdateAccountParams>
 */
export function createOrUpdateAccount(params) {
    return { evid: 2005, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'accountVerificationUpdate' event (evid: 2006)
 *
 * Event description: The provider sent a request or an update about the verifications for the account
 *
 * @param {accountVerificationUpdateParams} params - The parameters for this event
 * @property {string} verificationType - verification type like : identity, document, bank_account, company
 * @property {string} verificationStatus - verification status like: passed/data_provided, etc..
 * @property {string} providerAccountId - provider account id
 * @property {string} description - any extra info
 * @property {GUID} externalMerchantId - UUID
 * @property {GUID} accountId - account id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {string} code - verification code
 * @property {GUID} merchantId - merchant id
 * @property {string} status - CURRENT account status
 * @property {string} wpGeo - GEO
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {string} tenantId - tenant (currently just cashier)
 * @property {GUID} accountProfileId - Profile id
 * @return {LogObject<accountVerificationUpdateParams>} LogObject<accountVerificationUpdateParams>
 */
export function accountVerificationUpdate(params) {
    return { evid: 2006, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'accountTierUpdate' event (evid: 2007)
 *
 * Event description: The provider sent a request or an update about the verifications for the account
 *
 * @param {accountTierUpdateParams} params - The parameters for this event
 * @property {string} providerAccountId - provider account id
 * @property {GUID} externalMerchantId - UUID
 * @property {GUID} accountId - account id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - merchant id
 * @property {string} status - CURRENT account status
 * @property {string} newTier - the new tier
 * @property {string} wpGeo - GEO
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {string} tenantId - tenant (currently just cashier)
 * @property {GUID} accountProfileId - Profile id
 * @return {LogObject<accountTierUpdateParams>} LogObject<accountTierUpdateParams>
 */
export function accountTierUpdate(params) {
    return { evid: 2007, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'requestToSmokeDetector' event (evid: 2008)
 *
 * Event description: Request to Smoke_Detector was sent
 *
 * @param {requestToSmokeDetectorParams} params - The parameters for this event
 * @property {GUID} externalMerchantId - UUID
 * @property {GUID} accountId - account id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - merchant id
 * @property {string} wpGeo - GEO
 * @property {string} tenantId - tenant (currently just cashier)
 * @return {LogObject<requestToSmokeDetectorParams>} LogObject<requestToSmokeDetectorParams>
 */
export function requestToSmokeDetector(params) {
    return { evid: 2008, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'smokeDetectorScanUpdate' event (evid: 2009)
 *
 * Event description: Received answer from Smoke_Detector with scan results
 *
 * @param {smokeDetectorScanUpdateParams} params - The parameters for this event
 * @property {GUID} externalMerchantId - UUID
 * @property {string} result - scan result: Initiated, NoDecision, Passed, NotPassed
 * @property {string} riskScore - Type of risk: No Risk, Low Risk, Medium Risk, Critical Risk, High Risk
 * @property {GUID} accountId - account id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {string} accountStatus - Final status received from provider status + smoke_detector risk score
 * @property {GUID} merchantId - merchant id
 * @property {string} wpGeo - GEO
 * @property {GUID} subscription_id - EverCompliant ID - subscription id on evercompliant side
 * @property {string} tenantId - tenant (currently just cashier)
 * @property {string} type - scan type: initialized / first scan / recurring
 * @return {LogObject<smokeDetectorScanUpdateParams>} LogObject<smokeDetectorScanUpdateParams>
 */
export function smokeDetectorScanUpdate(params) {
    return { evid: 2009, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'changeFiltersTransferHistory' event (evid: 201)
 *
 * Event description: The user changes his search filters
 *
 * @param {changeFiltersTransferHistoryParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {boolean} hasTransfers - 1 if the user has transfers, 0 if no transfers history, null if not available
 * @property {DateTime} fromDate - start date
 * @property {string} status - what statuses did the user choose to see (list if more than 1)
 * @property {string} sortOrder - 'Asc' - default, 'Desc'
 * @property {string} wpGeo - wp_geo
 * @property {DateTime} toDate - end date
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<changeFiltersTransferHistoryParams>} LogObject<changeFiltersTransferHistoryParams>
 */
export function changeFiltersTransferHistory(params) {
    return { evid: 201, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'accountManualReviewUpdate' event (evid: 2010)
 *
 * Event description: Manual review result
 *
 * @param {accountManualReviewUpdateParams} params - The parameters for this event
 * @property {GUID} externalMerchantId - UUID
 * @property {GUID} accountId - account id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - merchant id
 * @property {string} status - manual review status: started/passed/not_passed
 * @property {string} wpGeo - GEO
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {string} tenantId - tenant (currently just cashier)
 * @return {LogObject<accountManualReviewUpdateParams>} LogObject<accountManualReviewUpdateParams>
 */
export function accountManualReviewUpdate(params) {
    return { evid: 2010, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'smokeDetectorUnregisterMerchant' event (evid: 2011)
 *
 * Event description: Unregister from Smoke_Detector
 *
 * @param {smokeDetectorUnregisterMerchantParams} params - The parameters for this event
 * @property {GUID} externalMerchantId - wix uuid
 * @property {string} riskScore - Type of risk: No Risk, Low Risk, Medium Risk, Critical Risk, High Risk
 * @property {GUID} accountId - account id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - wix msid
 * @property {string} accountStatus - Final status received from provider status + smoke_detector risk score
 * @property {GUID} merchantId - merchant id
 * @property {string} wpGeo - GEO
 * @property {boolean} dormant - dormant
 * @property {GUID} subscription_id - EverCompliant ID - subscription id on evercompliant side
 * @property {string} tenantId - tenant (currently just cashier)
 * @return {LogObject<smokeDetectorUnregisterMerchantParams>} LogObject<smokeDetectorUnregisterMerchantParams>
 */
export function smokeDetectorUnregisterMerchant(params) {
    return { evid: 2011, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'smokeDetectorScanRestartForced' event (evid: 2012)
 *
 * Event description: Force SD scan after site was published
 *
 * @param {smokeDetectorScanRestartForcedParams} params - The parameters for this event
 * @property {GUID} externalMerchantId - wix uuid
 * @property {GUID} accountId - account id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - wix msid
 * @property {GUID} merchantId - merchant id
 * @property {string} wpGeo - GEO
 * @property {GUID} subscription_id - EverCompliant ID - subscription id on evercompliant side
 * @property {string} tenantId - tenant (currently just cashier)
 * @return {LogObject<smokeDetectorScanRestartForcedParams>} LogObject<smokeDetectorScanRestartForcedParams>
 */
export function smokeDetectorScanRestartForced(params) {
    return { evid: 2012, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'pingSmsWasInitiated' event (evid: 2013)
 *
 * Event description: Ping SMS was initiated   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {pingSmsWasInitiatedParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} externalMerchantId - uuid
 * @property {GUID} visitor_id - visitor_id
 * @property {string} templateName - sms template name
 * @property {GUID} accountId - wix payments accountId
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {string} wpGeo - wp_geo
 * @property {string} correlationId - ping notification id
 * @property {GUID} msid - msid
 * @property {GUID} transactionId - transaction_id
 * @return {LogObject<pingSmsWasInitiatedParams>} LogObject<pingSmsWasInitiatedParams>
 */
export function pingSmsWasInitiated(params) {
    return { evid: 2013, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutPolicyAssignPolicy' event (evid: 2014)
 *
 * Event description: Assign Payout policy
 *
 * @param {payoutPolicyAssignPolicyParams} params - The parameters for this event
 * @property {string} flowType - migration / default for new / by risk team etc.
 * @property {GUID} externalMerchantId - uuid
 * @property {string} payoutPolicyId - payout_policy_id
 * @property {GUID} accountId - internal account_id of Wix Payments
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - internal id for wix payments
 * @property {string} wpGeo - wp geo
 * @property {string} tenantId - currently just wix
 * @property {GUID} accountProfileId - internal profile id
 * @return {LogObject<payoutPolicyAssignPolicyParams>} LogObject<payoutPolicyAssignPolicyParams>
 */
export function payoutPolicyAssignPolicy(params) {
    return { evid: 2014, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutPolicyUnassignPolicy' event (evid: 2015)
 *
 * Event description: Unassign Payout policy
 *
 * @param {payoutPolicyUnassignPolicyParams} params - The parameters for this event
 * @property {string} flowType - migration / default for new / by risk team etc.
 * @property {GUID} externalMerchantId - uuid
 * @property {string} payoutPolicyId - payout_policy_id
 * @property {GUID} accountId - internal account_id of Wix Payments
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - internal id for wix payments
 * @property {string} wpGeo - wp geo
 * @property {string} tenantId - currently just wix
 * @property {GUID} accountProfileId - internal profile id
 * @return {LogObject<payoutPolicyUnassignPolicyParams>} LogObject<payoutPolicyUnassignPolicyParams>
 */
export function payoutPolicyUnassignPolicy(params) {
    return { evid: 2015, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutPolicyUpdatePayoutSettings' event (evid: 2016)
 *
 * Event description: Update settings (daily / weekly / monthly)
 *
 * @param {payoutPolicyUpdatePayoutSettingsParams} params - The parameters for this event
 * @property {string} flowType - migration / default for new / by risk team etc.
 * @property {GUID} externalMerchantId - uuid
 * @property {string} payoutPolicyId - payout_policy_id
 * @property {GUID} accountId - internal account_id of Wix Payments
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - internal id for wix payments
 * @property {string} wpGeo - wp geo
 * @property {string} frequency - payout_frequency
 * @property {string} tenantId - currently just wix
 * @property {GUID} accountProfileId - internal profile id
 * @property {number} days - payout_day
 * @return {LogObject<payoutPolicyUpdatePayoutSettingsParams>} LogObject<payoutPolicyUpdatePayoutSettingsParams>
 */
export function payoutPolicyUpdatePayoutSettings(params) {
    return { evid: 2016, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutPolicyComplainForReservationFunds' event (evid: 2017)
 *
 * Event description: Merchant complains for Reservation funds
 *
 * @param {payoutPolicyComplainForReservationFundsParams} params - The parameters for this event
 * @property {GUID} externalMerchantId - uuid
 * @property {string} payoutPolicyId - payout_policy_id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - internal id for wix payments
 * @property {string} wpGeo - wp geo
 * @property {string} tenantId - currently just wix
 * @property {GUID} accountProfileId - internal profile id
 * @return {LogObject<payoutPolicyComplainForReservationFundsParams>} LogObject<payoutPolicyComplainForReservationFundsParams>
 */
export function payoutPolicyComplainForReservationFunds(params) {
    return { evid: 2017, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutPolicyComplaintReviewResultForReservationFunds' event (evid: 2018)
 *
 * Event description: Risk team reviewed complaint for Reservation funds
 *
 * @param {payoutPolicyComplaintReviewResultForReservationFundsParams} params - The parameters for this event
 * @property {GUID} externalMerchantId - uuid
 * @property {string} payoutPolicyId - payout_policy_id
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - msid
 * @property {GUID} merchantId - internal id for wix payments
 * @property {string} wpGeo - wp geo
 * @property {string} tenantId - currently just wix
 * @property {GUID} accountProfileId - internal profile id
 * @return {LogObject<payoutPolicyComplaintReviewResultForReservationFundsParams>} LogObject<payoutPolicyComplaintReviewResultForReservationFundsParams>
 */
export function payoutPolicyComplaintReviewResultForReservationFunds(params) {
    return { evid: 2018, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'rfpInvoiceWasInitiated' event (evid: 2019)
 *
 * Event description: RFP invoice was initiated
 *
 * @param {rfpInvoiceWasInitiatedParams} params - The parameters for this event
 * @property {GUID} uuid - uuid
 * @property {GUID} accountId - wix payments account id
 * @property {string} invoiceId - invoice
 * @property {string} user_agent - user_agent
 * @property {number} amount - amount multiplied by 100 000
 * @property {string} wpGeo - wp geo
 * @property {GUID} accountProfileId - wix payments account profile id
 * @property {GUID} msid - uuid
 * @return {LogObject<rfpInvoiceWasInitiatedParams>} LogObject<rfpInvoiceWasInitiatedParams>
 */
export function rfpInvoiceWasInitiated(params) {
    return { evid: 2019, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickSaveBankDetails' event (evid: 202)
 *
 * Event description: The user clicked on save button
 *
 * @param {clickSaveBankDetailsParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - uuid
 * @property {boolean} verification - verification result
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} fields - name of the updated fields (list if more than 1)
 * @property {string} documentType - type of document that was added / changed
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} type - setting types for WixPayments onboarding: Bank | Personal | Company
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<clickSaveBankDetailsParams>} LogObject<clickSaveBankDetailsParams>
 */
export function clickSaveBankDetails(params) {
    return { evid: 202, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'hidingProhibitedProductsApiCallFeaturesSrc85Evid2020' event (evid: 2020)
 *
 * Event description: This event returns the results of the BED service which draws from BI and hides prohibited products accordingly.
 *
 * @param {hidingProhibitedProductsApiCallFeaturesSrc85Evid2020Params} params - The parameters for this event
 * @property {boolean} emailSent - Was the email sent successfully?
 * @property {boolean} boActivityCreated - Was BO note written successfully?
 * @property {boolean} dashboardNotificationSent - Was notification sent successfully?
 * @property {string} flow - The action taken on the product.
 * @property {string} prohibitedProducts - relevant data for the products on which the action was taken.
 * @property {GUID} siteId - msid for which the products actions were taken.
 * @property {string} prohibitedProductsGuidList - the product guids on which the action was taken.
 * @return {LogObject<hidingProhibitedProductsApiCallFeaturesSrc85Evid2020Params>} LogObject<hidingProhibitedProductsApiCallFeaturesSrc85Evid2020Params>
 */
export function hidingProhibitedProductsApiCallFeaturesSrc85Evid2020(params) {
    return { evid: 2020, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'changedBankDetailsSuccess' event (evid: 203)
 *
 * Event description: The user successfully changed his account settings
 *
 * @param {changedBankDetailsSuccessParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} fields - name of the fields (list if more than 1)
 * @property {string} documentType - type of document that was added / changed
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} type - setting types for WixPayments onboarding: Bank | Personal | Company
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<changedBankDetailsSuccessParams>} LogObject<changedBankDetailsSuccessParams>
 */
export function changedBankDetailsSuccess(params) {
    return { evid: 203, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'changedBankDetailsValidationError' event (evid: 204)
 *
 * Event description: The user had a validation error when trying to change his details
 *
 * @param {changedBankDetailsValidationErrorParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} fields - changed fields (list if more than 1)
 * @property {string} invalidFields - invalid fields in the form (list if more than 1)
 * @property {string} documentType - type of document that was added / changed
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} type - setting types for WixPayments onboarding: Bank | Personal | Company
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<changedBankDetailsValidationErrorParams>} LogObject<changedBankDetailsValidationErrorParams>
 */
export function changedBankDetailsValidationError(params) {
    return { evid: 204, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'changedAnticipationSettings' event (evid: 205)
 *
 * Event description: The user successfully changed his anticipation settings
 *
 * @param {changedAnticipationSettingsParams} params - The parameters for this event
 * @property {string} anticipationTerms - anticipation terms
 * @property {string} oldAnticipationTerms - old anticipation terms
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<changedAnticipationSettingsParams>} LogObject<changedAnticipationSettingsParams>
 */
export function changedAnticipationSettings(params) {
    return { evid: 205, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'changedInstallmentsSettings' event (evid: 206)
 *
 * Event description: The user successfully changed his installments settings
 *
 * @param {changedInstallmentsSettingsParams} params - The parameters for this event
 * @property {number} oldApplyAmount - old apply amount
 * @property {GUID} userGuid - uuid
 * @property {string} oldMaxInstallmentsDigit - old max installment
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} oldMinInstallmentsDigit - old min installment
 * @property {string} maxInstallmentsDigit - max installment
 * @property {string} status - 0 for off 1 for on
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @property {string} minInstallmentsDigit - min installment
 * @property {number} applyAmount - applied amount
 * @return {LogObject<changedInstallmentsSettingsParams>} LogObject<changedInstallmentsSettingsParams>
 */
export function changedInstallmentsSettings(params) {
    return { evid: 206, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'createAccountClick' event (evid: 207)
 *
 * Event description: The user clicked on create an account in account settings page
 *
 * @param {createAccountClickParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} wpGeo - wp_geo
 * @property {string} place - settings/top-bar, etc
 * @property {string} pageType - transfer history/manage funds/account settings
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<createAccountClickParams>} LogObject<createAccountClickParams>
 */
export function createAccountClick(params) {
    return { evid: 207, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickTopBar' event (evid: 208)
 *
 * Event description: The user clicked on warning top-bar
 *
 * @param {clickTopBarParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} wpGeo - wp_geo
 * @property {string} message - learn more / review activation instructions / terms and conditions, etc
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<clickTopBarParams>} LogObject<clickTopBarParams>
 */
export function clickTopBar(params) {
    return { evid: 208, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'viewErrorPopupCreateNewAccount' event (evid: 209)
 *
 * Event description: When a user creates a 3rd account per day and than try to create 4th, he will see warning pop-up
 *
 * @param {viewErrorPopupCreateNewAccountParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} wpGeo - wp_geo
 * @property {string} place - settings/top-bar
 * @property {string} pageType - transfer history/manage funds/account settings
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<viewErrorPopupCreateNewAccountParams>} LogObject<viewErrorPopupCreateNewAccountParams>
 */
export function viewErrorPopupCreateNewAccount(params) {
    return { evid: 209, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'accountFundsShown' event (evid: 210)
 *
 * Event description: When users see a number of available funds / or don’t see.
 *
 * @param {accountFundsShownParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {number} pendingFunds - number of pending funds  - multiply by 100,000
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} wpGeo - wp_geo
 * @property {number} availableFunds - number of available funds  - multiply by 100,000
 * @property {string} message - 1 = success, 0 = failure
 * @property {string} pageType - transfer history/manage funds/account settings
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<accountFundsShownParams>} LogObject<accountFundsShownParams>
 */
export function accountFundsShown(params) {
    return { evid: 210, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'onboardingPageLearnMore' event (evid: 211)
 *
 * Event description: When users click on learn more
 *
 * @param {onboardingPageLearnMoreParams} params - The parameters for this event
 * @property {boolean} hasQualifiedPremium - yes / no
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} usageType - personal / business
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - where users come from
 * @property {string} place - e.g. partners_dashboard
 * @property {boolean} isPremium - yes / no
 * @property {GUID} msid - msid
 * @return {LogObject<onboardingPageLearnMoreParams>} LogObject<onboardingPageLearnMoreParams>
 */
export function onboardingPageLearnMore(params) {
    return { evid: 211, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myTransfersClickTransferFunds' event (evid: 212)
 *
 * Event description: When users click on ClickTransferFunds
 *
 * @param {myTransfersClickTransferFundsParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - where users come from
 * @property {number} availableFunds - (multiply by 100000)
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<myTransfersClickTransferFundsParams>} LogObject<myTransfersClickTransferFundsParams>
 */
export function myTransfersClickTransferFunds(params) {
    return { evid: 212, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myTransfersCloseTransferFundsPopUp' event (evid: 213)
 *
 * Event description: When users close ClickTransferFunds pop-up.
 *
 * @param {myTransfersCloseTransferFundsPopUpParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - where users come from
 * @property {number} availableFunds - (multiply by 100000)
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {GUID} msid - msid
 * @return {LogObject<myTransfersCloseTransferFundsPopUpParams>} LogObject<myTransfersCloseTransferFundsPopUpParams>
 */
export function myTransfersCloseTransferFundsPopUp(params) {
    return { evid: 213, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myTransfersUserTransferredFunds' event (evid: 214)
 *
 * Event description: When users transferred funds
 *
 * @param {myTransfersUserTransferredFundsParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Active/Blocked/Suspended, etc
 * @property {string} wpGeo - wp_geo
 * @property {string} referralAdditionalInfo - where users come from
 * @property {number} availableFunds - available funds
 * @property {string} message - success, validation error, server error
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {GUID} accountProfileId - internal profile id for wix payments
 * @property {number} transferredFunds - (multiply by 100000)
 * @property {GUID} msid - msid
 * @return {LogObject<myTransfersUserTransferredFundsParams>} LogObject<myTransfersUserTransferredFundsParams>
 */
export function myTransfersUserTransferredFunds(params) {
    return { evid: 214, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myFundsChangedFilters' event (evid: 215)
 *
 * Event description: The user changes the filters exists on my funds page
 *
 * @param {myFundsChangedFiltersParams} params - The parameters for this event
 * @property {string} filterType - list of applied filters (date / transaction or order)
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - profile status
 * @property {string} removalType - list of removed filters (date / transaction or order)
 * @property {DateTime} fromDate - start date
 * @property {string} wpGeo - wp_geo
 * @property {string} target - on which section filter was applied - transaction table / payments overview
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {DateTime} toDate - end date
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} selectedTypes - selected type in the filter
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @return {LogObject<myFundsChangedFiltersParams>} LogObject<myFundsChangedFiltersParams>
 */
export function myFundsChangedFilters(params) {
    return { evid: 215, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myFundsAboutThisTableLearnMore' event (evid: 216)
 *
 * Event description: Learn more button about the my funds table
 *
 * @param {myFundsAboutThisTableLearnMoreParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - Profile status
 * @property {string} wpGeo - wp_geo
 * @property {string} place - about-this-table/learn-more
 * @property {string} pageType - transactions / report / payouts and etc.
 * @property {string} type - e.g. type of modal
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @return {LogObject<myFundsAboutThisTableLearnMoreParams>} LogObject<myFundsAboutThisTableLearnMoreParams>
 */
export function myFundsAboutThisTableLearnMore(params) {
    return { evid: 216, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myFundsDownloadCsvClick' event (evid: 217)
 *
 * Event description: CSV download. When page_type IN NULL then its "Dashboard > My funds > Download CSV click"
 *
 * @param {myFundsDownloadCsvClickParams} params - The parameters for this event
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - profile status
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @return {LogObject<myFundsDownloadCsvClickParams>} LogObject<myFundsDownloadCsvClickParams>
 */
export function myFundsDownloadCsvClick(params) {
    return { evid: 217, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'myFundsClickOnPayoutDetails' event (evid: 218)
 *
 * Event description: A user clicked on a certain payout to see its info
 *
 * @param {myFundsClickOnPayoutDetailsParams} params - The parameters for this event
 * @property {string} payoutId - the payout id
 * @property {string} clickType - last/next/pending/etc
 * @property {string} screenResolution - screen_resolution
 * @property {number} amount - payout amount
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp_geo
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - site guid
 * @return {LogObject<myFundsClickOnPayoutDetailsParams>} LogObject<myFundsClickOnPayoutDetailsParams>
 */
export function myFundsClickOnPayoutDetails(params) {
    return { evid: 218, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userScrollDown' event (evid: 219)
 *
 * Event description: When a user is scrolling a table, fired every time a new set of results are loaded
 *
 * @param {userScrollDownParams} params - The parameters for this event
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - wp_geo
 * @property {string} pageType - the page where it happened
 * @property {GUID} accountProfileId - profile_id
 * @property {GUID} msid - site guid
 * @return {LogObject<userScrollDownParams>} LogObject<userScrollDownParams>
 */
export function userScrollDown(params) {
    return { evid: 219, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickOnTransaction' event (evid: 220)
 *
 * Event description: User click on transaction
 *
 * @param {clickOnTransactionParams} params - The parameters for this event
 * @property {string} orderID - order ID
 * @property {string} displayType - transaction type on UI
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {GUID} userGuid - UUID
 * @property {string} transactionType - transaction type
 * @property {GUID} transactionid - Transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {number} amount - Multiplied by 100000
 * @property {string} accountStatus - account status
 * @property {string} transactionStatus - status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - WP GEO
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - profile ID
 * @property {GUID} msid - msid
 * @return {LogObject<clickOnTransactionParams>} LogObject<clickOnTransactionParams>
 */
export function clickOnTransaction(params) {
    return { evid: 220, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'viewTransaction' event (evid: 221)
 *
 * Event description: User view transaction's details
 *
 * @param {viewTransactionParams} params - The parameters for this event
 * @property {string} referralInfo - Place where user come from
 * @property {string} orderID - order ID
 * @property {string} displayType - transaction type on UI
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {GUID} userGuid - UUID
 * @property {string} transactionType - transaction type
 * @property {GUID} transactionid - Transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {number} amount - Multiplied by 100000
 * @property {string} accountStatus - account status
 * @property {string} transactionStatus - status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - WP GEO
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - profile ID
 * @property {GUID} msid - msid
 * @return {LogObject<viewTransactionParams>} LogObject<viewTransactionParams>
 */
export function viewTransaction(params) {
    return { evid: 221, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundWidgetClickRefundButton' event (evid: 222)
 *
 * Event description: User click refund button
 *
 * @param {refundWidgetClickRefundButtonParams} params - The parameters for this event
 * @property {string} orderID - order ID
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {GUID} userGuid - UUID
 * @property {GUID} transactionid - Transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} transactionStatus - status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - WP GEO
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {boolean} isEnabled - TRUE - enabled / FALSE - disabled (e.g. for NB trx)
 * @property {GUID} accountProfileId - profile ID
 * @property {GUID} msid - msid
 * @return {LogObject<refundWidgetClickRefundButtonParams>} LogObject<refundWidgetClickRefundButtonParams>
 */
export function refundWidgetClickRefundButton(params) {
    return { evid: 222, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundWidgetViewRefundBanner' event (evid: 223)
 *
 * Event description: User view the refund banner
 *
 * @param {refundWidgetViewRefundBannerParams} params - The parameters for this event
 * @property {number} refundable - the amount which can be refunded
 * @property {string} orderID - order ID
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {GUID} userGuid - UUID
 * @property {GUID} transactionid - Transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} transactionStatus - status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - WP GEO
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - profile ID
 * @property {GUID} msid - msid
 * @return {LogObject<refundWidgetViewRefundBannerParams>} LogObject<refundWidgetViewRefundBannerParams>
 */
export function refundWidgetViewRefundBanner(params) {
    return { evid: 223, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundWidgetClickRefundNow' event (evid: 224)
 *
 * Event description: User click refund after he choose the amount
 *
 * @param {refundWidgetClickRefundNowParams} params - The parameters for this event
 * @property {number} refundable - the amount which can be refunded
 * @property {string} orderID - order ID
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {GUID} userGuid - UUID
 * @property {GUID} transactionid - Transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {number} amount - the amount the user choose to refund
 * @property {string} accountStatus - account status
 * @property {string} transactionStatus - status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - WP GEO
 * @property {string} referralAdditionalInfo - referral
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} notes - refund description (inputted text)
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - profile ID
 * @property {GUID} msid - msid
 * @return {LogObject<refundWidgetClickRefundNowParams>} LogObject<refundWidgetClickRefundNowParams>
 */
export function refundWidgetClickRefundNow(params) {
    return { evid: 224, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundWidgetValidationErrorInRefundBanner' event (evid: 225)
 *
 * Event description: Validation error like wrong amount typed in the refund banner
 *
 * @param {refundWidgetValidationErrorInRefundBannerParams} params - The parameters for this event
 * @property {number} refundable - the amount which can be refunded
 * @property {string} orderID - order ID
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {GUID} userGuid - UUID
 * @property {GUID} transactionid - Transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {number} amount - the amount the user choose to refund
 * @property {string} accountStatus - account status
 * @property {string} transactionStatus - status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - WP GEO
 * @property {string} referralAdditionalInfo - referral
 * @property {string} message - error message
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - profile ID
 * @property {GUID} msid - msid
 * @return {LogObject<refundWidgetValidationErrorInRefundBannerParams>} LogObject<refundWidgetValidationErrorInRefundBannerParams>
 */
export function refundWidgetValidationErrorInRefundBanner(params) {
    return { evid: 225, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundWidgetViewAreYouSure' event (evid: 226)
 *
 * Event description: view are you sure
 *
 * @param {refundWidgetViewAreYouSureParams} params - The parameters for this event
 * @property {number} refundable - the amount which can be refunded
 * @property {string} orderID - order ID
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {GUID} userGuid - UUID
 * @property {GUID} transactionid - Transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {number} amount - the amount the user choose to refund
 * @property {string} accountStatus - account status
 * @property {string} transactionStatus - status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - WP GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - profile ID
 * @property {GUID} msid - msid
 * @return {LogObject<refundWidgetViewAreYouSureParams>} LogObject<refundWidgetViewAreYouSureParams>
 */
export function refundWidgetViewAreYouSure(params) {
    return { evid: 226, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'refundWidgetConfirmRefund' event (evid: 227)
 *
 * Event description: User confirmed the refund
 *
 * @param {refundWidgetConfirmRefundParams} params - The parameters for this event
 * @property {number} refundable - the amount which can be refunded
 * @property {string} orderID - order ID
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {GUID} userGuid - UUID
 * @property {GUID} transactionid - Transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {number} amount - the amount the user choose to refund
 * @property {string} accountStatus - account status
 * @property {string} transactionStatus - status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - WP GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {GUID} accountProfileId - profile ID
 * @property {GUID} msid - msid
 * @return {LogObject<refundWidgetConfirmRefundParams>} LogObject<refundWidgetConfirmRefundParams>
 */
export function refundWidgetConfirmRefund(params) {
    return { evid: 227, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'viewDashboardPopUp' event (evid: 228)
 *
 * Event description: User view pop up in the WixPay dashboard
 *
 * @param {viewDashboardPopUpParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - what page in the dashboard
 * @property {string} popupType - what pop up is it
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @return {LogObject<viewDashboardPopUpParams>} LogObject<viewDashboardPopUpParams>
 */
export function viewDashboardPopUp(params) {
    return { evid: 228, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'interactionWithThePopUp' event (evid: 229)
 *
 * Event description: User interact with pop up in the WixPay dashboard
 *
 * @param {interactionWithThePopUpParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - what page in the dashboard
 * @property {string} type - click type
 * @property {string} popupType - what pop up is it
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @return {LogObject<interactionWithThePopUpParams>} LogObject<interactionWithThePopUpParams>
 */
export function interactionWithThePopUp(params) {
    return { evid: 229, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'identificationMethodChoosen' event (evid: 230)
 *
 * Event description: user choose an identification method
 *
 * @param {identificationMethodChoosenParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} type - click type
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @property {boolean} isDocumentTypeAlreadyUsed - true if user already used this type of document
 * @return {LogObject<identificationMethodChoosenParams>} LogObject<identificationMethodChoosenParams>
 */
export function identificationMethodChoosen(params) {
    return { evid: 230, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'addImage' event (evid: 231)
 *
 * Event description: User clicked on the tile to upload Doc
 *
 * @param {addImageParams} params - The parameters for this event
 * @property {string} validationMessage - valid/validation error message
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - bank statement/identification...
 * @property {string} type - choosen method
 * @property {GUID} accountProfileId - profile id
 * @property {string} subType - back/front image
 * @property {GUID} msid - msid
 * @return {LogObject<addImageParams>} LogObject<addImageParams>
 */
export function addImage(params) {
    return { evid: 231, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'clickSubmitForVerification' event (evid: 232)
 *
 * Event description: User clicks to submit the document that he uploaded
 *
 * @param {clickSubmitForVerificationParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - bank statement/identification...
 * @property {string} type - choosen method
 * @property {GUID} accountProfileId - profile id
 * @property {string} subType - back/front image
 * @property {GUID} msid - msid
 * @return {LogObject<clickSubmitForVerificationParams>} LogObject<clickSubmitForVerificationParams>
 */
export function clickSubmitForVerification(params) {
    return { evid: 232, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dashboardVerificationResult' event (evid: 233)
 *
 * Event description: user see validation error in the dashboard
 *
 * @param {dashboardVerificationResultParams} params - The parameters for this event
 * @property {string} verificationType - verification message
 * @property {GUID} userGuid - uuid
 * @property {boolean} verification - verification success
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - GEO
 * @property {string} message - top bar message
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - what page in the dashboard
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @return {LogObject<dashboardVerificationResultParams>} LogObject<dashboardVerificationResultParams>
 */
export function dashboardVerificationResult(params) {
    return { evid: 233, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'imageSubmissionResult' event (evid: 234)
 *
 * Event description: user submit the image - result
 *
 * @param {imageSubmissionResultParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} success - success
 * @property {string} subtype - subtype of document
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - bank statement/identification...
 * @property {string} type - choosen method
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @return {LogObject<imageSubmissionResultParams>} LogObject<imageSubmissionResultParams>
 */
export function imageSubmissionResult(params) {
    return { evid: 234, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userClickedOnPagination' event (evid: 235)
 *
 * Event description: When a user clicks on pagination, fired every time when user make a click
 *
 * @param {userClickedOnPaginationParams} params - The parameters for this event
 * @property {number} nextPageNumber - selected page number when user clicks on pagination
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - WP geo
 * @property {number} pageNumber - current page number on the board
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - the page where it happened
 * @property {GUID} accountProfileId - profile_id
 * @property {GUID} msid - site guid
 * @return {LogObject<userClickedOnPaginationParams>} LogObject<userClickedOnPaginationParams>
 */
export function userClickedOnPagination(params) {
    return { evid: 235, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'transactionDetailsViewOrder' event (evid: 236)
 *
 * Event description: User view order's details
 *
 * @param {transactionDetailsViewOrderParams} params - The parameters for this event
 * @property {string} orderID - order ID
 * @property {string} paymentMethod - paymentMethod (credit card / sofort / etc.)
 * @property {GUID} transactionid - Transaction ID
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} transactionStatus - status
 * @property {string} paymentProvider - paymentProvider (Wix Payments / Stripe / etc.)
 * @property {string} wpGeo - WP GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transaction / report / transaction details
 * @property {GUID} accountProfileId - profile ID
 * @return {LogObject<transactionDetailsViewOrderParams>} LogObject<transactionDetailsViewOrderParams>
 */
export function transactionDetailsViewOrder(params) {
    return { evid: 236, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dashboardClickOnFilterButton' event (evid: 237)
 *
 * Event description: User clicked on Filter Button
 *
 * @param {dashboardClickOnFilterButtonParams} params - The parameters for this event
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - WP GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transaction / report / transaction details
 * @property {GUID} accountProfileId - profile ID
 * @return {LogObject<dashboardClickOnFilterButtonParams>} LogObject<dashboardClickOnFilterButtonParams>
 */
export function dashboardClickOnFilterButton(params) {
    return { evid: 237, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dashboardClickOnContextMenu' event (evid: 238)
 *
 * Event description: User clicked on Context Menu (e.g. (...) ) or on Menu item
 *
 * @param {dashboardClickOnContextMenuParams} params - The parameters for this event
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - WP GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transaction / report / transaction details
 * @property {string} type - 3 dots / menu name etc.
 * @property {GUID} accountProfileId - profile ID
 * @return {LogObject<dashboardClickOnContextMenuParams>} LogObject<dashboardClickOnContextMenuParams>
 */
export function dashboardClickOnContextMenu(params) {
    return { evid: 238, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dashboardClickOnShowDetails' event (evid: 239)
 *
 * Event description: User clicked on Show Details
 *
 * @param {dashboardClickOnShowDetailsParams} params - The parameters for this event
 * @property {string} sectionName - What section was clicked to show details
 * @property {boolean} hidden - TRUE = hide details; FALSE = show details (till 21.05.2023 was opposite way)
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - account status
 * @property {string} wpGeo - WP GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transaction / report / transaction details
 * @property {GUID} accountProfileId - profile ID
 * @return {LogObject<dashboardClickOnShowDetailsParams>} LogObject<dashboardClickOnShowDetailsParams>
 */
export function dashboardClickOnShowDetails(params) {
    return { evid: 239, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'userClickedOnScrollButton' event (evid: 240)
 *
 * Event description: User clicked on Scroll button:
 * When user has issues with verification process he will have a scroll button that will lead to the closest place with issue.
 *
 * @param {userClickedOnScrollButtonParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} scrolledToBlock - closest place with issue
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - profile status
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {GUID} accountProfileId - profile id
 * @return {LogObject<userClickedOnScrollButtonParams>} LogObject<userClickedOnScrollButtonParams>
 */
export function userClickedOnScrollButton(params) {
    return { evid: 240, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dashboardExpandCollapseFilterValuesList' event (evid: 241)
 *
 * Event description: User expand / collapse Filter values list
 *
 * @param {dashboardExpandCollapseFilterValuesListParams} params - The parameters for this event
 * @property {string} filterType - selected filter
 * @property {string} screenResolution - screen_resolution
 * @property {string} wpGeo - WP GEO
 * @property {string} target - on which section filter was applied - transaction table / payments overview
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transaction / report / transaction details
 * @property {GUID} accountProfileId - profile ID
 * @property {string} value - expand / collapse
 * @return {LogObject<dashboardExpandCollapseFilterValuesListParams>} LogObject<dashboardExpandCollapseFilterValuesListParams>
 */
export function dashboardExpandCollapseFilterValuesList(params) {
    return { evid: 241, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'dashboardUseSearch' event (evid: 242)
 *
 * Event description: The user input some value in Search box and applied / clear search
 *
 * @param {dashboardUseSearchParams} params - The parameters for this event
 * @property {string} screenResolution - screen_resolution
 * @property {string} actionType - apply / clear
 * @property {string} wpGeo - wp_geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - transfer history/manage funds/account settings, etc
 * @property {string} value - filter value
 * @return {LogObject<dashboardUseSearchParams>} LogObject<dashboardUseSearchParams>
 */
export function dashboardUseSearch(params) {
    return { evid: 242, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'advanceThroughWizard' event (evid: 243)
 *
 * Event description: Advance Through Wizard
 *
 * @param {advanceThroughWizardParams} params - The parameters for this event
 * @property {string} screenResolution - screen_resolution
 * @property {string} ctaName - what CTA user clicked
 * @property {string} selectedValue - value selected in wizard
 * @property {string} wpGeo - GEO
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - what page in the dashboard
 * @property {string} nextView - next view name
 * @property {string} wizardType - e.g. Dispute Wizard / other
 * @return {LogObject<advanceThroughWizardParams>} LogObject<advanceThroughWizardParams>
 */
export function advanceThroughWizard(params) {
    return { evid: 243, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'showClickContactCustomerCareCta' event (evid: 266)
 *
 * Event description: Show / Click "contact customer care" to a user
 *
 * @param {showClickContactCustomerCareCtaParams} params - The parameters for this event
 * @property {string} channel - agent / chatbot
 * @property {string} errorMessage - key for message (kyc / compliance / other)
 * @property {string} url - link to customer care page
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - wix payments account status
 * @property {string} actionType - show / click
 * @property {string} wpGeo - GEO
 * @property {string} place - place on the page (company / personal etc.)
 * @property {string} pageType - payments_dashboard / payouts / accept_payments
 * @property {string} onboardingType - old / new onboarding
 * @property {GUID} accountProfileId - account_profile_id on wp side
 * @property {GUID} msid - msid
 * @property {string} bannerName - banner name
 * @property {string} bucketType - bucket type for KYC (UploadIdDocumentation/UploadBankStatement/Pending)
 * @property {string} pageComponentId - Page ComponentID where we redirect user
 * @return {LogObject<showClickContactCustomerCareCtaParams>} LogObject<showClickContactCustomerCareCtaParams>
 */
export function showClickContactCustomerCareCta(params) {
    return { evid: 266, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'showClickAddFunds' event (evid: 267)
 *
 * Event description: Show / Click "Add Funds" (new RFP flow)
 *
 * @param {showClickAddFundsParams} params - The parameters for this event
 * @property {string} errorMessage - key for message (kyc / compliance / other)
 * @property {number} balance - available balance / multiplied by 100000
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - wix payments account status
 * @property {string} actionType - show / click
 * @property {string} wpGeo - GEO
 * @property {string} place - place on the page (company / personal etc.)
 * @property {string} pageType - payments_dashboard / payouts / accept_payments
 * @property {GUID} accountProfileId - account_profile_id on wp side
 * @return {LogObject<showClickAddFundsParams>} LogObject<showClickAddFundsParams>
 */
export function showClickAddFunds(params) {
    return { evid: 267, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'sizeIndicatorWasShown' event (evid: 268)
 *
 * Event description: Size indicator was shown (on update of the files list)
 *
 * @param {sizeIndicatorWasShownParams} params - The parameters for this event
 * @property {number} quantity - total number of uploaded files
 * @property {string} size - total size of uploaded files
 * @property {string} state - OK / WARNING / ERROR
 * @property {string} screenResolution - screen_resolution
 * @property {string} sizeLimit - size limit
 * @property {string} wpGeo - GEO
 * @property {string} place - place on the page (company / personal etc.)
 * @property {string} pageType - payments_dashboard / payouts / accept_payments
 * @property {string} type - x large, y small
 * @property {GUID} transactionId - wp transactionId
 * @return {LogObject<sizeIndicatorWasShownParams>} LogObject<sizeIndicatorWasShownParams>
 */
export function sizeIndicatorWasShown(params) {
    return { evid: 268, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'hoverOnTheSizeIndicator' event (evid: 269)
 *
 * Event description: Hover on the Size indicator (on a specific file)
 *
 * @param {hoverOnTheSizeIndicatorParams} params - The parameters for this event
 * @property {string} size - total size of uploaded files
 * @property {string} fileName - file type
 * @property {string} screenResolution - screen_resolution
 * @property {string} fileType - file name
 * @property {string} wpGeo - GEO
 * @property {string} place - place on the page (company / personal etc.)
 * @property {string} pageType - payments_dashboard / payouts / accept_payments
 * @property {GUID} transactionId - wp transactionId
 * @return {LogObject<hoverOnTheSizeIndicatorParams>} LogObject<hoverOnTheSizeIndicatorParams>
 */
export function hoverOnTheSizeIndicator(params) {
    return { evid: 269, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'buttonClickedGeneric' event (evid: 270)
 *
 * Event description: Some button was clicked. See button name in cta_name
 *
 * @param {buttonClickedGenericParams} params - The parameters for this event
 * @property {string} orderID - order_id (if needed)
 * @property {GUID} userGuid - uuid
 * @property {string} fileName - file_name (if applicable)
 * @property {GUID} transactionid - transaction_guid (if needed)
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - WP account status
 * @property {string} ctaName - button name (e.g. connect more payment methods)
 * @property {string} wpGeo - wix payments geo (if exists)
 * @property {string} place - e.g. "bm/wp-transactions" / "bm/balance"
 * @property {string} pageType - e.g. payouts / list / details
 * @property {GUID} accountProfileId - WP account profile
 * @property {GUID} msid - msid
 * @property {string} type - Type of the page: empty page no premium / empty page no connect / specific page / etc
 * @return {LogObject<buttonClickedGenericParams>} LogObject<buttonClickedGenericParams>
 */
export function buttonClickedGeneric(params) {
    return { evid: 270, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'reportCairoTableShown' event (evid: 271)
 *
 * Event description: Parameters to enrich Cairo events (source 144, evid: 116, 117, 118 ...)
 *
 * @param {reportCairoTableShownParams} params - The parameters for this event
 * @property {GUID} cairoSessionId - Сairo Session Id
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - profile status
 * @property {string} wpGeo - WP geo
 * @property {string} target - on which section filter was applied: transaction table / payments overview
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - the page where it happened
 * @property {GUID} accountProfileId - profile id
 * @property {GUID} msid - msid
 * @return {LogObject<reportCairoTableShownParams>} LogObject<reportCairoTableShownParams>
 */
export function reportCairoTableShown(params) {
    return { evid: 271, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'bankingCardStatusSnapshot' event (evid: 272)
 *
 * Event description: Wix Banking: snapshot for the card status when open relevant page
 *
 * @param {bankingCardStatusSnapshotParams} params - The parameters for this event
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} status - status of the card: Active / Cancelled / etc
 * @property {string} wpGeo - wix payments geo (if exists)
 * @property {string} place - e.g. "bm/wp-transactions" / "bm/balance"
 * @property {string} pageType - e.g. payouts / list / details
 * @property {string} msid - msid
 * @return {LogObject<bankingCardStatusSnapshotParams>} LogObject<bankingCardStatusSnapshotParams>
 */
export function bankingCardStatusSnapshot(params) {
    return { evid: 272, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'bankingCardFlowFedVerificationProcess' event (evid: 273)
 *
 * Event description: Wix Banking: Verification process for Card Flow / Transfer out / tbd. Frontend verification. (format validation)
 *
 * @param {bankingCardFlowFedVerificationProcessParams} params - The parameters for this event
 * @property {string} uuid - uuid
 * @property {string} verificationType - what was verified: cvv / expiration / pin / address
 * @property {string} verificationStatus - Succeed / Failed
 * @property {string} failureReason - error reason
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} wpGeo - wix payments geo (if exists)
 * @property {string} place - e.g. "bm/wp-transactions" / "bm/balance"
 * @property {string} pageType - e.g. payouts / list / details
 * @property {string} msid - msid
 * @return {LogObject<bankingCardFlowFedVerificationProcessParams>} LogObject<bankingCardFlowFedVerificationProcessParams>
 */
export function bankingCardFlowFedVerificationProcess(params) {
    return { evid: 273, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'bankingCardFlowFedCardActivationProcess' event (evid: 274)
 *
 * Event description: Wix Banking: Card activation process
 *
 * @param {bankingCardFlowFedCardActivationProcessParams} params - The parameters for this event
 * @property {string} step - Started / Completed / Failed
 * @property {string} failureReason - error reason
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - screen_resolution
 * @property {string} wpGeo - wix payments geo (if exists)
 * @property {string} place - e.g. "bm/wp-transactions" / "bm/balance"
 * @property {string} pageType - e.g. payouts / list / details
 * @property {string} msid - msid
 * @return {LogObject<bankingCardFlowFedCardActivationProcessParams>} LogObject<bankingCardFlowFedCardActivationProcessParams>
 */
export function bankingCardFlowFedCardActivationProcess(params) {
    return { evid: 274, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'obInvalidFieldValidationError' event (evid: 275)
 *
 * Event description: Field validation error on KYC form (use page type to define was it shareholder or account set up).
 *
 * @param {obInvalidFieldValidationErrorParams} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} errorMessage - error validation message
 * @property {string} role - Title in company / managing role in the company
 * @property {boolean} shared - own 25% or more of the business
 * @property {GUID} userGuid - uuid
 * @property {string} businessType - business_type
 * @property {string} country - country of citizenship
 * @property {boolean} managingPosition - C-position / high level position = shareholder
 * @property {string} screenResolution - screen_resolution
 * @property {string} businessDescription - field "Doing business as"
 * @property {string} errorType - is empty OR is frontend invalid OR is backend invalid
 * @property {string} productDescription - description of goods and services at KYC
 * @property {string} fieldValue - invalid value of field
 * @property {string} fieldName - name of field with invalid value
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp merchant geo
 * @property {string} referralAdditionalInfo - page that the user came from
 * @property {boolean} isShown - error is shown on UI
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {GUID} msid - msid
 * @return {LogObject<obInvalidFieldValidationErrorParams>} LogObject<obInvalidFieldValidationErrorParams>
 */
export function obInvalidFieldValidationError(params) {
    return { evid: 275, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantGenericErrorsSrc85Evid276' event (evid: 276)
 *
 * Event description: Generic error on Wix Payments pages
 *
 * @param {merchantGenericErrorsSrc85Evid276Params} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {string} errorMessage - error message
 * @property {GUID} userGuid - uuid
 * @property {string} businessType - business type code (mcc)
 * @property {string} errorReason - error code/reason - new field instead of error_code
 * @property {string} country - country of citizenship
 * @property {string} screenResolution - screen_resolution
 * @property {string} businessDescription - field "Doing business as"
 * @property {string} errorType - frontend or backend
 * @property {string} account_id - Wix Payments account_id
 * @property {string} usageType - personal or business
 * @property {string} wpGeo - wp merchant geo
 * @property {boolean} isShown - error is shown on UI
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - onboarding / shareholder modal etc.
 * @property {GUID} msid - msid
 * @return {LogObject<merchantGenericErrorsSrc85Evid276Params>} LogObject<merchantGenericErrorsSrc85Evid276Params>
 */
export function merchantGenericErrorsSrc85Evid276(params) {
    return { evid: 276, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'externalKycQrCodeGeneratedSrc85Evid277' event (evid: 277)
 *
 * Event description: QR-code generated with a link to External KYC (Proof of Life) for Wix Payments Brazil users
 *
 * @param {externalKycQrCodeGeneratedSrc85Evid277Params} params - The parameters for this event
 * @property {GUID} uuid - user id
 * @property {GUID} msid - metasite id
 * @property {GUID} wpAccountId - WP account id
 * @property {GUID} accountProfileId - WP account profile id
 * @property {string} wpGeo - WP country
 * @property {string} linkId - generated link id
 * @property {string} bucket_type - bucket type related
 * @return {LogObject<externalKycQrCodeGeneratedSrc85Evid277Params>} LogObject<externalKycQrCodeGeneratedSrc85Evid277Params>
 */
export function externalKycQrCodeGeneratedSrc85Evid277(params) {
    return { evid: 277, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'gizaWixPaymentsAppInstallationSrc85Evid278' event (evid: 278)
 *
 * Event description: Install/Uninstall applications for WixPayments in Giza
 *
 * @param {gizaWixPaymentsAppInstallationSrc85Evid278Params} params - The parameters for this event
 * @property {GUID} msid - msid
 * @property {string} account_id - wix account_id
 * @property {string} sourceAppId - app_id
 * @property {string} appName - application name
 * @property {string} appStatus - Install/Uninstall
 * @return {LogObject<gizaWixPaymentsAppInstallationSrc85Evid278Params>} LogObject<gizaWixPaymentsAppInstallationSrc85Evid278Params>
 */
export function gizaWixPaymentsAppInstallationSrc85Evid278(params) {
    return { evid: 278, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'showClickAiAssistantCtaSrc85Evid295' event (evid: 295)
 *
 * Event description: Show / Click "AI assistant" CTA to a user
 *
 * @param {showClickAiAssistantCtaSrc85Evid295Params} params - The parameters for this event
 * @property {string} errorMessage - key for message (kyc / compliance / other)
 * @property {string} screenResolution - screen_resolution
 * @property {string} accountStatus - wix payments account status
 * @property {string} actionType - show / click
 * @property {string} place - place on the page (company / personal etc.)
 * @property {string} pageType - payments_dashboard / payouts / accept_payments
 * @property {string} bucketType - bucket type for KYC (UploadIdDocumentation/UploadBankStatement/Pending)
 * @property {string} ctaName - CTA name (text user see)
 * @property {GUID} transactionId - transaction_id
 * @property {string} transactionStatus - transaction status
 * @property {string} skillName - name of the AI skill
 * (wp_fix_id_verification_issue etc)
 * @property {GUID} skillID - AI skill ID
 * @return {LogObject<showClickAiAssistantCtaSrc85Evid295Params>} LogObject<showClickAiAssistantCtaSrc85Evid295Params>
 */
export function showClickAiAssistantCtaSrc85Evid295(params) {
    return { evid: 295, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'checkingUnexpectedErrorOccurredSrc85Evid297' event (evid: 297)
 *
 * Event description: Checking: Unexpected error occurred (e.g. onboarding passed but card was not issued)
 *
 * @param {checkingUnexpectedErrorOccurredSrc85Evid297Params} params - The parameters for this event
 * @property {string} pageType - example: onboarding / activity / etc
 * @property {string} place - example: bm/checking
 * @property {string} errorReason - error reason; example: card not issued
 * @return {LogObject<checkingUnexpectedErrorOccurredSrc85Evid297Params>} LogObject<checkingUnexpectedErrorOccurredSrc85Evid297Params>
 */
export function checkingUnexpectedErrorOccurredSrc85Evid297(params) {
    return { evid: 297, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutProcess' event (evid: 3001)
 *
 * Event description: Event for Payout process
 *
 * @param {payoutProcessParams} params - The parameters for this event
 * @property {GUID} payoutId - payout id
 * @property {string} payoutType - Automatic / Manual
 * @property {string} reserveId - reserve_id created while payout
 * @property {string} errorReason - Payout error reason
 * @property {GUID} accountId - account id
 * @property {number} amount - Payout amount of transaction - multiply by 100,000
 * @property {string} status - Payout status: New / Failed / Transferred / PendingTransfer / Processing / Canceled
 * @property {string} paymentProvider - payment provider
 * @property {string} currency - Payout currency
 * @property {string} tenantId - tenant (currently just cashier)
 * @property {GUID} accountProfileId - account profile id
 * @return {LogObject<payoutProcessParams>} LogObject<payoutProcessParams>
 */
export function payoutProcess(params) {
    return { evid: 3001, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'payoutProcessIssue' event (evid: 3002)
 *
 * Event description: Event to cover Payout process issue:
 * 1) check status issue. 2) check balance on WP side. 3) check balance on Adyen side. 4) create payout issues. 5) update trx with payout_id. 6) unexpected errors
 *
 * @param {payoutProcessIssueParams} params - The parameters for this event
 * @property {GUID} externalMerchantId - wix uuid
 * @property {string} payoutType - Automatic / Manual
 * @property {string} errorMsg - error description, error code etc.
 * @property {string} errorReason - Payout error reason (e.g. wrong WP balance)
 * @property {GUID} accountId - account id
 * @property {number} amount - Payout amount of transaction - multiply by 100,000 (on WP side)
 * @property {GUID} externalAccountId - wix msid
 * @property {string} paymentProvider - payment provider
 * @property {string} currency - Payout currency
 * @property {string} tenantId - tenant (currently just cashier)
 * @property {GUID} accountProfileId - account profile id
 * @property {number} sum - Amount on Adyen side - multiply by 100,000 (on WP side)
 * @return {LogObject<payoutProcessIssueParams>} LogObject<payoutProcessIssueParams>
 */
export function payoutProcessIssue(params) {
    return { evid: 3002, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'emailWasTriggered' event (evid: 4501)
 *
 * Event description: An event which describes an email triggering   IMPORTANT Starting from 28-May-2021, data is stored in users_85 catalog. Historical data for the previous period is located in sites_85.
 *
 * @param {emailWasTriggeredParams} params - The parameters for this event
 * @property {GUID} _event_hash - event_hash
 * @property {GUID} externalMerchantId - user id
 * @property {string} emailType - Recurring_charge...
 * @property {GUID} visitor_id - visitor_id
 * @property {string} templateName - Email name (User Payments invoices email)
 * @property {GUID} accountId - wix Payments account unique identifier
 * @property {string} user_agent - user_agent
 * @property {GUID} externalAccountId - unique site id
 * @property {GUID} merchantId - wix Payments merchant unique identifier
 * @property {string} wpGeo - wpGeo
 * @property {string} emailLng - template lng
 * @property {GUID} msid - unique site id
 * @property {string} mailGuid - Server-side identifier of the API-call
 * @property {GUID} transactionId - unique identifier of the Payments transaction
 * @return {LogObject<emailWasTriggeredParams>} LogObject<emailWasTriggeredParams>
 */
export function emailWasTriggered(params) {
    return { evid: 4501, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantErrorOnKycDocUpload' event (evid: 4502)
 *
 * Event description: Errors that happened while merchant uploads docs on KYC form
 *
 * @param {merchantErrorOnKycDocUploadParams} params - The parameters for this event
 * @property {string} wix_account_id - wix account id
 * @property {string} subtype - doc type: COMPANY_REGISTRATION/BANK_STATEMENT
 * @property {string} shareholderId - shareholder_id
 * @property {string} uuid - uuid
 * @property {string} accountId - wix payments account_id
 * @property {string} user_agent - user_agent
 * @property {string} documentSide - front/back
 * @property {string} errorDescription - error description
 * @property {string} wpGeo - wp geo
 * @property {string} owner - WixSite / WixPartner / etc
 * @property {string} type - doc type area: BANK/COMPANY etc
 * @property {string} msid - msid
 * @property {string} uploadType - draft/regular
 * @return {LogObject<merchantErrorOnKycDocUploadParams>} LogObject<merchantErrorOnKycDocUploadParams>
 */
export function merchantErrorOnKycDocUpload(params) {
    return { evid: 4502, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantTechnicalErrorsInSentrySrc85Evid460' event (evid: 460)
 *
 * Event description: Errors that appear in Sentry for merchants in Wix Payments area
 *
 * @param {merchantTechnicalErrorsInSentrySrc85Evid460Params} params - The parameters for this event
 * @property {string} errorMessage - error message
 * @property {string} loggingPlatform - sentry, etc
 * @property {GUID} userGuid - uuid
 * @property {string} businessType - WP account type - Personal/Business etc
 * @property {string} sentryEventLink - sentry Event Link
 * @property {string} screenResolution - screen_resolution
 * @property {string} wpAccountId - wix payments account id
 * @property {string} wpGeo - wp merchant geo
 * @property {string} sentryEventId - sentry Event Id
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - settings / shareholder modal etc.
 * @property {GUID} msid - msid
 * @property {string} artifact - artifact where error was catched
 * @return {LogObject<merchantTechnicalErrorsInSentrySrc85Evid460Params>} LogObject<merchantTechnicalErrorsInSentrySrc85Evid460Params>
 */
export function merchantTechnicalErrorsInSentrySrc85Evid460(params) {
    return { evid: 460, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'obLimitationsOnKycForm' event (evid: 461)
 *
 * Event description: Onboarding KYC Form is shown with limitations
 *
 * @param {obLimitationsOnKycFormParams} params - The parameters for this event
 * @property {string} limitationCode - limitation code - e.g. updateBankAccountDisabled, updateShareholderDisabled
 * @property {string} shareholderId - shareholder_id in case of shareholder limitation
 * @property {GUID} userGuid - uuid
 * @property {string} businessType - WP account type - Personal/Business etc
 * @property {string} screenResolution - screen_resolution
 * @property {string} wpAccountId - wix payments account id
 * @property {string} wpGeo - wp merchant geo
 * @property {string} place - e.g. WP dashboard / business manager
 * @property {string} pageType - settings / shareholder modal etc.
 * @property {GUID} msid - msid
 * @return {LogObject<obLimitationsOnKycFormParams>} LogObject<obLimitationsOnKycFormParams>
 */
export function obLimitationsOnKycForm(params) {
    return { evid: 461, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'merchantFullStorySessionSrc85Evid462' event (evid: 462)
 *
 * Event description: Merchant FullStory session recorded
 *
 * @param {merchantFullStorySessionSrc85Evid462Params} params - The parameters for this event
 * @property {string} referralInfo - place from which user came
 * @property {boolean} isPartner - partner indication
 * @property {GUID} userGuid - uuid
 * @property {string} screenResolution - user screen resolution
 * @property {string} fullstoryUrl - fullstory Url
 * @property {string} wpGeo - wix payments country
 * @property {string} place - e.g. 'AccountLevel/onboarding' / 'SiteLevel/onboarding'
 * @property {string} pageType - create-account etc
 * @property {GUID} msid - msid
 * @property {string} experiment - experiment name
 * @return {LogObject<merchantFullStorySessionSrc85Evid462Params>} LogObject<merchantFullStorySessionSrc85Evid462Params>
 */
export function merchantFullStorySessionSrc85Evid462(params) {
    return { evid: 462, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'invoiceGenerationProcess' event (evid: 4700)
 *
 * Event description: Invoice generation process by stages
 *
 * @param {invoiceGenerationProcessParams} params - The parameters for this event
 * @property {string} stage - Initialised / Generated / Failed
 * @property {number} quantity - quantity
 * @property {string} invoiceNumber - invoiceNumber on payments side
 * @property {GUID} uuid - uuid
 * @property {string} errorReason - reason of failed
 * @property {GUID} accountId - wix payments account id (only for processing fees)
 * @property {GUID} invoiceId - invoice_id from payment_feeinvoice.fee_invoices
 * @property {number} amount - invoice amount multiply by 100,000
 * @property {string} currency - currency
 * @property {string} type - Application fees / Transaction fees
 * @property {GUID} msid - msid
 * @property {string} period - period for what the invoice was generated
 * @return {LogObject<invoiceGenerationProcessParams>} LogObject<invoiceGenerationProcessParams>
 */
export function invoiceGenerationProcess(params) {
    return { evid: 4700, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'invoiceSendingProcess' event (evid: 4701)
 *
 * Event description: Invoice sending process
 *
 * @param {invoiceSendingProcessParams} params - The parameters for this event
 * @property {number} quantity - quantity
 * @property {string} invoiceNumber - invoiceNumber on payments side
 * @property {GUID} uuid - uuid
 * @property {string} errorReason - Reason Invoice wasn't sent (BlockedUser etc.)
 * @property {GUID} accountId - wix payments account id (only for processing fees)
 * @property {GUID} invoiceId - invoice_id from payment_feeinvoice.fee_invoices
 * @property {number} amount - invoice amount multiply by 100,000
 * @property {string} status - Sent / NotSent
 * @property {string} currency - currency
 * @property {string} type - Application fees / Transaction fees
 * @property {GUID} msid - msid
 * @property {string} period - period for what the invoice was generated
 * @return {LogObject<invoiceSendingProcessParams>} LogObject<invoiceSendingProcessParams>
 */
export function invoiceSendingProcess(params) {
    return { evid: 4701, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'openingSentInvoice' event (evid: 4801)
 *
 * Event description: Interactions with processing and App fees invoices after they were sent (opening and result)
 *
 * @param {openingSentInvoiceParams} params - The parameters for this event
 * @property {string} stage - initiate/result
 * @property {string} invoiceNumber - invoiceNumber on payments side
 * @property {GUID} uuid - uuid
 * @property {string} errorMsg - message about any errors user got while opening the invoice
 * @property {GUID} accountId - wix payments account id
 * @property {GUID} invoiceId - invoice_id from payment_feeinvoice.fee_invoices
 * @property {string} type - Application fees / Transaction fees
 * @property {GUID} msid - msid
 * @property {DateTime} invoice_date - Date Invoice was created
 * @return {LogObject<openingSentInvoiceParams>} LogObject<openingSentInvoiceParams>
 */
export function openingSentInvoice(params) {
    return { evid: 4801, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'serverRedirection' event (evid: 5101)
 *
 * Event description: Redirection non-Brasil merchants to the AM or BM if they want to open Standalone dashboard by the link
 *
 * @param {serverRedirectionParams} params - The parameters for this event
 * @property {string} url - source url (url under CTA)
 * @property {GUID} uuid - uuid
 * @property {string} targetUrl - url user will be redirected
 * @property {string} templateName - email, notification template etc
 * @property {GUID} accountId - WP account_id
 * @property {string} origin - source from which user direct to CTA (email, ping)
 * @property {string} wpGeo - country of WP
 * @property {string} owner - Balance Management, Account Management, Transactions
 * @property {GUID} msid - wix msid
 * @return {LogObject<serverRedirectionParams>} LogObject<serverRedirectionParams>
 */
export function serverRedirection(params) {
    return { evid: 5101, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'externalKycLinkGeneratedSrc85Evid5102' event (evid: 5102)
 *
 * Event description: External KYC link generated for Wix Payments users.
 * E.g. QR code link generated for WP Brasil users (related to external KYC flow on provider side).
 *
 * @param {externalKycLinkGeneratedSrc85Evid5102Params} params - The parameters for this event
 * @property {GUID} uuid - user id
 * @property {GUID} msid - metasite id
 * @property {GUID} wpAccountId - WP account id
 * @property {GUID} accountProfileId - WP account profile id
 * @property {string} wpGeo - WP geo country
 * @property {string} linkId - generated link ud
 * @return {LogObject<externalKycLinkGeneratedSrc85Evid5102Params>} LogObject<externalKycLinkGeneratedSrc85Evid5102Params>
 */
export function externalKycLinkGeneratedSrc85Evid5102(params) {
    return { evid: 5102, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'externalKycLinkOpenedSrc85Evid5103' event (evid: 5103)
 *
 * Event description: External KYC link opened by Wix Payments users.
 * E.g. QR code link generated and opened by WP Brasil users (related to external KYC flow on provider side).
 *
 * @param {externalKycLinkOpenedSrc85Evid5103Params} params - The parameters for this event
 * @property {GUID} uuid - user id
 * @property {GUID} msid - metasite id
 * @property {GUID} wpAccountId - WP account id
 * @property {GUID} accountProfileId - WP account profile id
 * @property {string} wpGeo - WP geo country
 * @property {string} linkId - opened link id
 * @return {LogObject<externalKycLinkOpenedSrc85Evid5103Params>} LogObject<externalKycLinkOpenedSrc85Evid5103Params>
 */
export function externalKycLinkOpenedSrc85Evid5103(params) {
    return { evid: 5103, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'identiqRiskEngineRequest' event (evid: 5501)
 *
 * Event description: request to the Identiq Service
 *
 * @param {identiqRiskEngineRequestParams} params - The parameters for this event
 * @property {string} accountId - wixPayments account_id
 * @property {string} error - reason of the error
 * @property {string} additionalData - json data
 * @property {boolean} isSuccessful - is request finished successfully
 * @property {string} riskRequestType - type of the risk request (signup/guestPayment)
 * @property {string} transactionId - id of transaction
 * @return {LogObject<identiqRiskEngineRequestParams>} LogObject<identiqRiskEngineRequestParams>
 */
export function identiqRiskEngineRequest(params) {
    return { evid: 5501, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'riskEngineDecisions' event (evid: 5502)
 *
 * Event description: decision of the risk engine checks
 *
 * @param {riskEngineDecisionsParams} params - The parameters for this event
 * @property {string} riskCheckType - risk engine check type
 * @property {string} description - description of the risk decision
 * @property {string} accountId - wixPayments account_id
 * @property {string} decision - risk decision
 * @property {string} error - reason of the error
 * @property {string} additionalData - json data
 * @property {string} paymentProvider - WixPayment payment provider
 * @property {boolean} isFinalDecision - the current check is the final decision on transaction
 * @property {boolean} isSuccessful - is request finished successfully
 * @property {string} transactionId - id of transaction
 * @return {LogObject<riskEngineDecisionsParams>} LogObject<riskEngineDecisionsParams>
 */
export function riskEngineDecisions(params) {
    return { evid: 5502, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'cmsCaseCreationSrc85Evid5555' event (evid: 5555)
 *
 * Event description: Sales Force (CMS) case creation event
 *
 * @param {cmsCaseCreationSrc85Evid5555Params} params - The parameters for this event
 * @property {string} siteId - msid
 * @property {string} wixAccountId - wix account_ id
 * @property {string} wixPaymentsAccountId - wp account_id
 * @property {string} assigneeEmail - cms case assignee
 * @property {string} cmsCaseId - SF case id
 * @property {string} cmsAlertBiId - cms alert guid
 * @property {string} alertPriority - cms case priotity
 * @property {string} cmsCaseOrigin - cms case origin (area of the risk)
 * @property {string} additionalData - additional datapoints info
 * @property {boolean} isSuccessful - cms case created successfully
 * @property {string} error - error info if cms case creation failed
 * @property {string} wixUserId - Wix UUID
 * @return {LogObject<cmsCaseCreationSrc85Evid5555Params>} LogObject<cmsCaseCreationSrc85Evid5555Params>
 */
export function cmsCaseCreationSrc85Evid5555(params) {
    return { evid: 5555, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'bankingCardFlowBedVerificationProcess' event (evid: 5701)
 *
 * Event description: Wix Banking: Verification process for Card Flow. Backend verification. (value validation)
 *
 * @param {bankingCardFlowBedVerificationProcessParams} params - The parameters for this event
 * @property {string} verificationType - what was verified: cvv / expiration / pin / address
 * @property {string} verificationStatus - Succeed / Failed
 * @property {string} failureReason - error reason
 * @property {string} uuid - uuid / external_merchant_id (WP)
 * @property {string} cardId - providerCardId
 * @property {string} msid - msid / external_account_id (WP)
 * @return {LogObject<bankingCardFlowBedVerificationProcessParams>} LogObject<bankingCardFlowBedVerificationProcessParams>
 */
export function bankingCardFlowBedVerificationProcess(params) {
    return { evid: 5701, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'maestroAccountCreatedSrc85Evid5801' event (evid: 5801)
 *
 * Event description: Wix Payments account was created [Maestro] project
 *
 * @param {maestroAccountCreatedSrc85Evid5801Params} params - The parameters for this event
 * @property {string} uuid - wix uuid
 * @property {string} msid - wix msid
 * @property {string} wpAccountId - Wix Payments account id
 * @return {LogObject<maestroAccountCreatedSrc85Evid5801Params>} LogObject<maestroAccountCreatedSrc85Evid5801Params>
 */
export function maestroAccountCreatedSrc85Evid5801(params) {
    return { evid: 5801, src: 85, endpoint: '', params };
}
/**
 * A function to create a log object for the 'automaticRestrictionMechanismActionOnManualReviewStatusSrc85Evid7777' event (evid: 7777)
 *
 * Event description: Event represent the actions that done via ARM (automatic restriction mechanism):
 * manual review status change via automatic service call.
 *
 * @param {automaticRestrictionMechanismActionOnManualReviewStatusSrc85Evid7777Params} params - The parameters for this event
 * @property {string} wixPaymentsAccountId - wix payments account id
 * @property {string} additionalData - additional datapoints info
 * @property {boolean} isSuccessful - action performed successfully
 * @property {string} error - error when action on manual review status change failed
 * @property {string} actionType - action performed on manual review status field
 * @property {string} initiatorSystem - the system which request the action
 * @property {string} msid - msid
 * @property {string} message - bo_note to be added to WPBO
 * @property {string} wixUserId - Wix UUID
 * @return {LogObject<automaticRestrictionMechanismActionOnManualReviewStatusSrc85Evid7777Params>} LogObject<automaticRestrictionMechanismActionOnManualReviewStatusSrc85Evid7777Params>
 */
export function automaticRestrictionMechanismActionOnManualReviewStatusSrc85Evid7777(params) {
    return { evid: 7777, src: 85, endpoint: '', params };
}
//# sourceMappingURL=index.js.map